import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import AdminDashboardHome from "./Home";
import AdminDashboardShell from "./Shell";
import {
  AdminCustomersPage,
  AdminOrdersPage,
  AdminProductsPage
} from "./Commerce";
import { AdminInsightsPage, AdminSettingsHubPage } from "./Insights";
import {
  AdminLogisticsPage,
  AdminProcurementPage,
  AdminProductCreatePage,
  AdminQualityPage
} from "./Workflows";
import {
  AdminOpsShell,
  AdminInquiryHubPage,
  AdminSiteMaintenanceHubPage,
  AdminTeamPermissionsPage
} from "./AdminOps";
import AdminDashboardHomeWorkspaces from "./HomeWorkspaces";
import {
  AdminArticleManagementPage,
  AdminCarouselPage,
  AdminCompanyImagesPage,
  AdminFaqKnowledgePage,
  AdminStorefrontBasePage,
  AdminStorefrontNavigationPage,
  AdminTemplateStudioPage
} from "../storefront/ContentPages";
import {
  normalizeUserStatus,
  toUserDraft,
  formatUserTime,
  normalizePermissionKeys,
  normalizePositionRow
} from "./Helpers";
import {
  PositionMaintenanceTable,
  ProductsTable,
  CreateProductForm,
  NotificationManagementPanel,
  SystemConfigForm
} from "./Panels";
import {
  ORDER_STATUS_OPTIONS,
  PRODUCT_CATEGORIES,
  ADMIN_LIST_PAGE_SIZE,
  SIDEBAR_GROUP_STORAGE_KEY,
  DEFAULT_ADMIN_LIST_PAGES,
  MAX_FAQ_KNOWLEDGE_ITEMS,
  MAX_FAQ_QUESTION_LENGTH,
  MAX_FAQ_ANSWER_LENGTH,
  MAX_COMPANY_IMAGE_ITEMS,
  DEFAULT_FAQ_KNOWLEDGE_ITEMS,
  DEFAULT_COMPANY_IMAGE_SECTIONS,
  DEFAULT_COMPANY_IMAGE_SECTION_MAP,
  STOREFRONT_CONFIG_UPDATED_EVENT,
  STOREFRONT_CONFIG_UPDATED_AT_KEY,
  MAX_NAV_DEPTH,
  MENU_EMOJI_BY_KEY,
  POSITION_TEMPLATE_CAPABILITIES,
  TAB_CAPABILITY_RULES,
  CAPABILITIES_BY_ROLE,
  ASSIGNABLE_ROLES,
  ROLE_LEVELS,
  POSITION_LIST_STORAGE_KEY,
  HR_RECRUITMENT_STAGE_STORAGE_KEY,
  HR_ATTENDANCE_OVERRIDE_STORAGE_KEY,
  HR_ANNOUNCEMENTS_STORAGE_KEY,
  HR_DOCUMENT_UPLOADS_STORAGE_KEY,
  HR_RECRUITMENT_STAGE_OPTIONS,
  DEFAULT_POSITION_ROWS,
  POSITION_SCOPED_MENU_TABS
} from "./Config";
import {
  createEmptyProductEditorDraft,
  toProductEditorDraft,
  createDefaultSystemConfigDraft,
  createDefaultStoreSettingsDraft,
  createDefaultEditUserForm,
  createDefaultPasswordForm,
  createDefaultUserPasswordForm,
  createDefaultReviewDraft,
  createDefaultNavMenuDraft,
  createDraftProductGroupId,
  buildProductGroupDraftPath,
  isProductGroupDraftItem,
  normalizeNavDraftItems,
  sanitizeNavDraftItems
} from "./Drafts";
import {
  ORDER_STATUS_LABELS,
  ADMIN_TAB_DESCRIPTIONS,
  ROLE_LABELS,
  CAPABILITY_LABELS
} from "./I18n";
import { t, setLang } from "../../../i18n";
import {
  readStoredList,
  readStoredJson,
  inferPositionGroup,
  normalizePositionScopeKey,
  normalizeCapabilities,
  isSameProfilePermissionState,
  collectExpandablePaths,
  pickMenusByKey,
  normalizeFaqKnowledgeItems,
  buildCompanyImageSectionLabel,
  getCompanyImageSectionLocaleLabels,
  createCompanyImageSectionKey,
  normalizeCompanyImageSections,
  normalizeCompanyImageValue,
  normalizeExternalLink
} from "./Utils";
import ProductAnalyticsAdmin from "../user/ProductAnalyticsAdmin";
import { HrAdminData } from "../user/HrAdminData";
import HrAdminActions from "../user/HrAdminActions";
import MerchandisingFilters from "../user/MerchandisingFilters";
import MerchandisingSummary from "../user/MerchandisingSummary";
import OperationsViewSummary from "../user/OperationsViewSummary";
import Pagination from "../user/Pagination";
import PositionPermissionModalActions from "../user/PositionPermissionModalActions";
import PositionAdminActions from "../user/PositionAdminActions";
import EditActions from "../user/EditActions";
import PasswordActions from "../user/PasswordActions";
import DeleteActions from "../user/DeleteActions";
import ManagementActions from "../user/ManagementActions";
import OrderAdminActions from "../user/OrderAdminActions";
import PasswordActionsAdmin from "../user/PasswordActionsAdmin";
import PermissionPreviewHelpers from "../user/PermissionPreviewHelpers";
import StoreModeActions from "../user/StoreModeActions";
import ProductEditorActions from "../user/ProductEditorActions";
import ProductSubmitActions from "../user/ProductSubmitActions";
import CollectedProductActions from "../user/CollectedProductActions";
import ProductCatalogActions from "../user/ProductCatalogActions";

const MAX_BLOG_ARTICLE_ITEMS = 20;
const BLOG_CATEGORY_OPTIONS = ["Buying Guide", "Customization", "Sample Order", "Sourcing", "Product Tips"];

function normalizeAdminBlogCategory(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "Sourcing";
  }

  const lowered = raw.toLowerCase();
  if (lowered.includes("buy")) {
    return "Buying Guide";
  }
  if (lowered.includes("custom") || lowered.includes("oem") || lowered.includes("odm") || lowered.includes("private")) {
    return "Customization";
  }
  if (lowered.includes("sample")) {
    return "Sample Order";
  }
  if (lowered.includes("tip") || lowered.includes("material") || lowered.includes("spec")) {
    return "Product Tips";
  }

  return BLOG_CATEGORY_OPTIONS.includes(raw) ? raw : "Sourcing";
}

function normalizeAdminBlogDate(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
}

function normalizeAdminBlogArticles(items) {
  const source = Array.isArray(items) ? items : [];
  const normalized = source
    .map((item, index) => {
      const title = String(item?.title || item?.heading || "").trim().slice(0, 140);
      const category = normalizeAdminBlogCategory(item?.category || item?.topic || item?.type || item?.articleType);
      const summary = String(item?.summary || item?.description || item?.excerpt || "").trim().slice(0, 320);
      const body = String(item?.body || item?.content || item?.summary || item?.description || "").trim().slice(0, 3000);
      const cover = String(item?.cover || item?.coverImage || item?.coverUrl || item?.image || item?.imageUrl || item?.thumbnail || "").trim().slice(0, 600);
      const date = normalizeAdminBlogDate(item?.date || item?.publishDate || item?.publishedAt || item?.createdAt || item?.updatedAt);
      const readMoreUrl = String(item?.readMoreUrl || item?.url || item?.link || item?.path || "").trim().slice(0, 600);

      if (!title && !summary && !body && !cover && !date && !readMoreUrl) {
        return null;
      }

      return {
        id: String(item?.id || item?.slug || `blog-${Date.now()}-${index + 1}`),
        title,
        category,
        summary,
        body,
        cover,
        date,
        readMoreUrl
      };
    })
    .filter(Boolean)
    .slice(0, MAX_BLOG_ARTICLE_ITEMS);

  if (normalized.length) {
    return normalized;
  }

  return [
    {
      id: `blog-${Date.now()}`,
      title: "",
      category: "Sourcing",
      summary: "",
      body: "",
      cover: "",
      date: "",
      readMoreUrl: ""
    }
  ];
}

function createDefaultAdminNotificationDraft() {
  return {
    title: "",
    message: "",
    level: "info",
    targetType: "all_staff",
    targetRoles: ["admin"],
    targetUserIdsText: ""
  };
}

function toAdminNotificationPayload(draft = {}) {
  const targetType = String(draft?.targetType || "all_staff").trim() || "all_staff";
  const targetRoles = Array.isArray(draft?.targetRoles)
    ? draft.targetRoles.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const targetUserIds = String(draft?.targetUserIdsText || "")
    .split(/[,，\s]+/)
    .map((item) => Number(item || 0))
    .filter((value) => Number.isFinite(value) && value > 0);

  return {
    title: String(draft?.title || "").trim(),
    message: String(draft?.message || "").trim(),
    level: String(draft?.level || "info").trim() || "info",
    targetType,
    targetRoles,
    targetUserIds
  };
}

function getBlogArticleEditableSnapshot(item) {
  return {
    id: String(item?.id || ""),
    title: String(item?.title || ""),
    category: String(item?.category || ""),
    summary: String(item?.summary || ""),
    body: String(item?.body || ""),
    cover: String(item?.cover || ""),
    date: String(item?.date || ""),
    readMoreUrl: String(item?.readMoreUrl || "")
  };
}

function createDefaultAdminFinanceWorkspace() {
  return {
    incomeRows: [],
    expenseRows: [],
    receivableRows: [],
    payableRows: [],
    invoiceRows: [],
    approvalRows: [],
    reconciliationRows: [],
    taxRows: [],
    budgetRows: [],
    reportRows: [],
    dailyBillRows: [],
    monthlyBillRows: [],
    yearlyBillRows: []
  };
}

function normalizeStoreThemeLayoutDraft(value, fallback) {
  const source = value && typeof value === "object" ? value : {};
  const fallbackLayout = fallback && typeof fallback === "object" ? fallback : {};
  const fallbackHomeOrder = Array.isArray(fallbackLayout?.home?.moduleOrder) ? fallbackLayout.home.moduleOrder : [];
  const sourceHomeOrder = Array.isArray(source?.home?.moduleOrder) ? source.home.moduleOrder : fallbackHomeOrder;
  const normalizedHomeOrder = Array.from(
    new Set(
      sourceHomeOrder
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    )
  );

  return {
    header: {
      height: Number(source?.header?.height || fallbackLayout?.header?.height || 78),
      navAlign: String(source?.header?.navAlign || fallbackLayout?.header?.navAlign || "center"),
      navButtonStyle: String(source?.header?.navButtonStyle || fallbackLayout?.header?.navButtonStyle || "pill")
    },
    home: {
      moduleOrder: normalizedHomeOrder.length
        ? normalizedHomeOrder
        : Array.isArray(fallbackHomeOrder)
          ? fallbackHomeOrder
          : []
    },
    catalog: {
      cardColumns: Number(source?.catalog?.cardColumns || fallbackLayout?.catalog?.cardColumns || 3),
      sidebarMode: String(source?.catalog?.sidebarMode || fallbackLayout?.catalog?.sidebarMode || "left")
    },
    detail: {
      layoutMode: String(source?.detail?.layoutMode || fallbackLayout?.detail?.layoutMode || "split")
    }
  };
}

function normalizeStoreIdDraft(value, fallback = "default") {
  const normalized = String(value || fallback)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "");

  return normalized || String(fallback || "default").trim() || "default";
}

function normalizeThemePageConfigsDraft(value, fallback) {
  const source = value && typeof value === "object" ? value : {};
  const fallbackConfig = fallback && typeof fallback === "object"
    ? fallback
    : createDefaultStoreSettingsDraft().themePageConfigs;
  const homeModuleKeys = [
    "intro",
    "quick-nav",
    "categories",
    "best-selling",
    "featured",
    "solutions",
    "scenarios",
    "story",
    "process",
    "inquiry"
  ];
  const catalogSectionKeys = ["filters", "hero", "productGrid", "pagination"];
  const detailSectionKeys = ["media", "variations", "pricing", "description", "specs", "actions", "trustNote", "content"];
  const readText = (pageKey, fieldKey, maxLength) => {
    const fallbackValue = String(fallbackConfig?.[pageKey]?.[fieldKey] || "");
    const rawValue = String(source?.[pageKey]?.[fieldKey] || fallbackValue).trim();
    return rawValue.slice(0, maxLength) || fallbackValue;
  };

  const normalizeHomeModuleVisibility = () => {
    const sourceVisibility = source?.home?.moduleVisibility && typeof source.home.moduleVisibility === "object"
      ? source.home.moduleVisibility
      : {};
    const fallbackVisibility = fallbackConfig?.home?.moduleVisibility && typeof fallbackConfig.home.moduleVisibility === "object"
      ? fallbackConfig.home.moduleVisibility
      : {};
    return homeModuleKeys.reduce((visibility, key) => {
      visibility[key] = sourceVisibility[key] !== undefined
        ? sourceVisibility[key] !== false
        : fallbackVisibility[key] !== false;
      return visibility;
    }, {});
  };

  const normalizeHomeModules = () => {
    const sourceModules = source?.home?.modules && typeof source.home.modules === "object"
      ? source.home.modules
      : {};
    const fallbackModules = fallbackConfig?.home?.modules && typeof fallbackConfig.home.modules === "object"
      ? fallbackConfig.home.modules
      : {};
    return homeModuleKeys.reduce((modules, key) => {
      const moduleSource = sourceModules?.[key] && typeof sourceModules[key] === "object" ? sourceModules[key] : {};
      const moduleFallback = fallbackModules?.[key] && typeof fallbackModules[key] === "object" ? fallbackModules[key] : {};
      modules[key] = {
        title: String(moduleSource.title || moduleFallback.title || "").trim().slice(0, 160),
        subtitle: String(moduleSource.subtitle || moduleFallback.subtitle || "").trim().slice(0, 360),
        primaryCtaText: String(moduleSource.primaryCtaText || moduleFallback.primaryCtaText || "").trim().slice(0, 48),
        secondaryCtaText: String(moduleSource.secondaryCtaText || moduleFallback.secondaryCtaText || "").trim().slice(0, 48),
        imageUrl: String(moduleSource.imageUrl || moduleFallback.imageUrl || "").trim().slice(0, 600)
      };
      return modules;
    }, {});
  };

  const normalizePageSectionVisibility = (pageKey, sectionKeys) => {
    const sourceVisibility = source?.[pageKey]?.sectionVisibility && typeof source[pageKey].sectionVisibility === "object"
      ? source[pageKey].sectionVisibility
      : {};
    const fallbackVisibility = fallbackConfig?.[pageKey]?.sectionVisibility && typeof fallbackConfig[pageKey].sectionVisibility === "object"
      ? fallbackConfig[pageKey].sectionVisibility
      : {};
    return sectionKeys.reduce((visibility, key) => {
      visibility[key] = sourceVisibility[key] !== undefined
        ? sourceVisibility[key] !== false
        : fallbackVisibility[key] !== false;
      return visibility;
    }, {});
  };

  return {
    home: {
      heroEyebrow: readText("home", "heroEyebrow", 80),
      heroTitle: readText("home", "heroTitle", 180),
      heroSubtitle: readText("home", "heroSubtitle", 420),
      primaryCtaText: readText("home", "primaryCtaText", 48),
      secondaryCtaText: readText("home", "secondaryCtaText", 48),
      heroImage: readText("home", "heroImage", 600),
      moduleVisibility: normalizeHomeModuleVisibility(),
      modules: normalizeHomeModules()
    },
    catalog: {
      heroTitle: readText("catalog", "heroTitle", 120),
      heroDescription: readText("catalog", "heroDescription", 280),
      emptyMessage: readText("catalog", "emptyMessage", 160),
      ctaText: readText("catalog", "ctaText", 48),
      sectionVisibility: normalizePageSectionVisibility("catalog", catalogSectionKeys)
    },
    detail: {
      inquiryTitle: readText("detail", "inquiryTitle", 120),
      inquiryDescription: readText("detail", "inquiryDescription", 320),
      primaryCtaText: readText("detail", "primaryCtaText", 48),
      secondaryCtaText: readText("detail", "secondaryCtaText", 48),
      trustNote: readText("detail", "trustNote", 120),
      sectionVisibility: normalizePageSectionVisibility("detail", detailSectionKeys)
    }
  };
}

function normalizeCustomThemeTemplatesDraft(value, fallbackLayout, fallbackPageConfigs) {
  const source = Array.isArray(value) ? value : [];
  return source
    .map((item) => {
      const id = String(item?.id || "").trim();
      if (!id) {
        return null;
      }

      return {
        id,
        name: String(item?.name || "").trim() || id,
        themeTemplate: String(item?.themeTemplate || item?.baseThemeTemplate || "style-1").trim() || "style-1",
        themeLayoutConfig: normalizeStoreThemeLayoutDraft(item?.themeLayoutConfig, fallbackLayout),
        themePageConfigs: normalizeThemePageConfigsDraft(item?.themePageConfigs, fallbackPageConfigs)
      };
    })
    .filter(Boolean);
}

function toStoreSettingsDraft(storeSettings) {
  const defaults = createDefaultStoreSettingsDraft();
  const source = storeSettings && typeof storeSettings === "object" ? storeSettings : {};
  const themeLayoutConfig = normalizeStoreThemeLayoutDraft(source.themeLayoutConfig, defaults.themeLayoutConfig);
  const themePageConfigs = normalizeThemePageConfigsDraft(source.themePageConfigs, defaults.themePageConfigs);
  const customThemeTemplates = normalizeCustomThemeTemplatesDraft(source.customThemeTemplates, themeLayoutConfig, themePageConfigs);
  const activeCustomThemeTemplateId = String(source.activeCustomThemeTemplateId || "").trim();

  return {
    ...defaults,
    storeId: normalizeStoreIdDraft(source.storeId, defaults.storeId),
    themeTemplate: String(source.themeTemplate || defaults.themeTemplate),
    themeLayoutConfig,
    themePageConfigs,
    customThemeTemplates,
    activeCustomThemeTemplateId: customThemeTemplates.some((item) => item.id === activeCustomThemeTemplateId)
      ? activeCustomThemeTemplateId
      : "",
    storefrontTitle: String(source.storefrontTitle || defaults.storefrontTitle),
    adminTitle: String(source.adminTitle || defaults.adminTitle),
    logoUrl: String(source.logoUrl || defaults.logoUrl),
    logoAlt: String(source.logoAlt || defaults.logoAlt),
    topNoticeText: String(source.topNoticeText || defaults.topNoticeText),
    contactEmail: String(source.contactEmail || defaults.contactEmail),
    contactPhone: String(source.contactPhone || defaults.contactPhone),
    contactWhatsapp: String(source.contactWhatsapp || defaults.contactWhatsapp),
    seoMetaTitle: String(source.seo?.metaTitle || defaults.seoMetaTitle),
    seoDescription: String(source.seo?.metaDescription || defaults.seoDescription),
    seoKeywords: String(source.seo?.keywords || defaults.seoKeywords),
    seoCanonicalUrl: String(source.seo?.canonicalUrl || defaults.seoCanonicalUrl),
    seoRobots: String(source.seo?.robots || defaults.seoRobots),
    seoOgTitle: String(source.seo?.ogTitle || defaults.seoOgTitle),
    seoOgDescription: String(source.seo?.ogDescription || defaults.seoOgDescription),
    seoOgImage: String(source.seo?.ogImage || defaults.seoOgImage),
    seoTwitterCard: String(source.seo?.twitterCard || defaults.seoTwitterCard),
    seoTwitterSite: String(source.seo?.twitterSite || defaults.seoTwitterSite)
  };
}

function toStoreSettingsPayload(storeSettingsDraft) {
  return {
    storeId: normalizeStoreIdDraft(storeSettingsDraft.storeId, createDefaultStoreSettingsDraft().storeId),
    themeTemplate: storeSettingsDraft.themeTemplate,
    themeLayoutConfig: storeSettingsDraft.themeLayoutConfig,
    themePageConfigs: normalizeThemePageConfigsDraft(
      storeSettingsDraft.themePageConfigs,
      createDefaultStoreSettingsDraft().themePageConfigs
    ),
    customThemeTemplates: storeSettingsDraft.customThemeTemplates,
    activeCustomThemeTemplateId: storeSettingsDraft.activeCustomThemeTemplateId,
    storefrontTitle: storeSettingsDraft.storefrontTitle,
    adminTitle: storeSettingsDraft.adminTitle,
    logoUrl: storeSettingsDraft.logoUrl,
    logoAlt: storeSettingsDraft.logoAlt,
    topNoticeText: storeSettingsDraft.topNoticeText,
    contactEmail: storeSettingsDraft.contactEmail,
    contactPhone: storeSettingsDraft.contactPhone,
    contactWhatsapp: storeSettingsDraft.contactWhatsapp,
    seo: {
      metaTitle: storeSettingsDraft.seoMetaTitle,
      metaDescription: storeSettingsDraft.seoDescription,
      keywords: storeSettingsDraft.seoKeywords,
      canonicalUrl: storeSettingsDraft.seoCanonicalUrl,
      robots: storeSettingsDraft.seoRobots,
      ogTitle: storeSettingsDraft.seoOgTitle,
      ogDescription: storeSettingsDraft.seoOgDescription,
      ogImage: storeSettingsDraft.seoOgImage,
      twitterCard: storeSettingsDraft.seoTwitterCard,
      twitterSite: storeSettingsDraft.seoTwitterSite
    }
  };
}

export default function AdminDashboardPage({
  apiBaseUrl,
  adminToken,
  adminRole,
  locale = "zh",
  onChangeLocale,
  onSessionExpired,
  onRestrictedAccess,
  onLogout
}) {
  setLang(locale);
  const texts = new Proxy({}, {
    get: (_, key) => t(`admin.${key}`)
  });
  const resolveAdminToken = useCallback((token, fallback = "") => {
    const normalized = String(token || "").trim();
    if (!normalized) {
      return fallback;
    }

    const key = `admin.${normalized}`;
    const translated = t(key);
    return translated !== key ? translated : (fallback || normalized);
  }, []);
  const formatCapabilityLabel = useCallback((capabilityKey, targetLocale = locale) => {
    const normalized = String(capabilityKey || "").trim();
    if (!normalized) {
      return "";
    }

    const directKey = `admin.capability.${normalized}`;
    const directLabel = t(directKey);
    if (directLabel !== directKey) {
      return directLabel;
    }

    const segments = normalized.split(":").filter(Boolean);
    const zhMap = {
      admin: "后台",
      shop: "店铺",
      internal: "内部",
      crm: "客户",
      website: "网站",
      merchandising: "选品",
      catalog: "商品",
      sales: "销售",
      order: "订单",
      procurement: "采购",
      production: "生产",
      qa: "质检",
      logistics: "物流",
      trade: "外贸",
      finance: "财务",
      marketing: "营销",
      analytics: "分析",
      support: "客服",
      office: "行政",
      hr: "人事",
      overview: "总览",
      products: "商品",
      orders: "订单",
      users: "用户",
      storefront: "店铺",
      system: "系统",
      config: "配置",
      security: "安全",
      integration: "集成",
      customer: "客户",
      content: "内容",
      navigation: "导航",
      banner: "横幅",
      seo: "SEO",
      theme: "主题",
      publish: "发布",
      selection: "选品",
      pricing: "定价",
      inventory: "库存",
      translation: "翻译",
      inquiry: "询盘",
      quotation: "报价",
      contract: "合同",
      sample: "样品",
      status: "状态",
      split: "拆分",
      merge: "合并",
      purchase: "采购",
      plan: "计划",
      inspection: "质检",
      shipment: "发货",
      tracking: "追踪",
      customs: "报关",
      doc: "单证",
      invoice: "发票",
      payment: "付款",
      refund: "退款",
      reconciliation: "对账",
      tax: "税务",
      campaign: "活动",
      coupon: "优惠券",
      lead: "线索",
      dashboard: "看板",
      report: "报表",
      ticket: "工单",
      return: "退货",
      complaint: "投诉",
      announcement: "公告",
      document: "文档",
      workflow: "流程",
      meeting: "会议",
      employee: "员工",
      profile: "资料",
      password: "密码",
      role: "角色",
      position: "岗位",
      processing: "处理",
      "after-sales": "售后",
      read: "查看",
      write: "编辑",
      create: "创建",
      delete: "删除",
      update: "更新",
      reset: "重置",
      approve: "审批",
      revoke: "吊销",
      manage: "管理",
      export: "导出",
      handle: "处理",
      assign: "分配"
    };

    const toTitleCase = (segment) => segment
      .split("-")
      .filter(Boolean)
      .map((item) => item.charAt(0).toUpperCase() + item.slice(1))
      .join(" ");

    if (targetLocale === "zh") {
      return segments.map((segment) => zhMap[segment] || segment).join(" · ");
    }

    return segments.map((segment) => toTitleCase(segment)).join(" / ");
  }, [locale]);
  const roleLabels = useMemo(() => {
    return Object.entries(ROLE_LABELS).reduce((acc, [roleKey, token]) => {
      acc[roleKey] = resolveAdminToken(token, roleKey);
      return acc;
    }, {});
  }, [resolveAdminToken]);
  const statusLabels = useMemo(() => {
    return Object.entries(ORDER_STATUS_LABELS).reduce((acc, [statusKey, token]) => {
      acc[statusKey] = resolveAdminToken(token, statusKey);
      return acc;
    }, {});
  }, [resolveAdminToken]);
  const [adminTab, setAdminTab] = useState(() => {
    const saved = localStorage.getItem("ft_admin_tab") || "home";
    return saved === "designBanner" ? "designCarousel" : saved;
  });
  const [adminQuery, setAdminQuery] = useState("");
  const [adminError, setAdminError] = useState("");
  const [adminOverview, setAdminOverview] = useState(null);
  const [adminProducts, setAdminProducts] = useState([]);
  const [collectedProducts, setCollectedProducts] = useState([]);
  const [collectedProductsLoading, setCollectedProductsLoading] = useState(false);
  const [collectedProductActionId, setCollectedProductActionId] = useState("");
  const [adminOrders, setAdminOrders] = useState([]);
  const [adminInquiries, setAdminInquiries] = useState([]);
  const [notificationSummaryAvailable, setNotificationSummaryAvailable] = useState(true);
  const [notificationSummary, setNotificationSummary] = useState({
    unreadInquiryCount: 0,
    unreadNotificationCount: 0,
    notifications: [],
    readSystemNotificationKeys: []
  });
  const [adminManagedNotifications, setAdminManagedNotifications] = useState([]);
  const [adminNotificationLoading, setAdminNotificationLoading] = useState(false);
  const [adminNotificationSaving, setAdminNotificationSaving] = useState(false);
  const [adminNotificationNotice, setAdminNotificationNotice] = useState("");
  const [adminNotificationDraft, setAdminNotificationDraft] = useState(createDefaultAdminNotificationDraft);
  const [editingAdminNotificationId, setEditingAdminNotificationId] = useState(0);
  const [adminInquiryLoading, setAdminInquiryLoading] = useState(false);
  const [adminInquiryActionLoading, setAdminInquiryActionLoading] = useState(false);
  const [adminInquirySelectedIds, setAdminInquirySelectedIds] = useState([]);
  const [adminInquiryDetail, setAdminInquiryDetail] = useState(null);
  const [adminInquiryPage, setAdminInquiryPage] = useState(1);
  const [adminInquiryPagination, setAdminInquiryPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 1
  });
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminListPages, setAdminListPages] = useState(DEFAULT_ADMIN_LIST_PAGES);
  const [memberRoleFilter, setMemberRoleFilter] = useState("");
  const [memberStatusFilter, setMemberStatusFilter] = useState("");
  const [memberSelectedIds, setMemberSelectedIds] = useState([]);
  const [userDraftById, setUserDraftById] = useState({});
  const [userSavingId, setUserSavingId] = useState(0);
  const [editUserModalUser, setEditUserModalUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState(createDefaultEditUserForm);
  const [editUserSaving, setEditUserSaving] = useState(false);
  const [editUserNotice, setEditUserNotice] = useState("");
  const [deleteUserModalUser, setDeleteUserModalUser] = useState(null);
  const [deleteUserConfirmText, setDeleteUserConfirmText] = useState("");
  const [deleteUserSaving, setDeleteUserSaving] = useState(false);
  const [deleteUserNotice, setDeleteUserNotice] = useState("");
  const [profileData, setProfileData] = useState(null);
  const [hasValidatedSession, setHasValidatedSession] = useState(false);
  const [passwordForm, setPasswordForm] = useState(createDefaultPasswordForm);
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordNotice, setPasswordNotice] = useState("");
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [userPasswordForm, setUserPasswordForm] = useState(createDefaultUserPasswordForm);
  const [userPasswordSaving, setUserPasswordSaving] = useState(false);
  const [userPasswordNotice, setUserPasswordNotice] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState(() => localStorage.getItem("ft_admin_settings_tab") || "general");
  const [storeMode, setStoreMode] = useState("b2c");
  const [updatingStoreMode, setUpdatingStoreMode] = useState(false);
  const [adminToast, setAdminToast] = useState("");
  const [storeSettingsDraft, setStoreSettingsDraft] = useState(createDefaultStoreSettingsDraft);
  const [blogArticleDrafts, setBlogArticleDrafts] = useState(() => normalizeAdminBlogArticles([]));
  const [editingBlogArticleIndex, setEditingBlogArticleIndex] = useState(-1);
  const [editingBlogArticleSnapshot, setEditingBlogArticleSnapshot] = useState(null);
  const [blogCoverPreviewFailed, setBlogCoverPreviewFailed] = useState(false);
  const [faqKnowledgeDrafts, setFaqKnowledgeDrafts] = useState(() =>
    normalizeFaqKnowledgeItems(DEFAULT_FAQ_KNOWLEDGE_ITEMS)
  );
  const [companyImageSectionsDraft, setCompanyImageSectionsDraft] = useState(() =>
    normalizeCompanyImageSections(DEFAULT_COMPANY_IMAGE_SECTIONS)
  );
  const [companyImageLinkDrafts, setCompanyImageLinkDrafts] = useState({});
  const [companyIntroductionDraft, setCompanyIntroductionDraft] = useState("");
  const [showCustomCompanySectionInput, setShowCustomCompanySectionInput] = useState(false);
  const [customCompanySectionName, setCustomCompanySectionName] = useState("");
  const [uploadingCompanyImageSectionKey, setUploadingCompanyImageSectionKey] = useState("");
  const [companyImageDragging, setCompanyImageDragging] = useState(null);
  const [companyImagePreviewModal, setCompanyImagePreviewModal] = useState(null);
  const [savingStoreSettings, setSavingStoreSettings] = useState(false);
  const [applyingStorefrontTheme, setApplyingStorefrontTheme] = useState(false);
  const [savingCompanyImages, setSavingCompanyImages] = useState(false);
  const [savingFaqKnowledge, setSavingFaqKnowledge] = useState(false);
  const [productReviewRows, setProductReviewRows] = useState([]);
  const [reviewFilterProductId, setReviewFilterProductId] = useState("");
  const [loadingProductReviews, setLoadingProductReviews] = useState(false);
  const [savingProductReview, setSavingProductReview] = useState(false);
  const [reviewDraft, setReviewDraft] = useState(createDefaultReviewDraft);
  const [savingSystemConfig, setSavingSystemConfig] = useState(false);
  const [systemConfigPath, setSystemConfigPath] = useState("");
  const [systemConfigDraft, setSystemConfigDraft] = useState(createDefaultSystemConfigDraft);
  const [savingNavMenu, setSavingNavMenu] = useState(false);
  const [navMenuDraft, setNavMenuDraft] = useState(createDefaultNavMenuDraft);
  const [editingNavPath, setEditingNavPath] = useState("");
  const [expandedNavRows, setExpandedNavRows] = useState(() => new Set());
  const [bannerDrafts, setBannerDrafts] = useState([]);
  const [savingBanners, setSavingBanners] = useState(false);
  const [editingBannerIndex, setEditingBannerIndex] = useState(null);
  const [pendingBannerDraft, setPendingBannerDraft] = useState(null);
  const [bannerEditSnapshot, setBannerEditSnapshot] = useState(null);
  const [uploadingBannerIndex, setUploadingBannerIndex] = useState(null);
  const [bannerPositionDragging, setBannerPositionDragging] = useState(false);
  const [financeWorkspaceData, setFinanceWorkspaceData] = useState(createDefaultAdminFinanceWorkspace);
  const [expandedSidebarGroups, setExpandedSidebarGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(SIDEBAR_GROUP_STORAGE_KEY) || "{}");
      return saved && typeof saved === "object" ? saved : {};
    } catch {
      return {};
    }
  });
  const [newProduct, setNewProduct] = useState(createEmptyProductEditorDraft);
  const [editingProductId, setEditingProductId] = useState(null);
  const [productEditorOriginalUploadUrls, setProductEditorOriginalUploadUrls] = useState([]);
  const [positionRows, setPositionRows] = useState(() =>
    readStoredList(POSITION_LIST_STORAGE_KEY, DEFAULT_POSITION_ROWS)
  );
  const [positionDraft, setPositionDraft] = useState({ name: "", key: "", department: "", permissionLevel: 0, permissions: [] });
  const [editingPositionId, setEditingPositionId] = useState(0);
  const [positionEditDraft, setPositionEditDraft] = useState({ name: "", key: "", department: "", permissionLevel: 0, permissions: [] });
  const [positionPermissionModal, setPositionPermissionModal] = useState(null);
  const [positionPermissionSelection, setPositionPermissionSelection] = useState([]);
  const [expandedPermissionGroupByTarget, setExpandedPermissionGroupByTarget] = useState({});
  const [positionPermissionSearch, setPositionPermissionSearch] = useState("");
  const [positionWorkspaceData, setPositionWorkspaceData] = useState(null);
  const [positionWorkspaceLoading, setPositionWorkspaceLoading] = useState(false);
  const [siteMaintenancePanelTab, setSiteMaintenancePanelTab] = useState(
    () => localStorage.getItem("ft_admin_site_maintenance_panel_tab") || "probes"
  );
  const [siteMaintenanceProbeData, setSiteMaintenanceProbeData] = useState(null);
  const [siteMaintenanceProbeLoading, setSiteMaintenanceProbeLoading] = useState(false);
  const [siteMaintenanceProbeHistory, setSiteMaintenanceProbeHistory] = useState({
    mysql: [],
    redis: []
  });
  const [merchandisingFilters, setMerchandisingFilters] = useState({
    targetMarket: "",
    priceBand: "",
    moqRange: "",
    sampleStatus: "",
    complianceStatus: ""
  });
  const [merchandisingView, setMerchandisingView] = useState(() => {
    const storedView = localStorage.getItem("ft_admin_merchandising_view");
    return ["overview", "risk", "decision", "focus", "collection"].includes(storedView) ? storedView : "overview";
  });
  const [isMerchandisingDrawerOpen, setIsMerchandisingDrawerOpen] = useState(false);
  const [procurementView, setProcurementView] = useState(() => localStorage.getItem("ft_admin_procurement_view") || "overview");
  const [qualityView, setQualityView] = useState(() => localStorage.getItem("ft_admin_quality_view") || "overview");
  const [logisticsView, setLogisticsView] = useState(() => localStorage.getItem("ft_admin_logistics_view") || "overview");
  const [hrRecruitmentStageById, setHrRecruitmentStageById] = useState(() =>
    readStoredJson(HR_RECRUITMENT_STAGE_STORAGE_KEY, {})
  );
  const [hrAttendanceOverrideById, setHrAttendanceOverrideById] = useState(() =>
    readStoredJson(HR_ATTENDANCE_OVERRIDE_STORAGE_KEY, {})
  );
  const [hrAttendanceImporting, setHrAttendanceImporting] = useState(false);
  const [hrAnnouncementDraft, setHrAnnouncementDraft] = useState({ title: "", content: "" });
  const [hrAnnouncements, setHrAnnouncements] = useState(() =>
    readStoredJson(HR_ANNOUNCEMENTS_STORAGE_KEY, [])
  );
  const [hrDocumentUploads, setHrDocumentUploads] = useState(() =>
    readStoredJson(HR_DOCUMENT_UPLOADS_STORAGE_KEY, [])
  );
  const [hrDocumentUploading, setHrDocumentUploading] = useState(false);
  const permissionItemRefs = useRef({});

  const accessRole = String(profileData?.role || adminRole || "customer").trim() || "customer";
  const isSuperAdmin = ["developer", "super-admin"].includes(accessRole);
  const canManageNotifications = ["developer", "super-admin", "admin"].includes(accessRole);
  const positionGroup = inferPositionGroup(profileData, accessRole);
  const permissionRows = useMemo(() => {
    const configuredCapabilityKeys = Array.isArray(CAPABILITY_LABELS) ? CAPABILITY_LABELS : [];
    const keys = Array.from(new Set(configuredCapabilityKeys)).sort((left, right) => left.localeCompare(right));

    return keys.map((key, index) => ({
      id: index + 1,
      key,
      name: formatCapabilityLabel(key, locale)
    }));
  }, [formatCapabilityLabel, locale]);
  const groupedPermissionRows = useMemo(() => {
    const groups = new Map();
    permissionRows.forEach((item) => {
      const groupKey = String(item?.key || "").split(":")[0] || "other";
      const list = groups.get(groupKey) || [];
      list.push(item);
      groups.set(groupKey, list);
    });

    return Array.from(groups.entries())
      .sort((left, right) => left[0].localeCompare(right[0]))
      .map(([group, items]) => ({ group, items }));
  }, [permissionRows]);
  const quickPermissionResults = useMemo(() => {
    const query = String(positionPermissionSearch || "").trim().toLowerCase();
    if (!query) {
      return [];
    }

    return permissionRows
      .filter((item) => {
        const key = String(item?.key || "").toLowerCase();
        const name = String(item?.name || "").toLowerCase();
        const zhNote = String(formatCapabilityLabel(item?.key, "zh") || "").toLowerCase();
        const enNote = String(formatCapabilityLabel(item?.key, "en") || "").toLowerCase();
        return key.includes(query) || name.includes(query) || zhNote.includes(query) || enNote.includes(query);
      })
      .slice(0, 12);
  }, [formatCapabilityLabel, permissionRows, positionPermissionSearch]);

  const scopedPositionName = String(
    profileData?.jobTitle ||
    profileData?.position ||
    profileData?.permissionGroup ||
    ""
  ).trim();
  const scopedPositionKey = normalizePositionScopeKey(scopedPositionName);
  const isScopedAdminByPosition = accessRole === "admin" && Boolean(scopedPositionKey);
  const isAwaitingAdminProfile = adminRole === "admin" && !profileData;

  const currentPositionCapabilities = useMemo(() => {
    if (!scopedPositionKey) {
      return [];
    }

    const matchedPosition = positionRows.find((item) => normalizePositionScopeKey(item?.key || item?.name || "") === scopedPositionKey);
    return Array.isArray(matchedPosition?.permissions) ? matchedPosition.permissions : [];
  }, [positionRows, scopedPositionKey]);

  const accessCapabilities = useMemo(() => {
    const fromServer = Array.isArray(profileData?.capabilities) ? profileData.capabilities : [];
    const fromRole = CAPABILITIES_BY_ROLE[accessRole] || CAPABILITIES_BY_ROLE.guest;
    const fromGroup = POSITION_TEMPLATE_CAPABILITIES[positionGroup] || [];
    const common = POSITION_TEMPLATE_CAPABILITIES.common;
    return Array.from(new Set([...fromServer, ...fromRole, ...fromGroup, ...currentPositionCapabilities, ...common]));
  }, [profileData, accessRole, positionGroup, currentPositionCapabilities]);

  const allowedTabs = useMemo(() => {
    const capabilitySet = new Set(accessCapabilities);
    const roleAllowedTabs = Object.keys(TAB_CAPABILITY_RULES).filter((tabKey) => {
      const needs = TAB_CAPABILITY_RULES[tabKey] || [];
      if (!needs.length) {
        return false;
      }
      return needs.some((capability) => capabilitySet.has(capability));
    });

    const commonTabs = ["profile", "password"];

    if (isAwaitingAdminProfile) {
      return roleAllowedTabs.filter((tabKey) => commonTabs.includes(tabKey));
    }

    if (!isScopedAdminByPosition) {
      return roleAllowedTabs;
    }

    const scopedTabs = POSITION_SCOPED_MENU_TABS[scopedPositionKey] || [];
    return roleAllowedTabs.filter((tabKey) => scopedTabs.includes(tabKey) || commonTabs.includes(tabKey));
  }, [accessCapabilities, isAwaitingAdminProfile, isScopedAdminByPosition, scopedPositionKey]);
  const allowedTabsSignature = useMemo(() => allowedTabs.join("|"), [allowedTabs]);

  const storeDesignMenus = useMemo(
    () => [
      { key: "storeBase", label: texts.storeBase },
      { key: "templateStudio", label: locale === "zh" ? "模板工作台" : "Template Studio" },
      { key: "articleManagement", label: texts.articleManagement },
      { key: "companyImages", label: texts.companyProfileMenu || texts.companyImages },
      { key: "designNav", label: texts.designNav },
      { key: "designCarousel", label: texts.designBanner || texts.designCarousel },
      { key: "faqKnowledge", label: texts.faqKnowledge }
    ],
    [locale, texts]
  );

  const merchandisingMenus = useMemo(
    () => [
      { key: "merchandisingOverview", label: texts.merchandisingOverview },
      { key: "merchandisingCollection", label: texts.merchandisingCollection }
    ],
    [texts]
  );

  const visibleStoreDesignMenus = useMemo(
    () => storeDesignMenus.filter((menu) => allowedTabs.includes(menu.key)),
    [storeDesignMenus, allowedTabs]
  );
  const hasMerchandisingAccess = allowedTabs.includes("merchandising");

  const internalPrimaryMenus = useMemo(
    () => [
      { key: "home", label: texts.home },
      { key: "merchandising", label: texts.merchandising },
      { key: "procurement", label: texts.procurement },
      { key: "solutions", label: texts.quality },
      { key: "logistics", label: texts.logistics },
      { key: "products", label: texts.products },
      { key: "productCreate", label: texts.productCreate },
      { key: "inquiry", label: texts.inquiry },
      { key: "customers", label: texts.customers },
      { key: "marketing", label: texts.marketing },
      { key: "discounts", label: texts.discounts },
      { key: "finance", label: texts.finance },
      { key: "financeIncome", label: texts.financeIncome },
      { key: "financeExpenses", label: texts.financeExpenses },
      { key: "financeReceivables", label: texts.financeReceivables },
      { key: "financePayables", label: texts.financePayables },
      { key: "financeInvoices", label: texts.financeInvoices },
      { key: "financeTax", label: texts.financeTax },
      { key: "financeBudget", label: texts.financeBudget },
      { key: "financeDaily", label: texts.financeDaily },
      { key: "financeMonthly", label: texts.financeMonthly },
      { key: "financeYearly", label: texts.financeYearly },
      { key: "financeReconciliation", label: texts.financeReconciliation },
      { key: "financeApprovals", label: texts.financeApprovals },
      { key: "financeReports", label: texts.financeReports },
      { key: "analytics", label: texts.analytics },
      { key: "settings", label: texts.settings }
    ],
    [texts]
  );

  const visibleInternalPrimaryMenus = useMemo(
    () => internalPrimaryMenus.filter((menu) => menu.key !== "merchandising" && allowedTabs.includes(menu.key)),
    [internalPrimaryMenus, allowedTabs]
  );

  const productSubMenus = useMemo(() => {
    const list = [
      { key: "productCreate", label: texts.productCreateMenu },
      { key: "products", label: texts.productsMenu }
    ];
    return list.filter((menu) => allowedTabs.includes(menu.key));
  }, [allowedTabs, texts]);

  const hasProductSubMenuAccess = productSubMenus.length > 0;

  const commonMenus = useMemo(
    () => [
      { key: "profile", label: texts.profile },
      { key: "password", label: texts.password }
    ],
    [texts]
  );

  const internalWorkMenus = useMemo(
    () => [
      { key: "workTargets", label: texts.workTargets },
      { key: "hrEmployee", label: texts.hrEmployee },
      { key: "hrRecruitment", label: texts.hrRecruitment },
      { key: "hrAttendance", label: texts.hrAttendance },
      { key: "hrOffice", label: texts.hrOffice }
    ],
    [texts]
  );

  const internalMaintenanceMenus = useMemo(
    () => [
      { key: "siteMaintenance", label: texts.siteMaintenance },
      { key: "positionMaintenance", label: texts.positionMaintenance }
    ],
    [texts]
  );

  const guestMenus = useMemo(
    () => [
      { key: "inquiry", label: texts.inquiry },
      { key: "wishlist", label: texts.wishlist }
    ],
    [texts]
  );

  const customerMenus = useMemo(
    () => [
      { key: "inquiry", label: texts.inquiry },
      { key: "orderProcessing", label: texts.orderProcessing },
      { key: "afterSales", label: texts.afterSales },
      { key: "wishlist", label: texts.wishlist }
    ],
    [texts]
  );

  const visibleCommonMenus = useMemo(
    () => commonMenus.filter((menu) => allowedTabs.includes(menu.key)),
    [commonMenus, allowedTabs]
  );

  const visibleInternalWorkMenus = useMemo(
    () => internalWorkMenus.filter((menu) => allowedTabs.includes(menu.key)),
    [internalWorkMenus, allowedTabs]
  );

  const visibleInternalMaintenanceMenus = useMemo(
    () => internalMaintenanceMenus.filter((menu) => allowedTabs.includes(menu.key)),
    [internalMaintenanceMenus, allowedTabs]
  );

  const hasMaintenancePanelAccess = useMemo(
    () => getRoleLevel(ROLE_LEVELS, String(accessRole || "guest").trim() || "guest") >= getRoleLevel(ROLE_LEVELS, "admin"),
    [accessRole]
  );

  const superAdminUserMenus = useMemo(() => {
    if (!hasMaintenancePanelAccess) {
      return [];
    }
    const order = ["siteMaintenance", "positionMaintenance"];
    return order
      .filter((key) => allowedTabs.includes(key))
      .map((key) => ({ key, label: texts[`${key}Menu`] || texts[key] || key }));
  }, [hasMaintenancePanelAccess, texts, allowedTabs]);

  const visibleInternalMaintenanceMenusFlat = useMemo(() => {
    if (!hasMaintenancePanelAccess) {
      return visibleInternalMaintenanceMenus;
    }

    const groupedKeys = new Set(["siteMaintenance", "positionMaintenance"]);
    return visibleInternalMaintenanceMenus.filter((menu) => !groupedKeys.has(menu.key));
  }, [hasMaintenancePanelAccess, visibleInternalMaintenanceMenus]);

  const visibleGuestMenus = useMemo(
    () => guestMenus.filter((menu) => allowedTabs.includes(menu.key)),
    [guestMenus, allowedTabs]
  );

  const visibleCustomerMenus = useMemo(
    () => customerMenus.filter((menu) => allowedTabs.includes(menu.key)),
    [customerMenus, allowedTabs]
  );

  const internalOverviewMenus = useMemo(
    () => pickMenusByKey(visibleInternalPrimaryMenus, ["home", "analytics", "settings"]),
    [visibleInternalPrimaryMenus]
  );

  const internalProductWorkspaceMenus = useMemo(() => {
    const primaryMenuMap = new Map(visibleInternalPrimaryMenus.map((menu) => [menu.key, menu]));
    const nextMenus = [];

    if (hasProductSubMenuAccess) {
      nextMenus.push(...productSubMenus);
    }

    if (hasMerchandisingAccess) {
      nextMenus.push(...merchandisingMenus);
    }

    ["procurement", "solutions", "logistics"].forEach((key) => {
      const menu = primaryMenuMap.get(key);
      if (menu) {
        nextMenus.push(menu);
      }
    });

    return nextMenus;
  }, [visibleInternalPrimaryMenus, hasProductSubMenuAccess, productSubMenus, hasMerchandisingAccess, merchandisingMenus]);

  const internalBusinessMenus = useMemo(
    () => pickMenusByKey(visibleInternalPrimaryMenus, ["inquiry", "orders", "customers", "marketing", "discounts", "finance", "financeIncome", "financeExpenses", "financeReceivables", "financePayables", "financeInvoices", "financeTax", "financeBudget", "financeDaily", "financeMonthly", "financeYearly", "financeReconciliation", "financeApprovals", "financeReports"]),
    [visibleInternalPrimaryMenus]
  );

  const maintenancePanelMenus = useMemo(
    () => [...visibleInternalMaintenanceMenusFlat, ...superAdminUserMenus],
    [visibleInternalMaintenanceMenusFlat, superAdminUserMenus]
  );

  const internalSidebarGroups = useMemo(
    () => [
      {
        key: "internalOverview",
        label: texts.internalOverview,
        items: internalOverviewMenus
      },
      {
        key: "internalWorkspace",
        label: texts.internalWorkspace,
        items: internalProductWorkspaceMenus
      },
      {
        key: "internalBusiness",
        label: texts.internalBusiness,
        items: internalBusinessMenus
      },
      {
        key: "internalOffice",
        label: texts.internalOffice,
        items: visibleInternalWorkMenus
      },
      {
        key: "internalMaintenance",
        label: texts.internalMaintenance,
        items: maintenancePanelMenus
      }
    ].filter((group) => group.items.length),
    [texts, internalOverviewMenus, internalProductWorkspaceMenus, internalBusinessMenus, visibleInternalWorkMenus, maintenancePanelMenus]
  );

  const storefrontSidebarGroups = useMemo(
    () => [
      {
        key: "storefrontContent",
        label: texts.storefrontContent,
        items: visibleStoreDesignMenus
      }
    ].filter((group) => group.items.length),
    [texts, visibleStoreDesignMenus]
  );

  const guestSidebarGroups = useMemo(
    () => [
      {
        key: "guestService",
        label: texts.guestService,
        items: visibleGuestMenus
      }
    ].filter((group) => group.items.length),
    [texts, visibleGuestMenus]
  );

  const customerSidebarGroups = useMemo(
    () => [
      {
        key: "customerService",
        label: texts.customerService,
        items: visibleCustomerMenus
      }
    ].filter((group) => group.items.length),
    [texts, visibleCustomerMenus]
  );

  const commonSidebarGroups = useMemo(
    () => [
      {
        key: "commonAccount",
        label: texts.commonAccount,
        items: visibleCommonMenus
      }
    ].filter((group) => group.items.length),
    [texts, visibleCommonMenus]
  );

  const sidebarSections = useMemo(() => {
    const sections = [];

    if (positionGroup === "internal" && internalSidebarGroups.length) {
      sections.push({ key: "internal", label: texts.menuInternal, groups: internalSidebarGroups });
    }

    if (storefrontSidebarGroups.length) {
      sections.push({ key: "storefront", label: texts.storeDesign, groups: storefrontSidebarGroups });
    }

    if (positionGroup === "guest" && guestSidebarGroups.length) {
      sections.push({ key: "guest", label: texts.menuGuest, groups: guestSidebarGroups });
    }

    if (positionGroup === "customer" && customerSidebarGroups.length) {
      sections.push({ key: "customer", label: texts.menuCustomer, groups: customerSidebarGroups });
    }

    if (commonSidebarGroups.length) {
      sections.push({ key: "common", label: texts.menuCommon, groups: commonSidebarGroups });
    }

    return sections;
  }, [positionGroup, internalSidebarGroups, storefrontSidebarGroups, guestSidebarGroups, customerSidebarGroups, commonSidebarGroups, texts]);

  const isSidebarMenuActive = useCallback((menuKey) => {
    if (menuKey === "merchandisingOverview") {
      return adminTab === "merchandising" && merchandisingView !== "collection";
    }

    if (menuKey === "merchandisingCollection") {
      return adminTab === "merchandising" && merchandisingView === "collection";
    }

    return adminTab === menuKey;
  }, [adminTab, merchandisingView]);

  const activeSidebarGroupKey = useMemo(() => {
    for (const section of sidebarSections) {
      for (const group of section.groups) {
        if (group.items.some((item) => isSidebarMenuActive(item.key))) {
          return group.key;
        }
      }
    }

    return "";
  }, [sidebarSections, isSidebarMenuActive]);

  const isSidebarGroupExpanded = useCallback(
    (groupKey) => expandedSidebarGroups[groupKey] !== false,
    [expandedSidebarGroups]
  );

  const normalizedAdminQuery = adminQuery.trim().toLowerCase();

  const normalizeUserStatus = useCallback((value) => {
    const normalizedStatus = String(value || "active").trim().toLowerCase();
    if (["disactive", "deactive", "inactive", "disabled", "停用", "禁用", "0"].includes(normalizedStatus)) {
      return "disactive";
    }
    return "active";
  }, []);

  const toUserDraft = useCallback((item) => ({
    name: String(item?.name || "").trim(),
    email: String(item?.email || "").trim(),
    role: String(item?.role || "customer").trim() || "customer",
    phone: String(item?.phone || "").trim(),
    status: normalizeUserStatus(item?.status),
    jobTitle: String(item?.jobTitle || "").trim()
  }), [normalizeUserStatus]);

  const formatUserTime = useCallback((value) => {
    const asNumber = Number(value);
    let date = null;

    if (Number.isFinite(asNumber) && asNumber > 0) {
      const epochMs = asNumber > 1e12 ? asNumber : asNumber * 1000;
      date = new Date(epochMs);
    } else if (typeof value === "string" && value.trim()) {
      date = new Date(value);
    }

    if (!date) {
      return "-";
    }

    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString();
  }, []);

  const formatCollectedPlatformLabel = useCallback((value) => {
    const normalized = String(value || "").trim().toUpperCase();
    if (normalized === "A1688") {
      return "1688";
    }
    if (normalized === "ICBU") {
      return "Alibaba ICBU";
    }
    return String(value || "").trim() || "Third-party";
  }, []);

  const formatCollectedMetaValue = useCallback((value, fallback = "-") => {
    if (value === null || value === undefined) {
      return fallback;
    }

    if (typeof value === "number") {
      return Number.isFinite(value) ? String(value) : fallback;
    }

    const text = String(value).trim();
    return text || fallback;
  }, []);

  const filteredAdminProducts = useMemo(() => {
    if (!normalizedAdminQuery) {
      return adminProducts;
    }

    return adminProducts.filter((item) => {
      const haystack = [item.sku, item.model, item.category, item.name?.en || item.name, item.description?.en]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalizedAdminQuery);
    });
  }, [adminProducts, normalizedAdminQuery]);

  const filteredAdminOrders = useMemo(() => {
    if (!normalizedAdminQuery) {
      return adminOrders;
    }

    return adminOrders.filter((item) => {
      const haystack = `${item.id} ${item.userId} ${item.status} ${item.currency}`.toLowerCase();
      return haystack.includes(normalizedAdminQuery);
    });
  }, [adminOrders, normalizedAdminQuery]);

  const filteredAdminUsers = useMemo(() => {
    if (!normalizedAdminQuery) {
      return adminUsers;
    }

    return adminUsers.filter((item) => {
      const haystack = `${item.id} ${item.username || ""} ${item.name} ${item.email} ${item.role} ${item.phone || ""} ${item.status || ""} ${item.jobTitle || ""}`.toLowerCase();
      return haystack.includes(normalizedAdminQuery);
    });
  }, [adminUsers, normalizedAdminQuery]);
  const memberRoleOptions = useMemo(
    () => Array.from(new Set(filteredAdminUsers.map((item) => String(item?.role || "").trim()).filter(Boolean))),
    [filteredAdminUsers]
  );
  const memberFilteredRows = useMemo(() => {
    return filteredAdminUsers.filter((item) => {
      const roleMatches = !memberRoleFilter || String(item?.role || "").trim() === memberRoleFilter;
      const statusMatches = !memberStatusFilter || normalizeUserStatus(item?.status) === memberStatusFilter;
      return roleMatches && statusMatches;
    });
  }, [filteredAdminUsers, memberRoleFilter, memberStatusFilter, normalizeUserStatus]);
  const paginatedMemberRows = useMemo(() => {
    const totalItems = memberFilteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ADMIN_LIST_PAGE_SIZE));
    const requestedPage = Number(adminListPages.userManagement || 1);
    const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
    const startIndex = (currentPage - 1) * ADMIN_LIST_PAGE_SIZE;

    return {
      items: memberFilteredRows.slice(startIndex, startIndex + ADMIN_LIST_PAGE_SIZE),
      currentPage,
      totalPages,
      totalItems,
      startIndex
    };
  }, [adminListPages.userManagement, memberFilteredRows]);

  const scopedWorkspace = useMemo(() => {
    if (!positionWorkspaceData || positionWorkspaceData.key !== scopedPositionKey) {
      return null;
    }

    return positionWorkspaceData;
  }, [positionWorkspaceData, scopedPositionKey]);
  const scopedWorkspaceMetrics = scopedWorkspace?.metrics || {};
  const scopedWorkspaceSections = scopedWorkspace?.sections || {};
  const merchandisingQueueRows = Array.isArray(scopedWorkspaceSections.merchandisingQueue)
    ? scopedWorkspaceSections.merchandisingQueue
    : [];
  const procurementQueueRows = Array.isArray(scopedWorkspaceSections.procurementQueue)
    ? scopedWorkspaceSections.procurementQueue
    : [];
  const qualityQueueRows = Array.isArray(scopedWorkspaceSections.qualityQueue)
    ? scopedWorkspaceSections.qualityQueue
    : [];
  const logisticsQueueRows = Array.isArray(scopedWorkspaceSections.logisticsQueue)
    ? scopedWorkspaceSections.logisticsQueue
    : [];
  const merchandisingCategorySummary = Array.isArray(scopedWorkspaceSections.categorySummary)
    ? scopedWorkspaceSections.categorySummary
    : [];
  const merchandisingPriceBandSummary = Array.isArray(scopedWorkspaceSections.priceBandSummary)
    ? scopedWorkspaceSections.priceBandSummary
    : [];
  const merchandisingReadinessBreakdown = Array.isArray(scopedWorkspaceSections.readinessBreakdown)
    ? scopedWorkspaceSections.readinessBreakdown
    : [];
  const merchandisingAlertRows = Array.isArray(scopedWorkspaceSections.selectionAlerts)
    ? scopedWorkspaceSections.selectionAlerts
    : [];
  const merchandisingMarketSummary = Array.isArray(scopedWorkspaceSections.marketSummary)
    ? scopedWorkspaceSections.marketSummary
    : [];
  const merchandisingSourcingBoard = Array.isArray(scopedWorkspaceSections.sourcingBoard)
    ? scopedWorkspaceSections.sourcingBoard
    : [];
  const merchandisingRecommendationBoard = Array.isArray(scopedWorkspaceSections.recommendationBoard)
    ? scopedWorkspaceSections.recommendationBoard
    : [];
  const collectedProductsImportedCount = useMemo(
    () => collectedProducts.filter((item) => Boolean(item.isFormalSelection) || Number(item.draftPid || 0) > 0).length,
    [collectedProducts]
  );
  const collectedProductsPendingCount = Math.max(collectedProducts.length - collectedProductsImportedCount, 0);
  const {
    merchandisingFilterOptions,
    filteredMerchandisingQueueRows,
    filteredMerchandisingAlertRows,
    filteredMerchandisingSourcingBoard
  } = MerchandisingFilters({
    merchandisingQueueRows,
    merchandisingAlertRows,
    merchandisingSourcingBoard,
    merchandisingFilters
  });
  const {
    merchandisingHeroMetrics,
    merchandisingSummaryMaxima,
    merchandisingExecutiveQueue,
    merchandisingExecutiveActions,
    merchandisingDrawerTabs,
    activeMerchandisingDrawerTab,
    merchandisingViewTabs
  } = MerchandisingSummary({
    locale,
    merchandisingFilters,
    filteredMerchandisingAlertRows,
    filteredMerchandisingQueueRows,
    filteredMerchandisingSourcingBoard,
    scopedWorkspaceMetrics,
    merchandisingReadinessBreakdown,
    merchandisingCategorySummary,
    merchandisingMarketSummary,
    merchandisingRecommendationBoard,
    merchandisingView
  });
  const merchandisingLeadItem = merchandisingExecutiveQueue[0] || null;
  const merchandisingDecisionLeadItem = filteredMerchandisingSourcingBoard[0] || null;
  const merchandisingRiskLeadItem = filteredMerchandisingAlertRows[0] || null;
  const {
    procurementReadyRows,
    procurementCostingRows,
    procurementPriorityRows,
    qualityInspectionRows,
    qualityBlockedRows,
    qualityPriorityRows,
    logisticsInTransitRows,
    logisticsPreShipmentRows,
    logisticsDeliveredRows,
    logisticsPriorityRows,
    procurementHeroMetrics,
    qualityHeroMetrics,
    logisticsHeroMetrics,
    procurementViewTabs,
    qualityViewTabs,
    logisticsViewTabs
  } = OperationsViewSummary({
    locale,
    scopedWorkspaceMetrics,
    procurementQueueRows,
    qualityQueueRows,
    logisticsQueueRows
  });
  const {
    paginatedPositionRows,
    paginatedUserManagementRows,
    paginatedProductRows,
    paginatedOrderRows,
    paginatedCustomerRows,
    paginatedBannerRows,
    paginatedCollectedProductRows,
    renderPagination
  } = Pagination({
    adminListPages,
    setAdminListPages,
    texts,
    positionRows,
    filteredAdminUsers,
    filteredAdminProducts,
    filteredAdminOrders,
    bannerDrafts,
    collectedProducts
  });
  const paginateAdminView = useCallback((items, pageKey) => {
    const list = Array.isArray(items) ? items : [];
    const totalItems = list.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ADMIN_LIST_PAGE_SIZE));
    const requestedPage = Number(adminListPages[pageKey] || 1);
    const currentPage = Math.min(Math.max(requestedPage, 1), totalPages);
    const startIndex = (currentPage - 1) * ADMIN_LIST_PAGE_SIZE;

    return {
      items: list.slice(startIndex, startIndex + ADMIN_LIST_PAGE_SIZE),
      currentPage,
      totalPages,
      totalItems,
      startIndex
    };
  }, [adminListPages]);
  const orderRowsByView = useMemo(() => ({
    all: filteredAdminOrders,
    paidQueue: filteredAdminOrders.filter((item) => String(item?.status || "") === "paid"),
    inTransit: filteredAdminOrders.filter((item) => String(item?.status || "") === "in_transit"),
    completed: filteredAdminOrders.filter((item) => String(item?.status || "") === "delivered"),
    afterSales: filteredAdminOrders.filter((item) => String(item?.status || "") === "cancelled")
  }), [filteredAdminOrders]);
  const customerRowsByView = useMemo(() => ({
    records: filteredAdminUsers,
    segments: filteredAdminUsers,
    tags: (() => {
      const tagged = filteredAdminUsers.filter((item) => {
        const status = String(item?.status || "active").toLowerCase();
        const role = String(item?.role || "").toLowerCase();
        return Number(item?.cartItems || 0) > 0 || status === "disactive" || role.includes("admin");
      });
      return tagged.length ? tagged : filteredAdminUsers;
    })()
  }), [filteredAdminUsers]);
  const activeOrderView = {
    orders: "all",
    ordersPaidQueue: "paidQueue",
    ordersInTransit: "inTransit",
    ordersCompleted: "completed",
    ordersAfterSales: "afterSales"
  }[adminTab] || "all";
  const activeCustomerView = {
    customers: "records",
    customersSegments: "segments",
    customersTags: "tags"
  }[adminTab] || "records";
  const activeOrderRows = orderRowsByView[activeOrderView] || filteredAdminOrders;
  const activeCustomerRows = customerRowsByView[activeCustomerView] || filteredAdminUsers;
  const activePaginatedOrderRows = useMemo(
    () => (activeOrderView === "all" ? paginatedOrderRows : paginateAdminView(activeOrderRows, "orders")),
    [activeOrderRows, activeOrderView, paginateAdminView, paginatedOrderRows]
  );
  const activePaginatedCustomerRows = useMemo(
    () => (activeCustomerView === "records" ? paginatedCustomerRows : paginateAdminView(activeCustomerRows, "customers")),
    [activeCustomerView, activeCustomerRows, paginateAdminView, paginatedCustomerRows]
  );
  const siteMaintenanceProbes = Array.isArray(siteMaintenanceProbeData?.probes)
    ? siteMaintenanceProbeData.probes
    : [];
  const siteMaintenanceSummary = siteMaintenanceProbeData?.summary && typeof siteMaintenanceProbeData.summary === "object"
    ? siteMaintenanceProbeData.summary
    : { total: siteMaintenanceProbes.length, healthy: 0, unhealthy: 0 };
  const siteMaintenanceConfig = siteMaintenanceProbeData?.config && typeof siteMaintenanceProbeData.config === "object"
    ? siteMaintenanceProbeData.config
    : {};
  const siteMaintenanceStatus = String(siteMaintenanceProbeData?.status || "").toLowerCase();
  const siteMaintenanceIsHealthy = siteMaintenanceStatus === "ok";

  const formatProbeTime = useCallback((value) => {
    const timestamp = Number(value || 0);
    if (!Number.isFinite(timestamp) || timestamp <= 0) {
      return "-";
    }

    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }

    return date.toLocaleString(locale === "zh" ? "zh-CN" : "en-US");
  }, [locale]);

  const getProbeTone = useCallback((available) => {
    return available ? {
      color: "#0f766e",
      background: "rgba(15, 118, 110, 0.12)",
      border: "1px solid rgba(15, 118, 110, 0.22)"
    } : {
      color: "#b91c1c",
      background: "rgba(185, 28, 28, 0.12)",
      border: "1px solid rgba(185, 28, 28, 0.22)"
    };
  }, []);
  const mysqlProbeHistory = Array.isArray(siteMaintenanceProbeHistory?.mysql)
    ? siteMaintenanceProbeHistory.mysql
    : [];
  const redisProbeHistory = Array.isArray(siteMaintenanceProbeHistory?.redis)
    ? siteMaintenanceProbeHistory.redis
    : [];
  const renderProbeHistoryStrip = useCallback((historyRows = []) => {
    if (!Array.isArray(historyRows) || !historyRows.length) {
      return (
        <p className="controlhub-merchandising-caption" style={{ margin: 0 }}>
          {locale === "zh" ? "暂无趋势数据" : "No trend data yet"}
        </p>
      );
    }

    const healthyCount = historyRows.filter((item) => item?.available).length;
    const healthRate = Math.round((healthyCount / historyRows.length) * 100);

    return (
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
          <strong style={{ fontSize: 13 }}>{locale === "zh" ? "最近20次探针趋势" : "Recent 20 checks"}</strong>
          <small style={{ color: "#475569" }}>{locale === "zh" ? `可用率 ${healthRate}%` : `Availability ${healthRate}%`}</small>
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(${historyRows.length}, minmax(0, 1fr))`,
            gap: 4,
            alignItems: "end",
            height: 38,
            padding: "6px 8px",
            borderRadius: 10,
            background: "#f8fafc",
            border: "1px solid #e2e8f0"
          }}
        >
          {historyRows.map((item, index) => (
            <span
              key={`probe-trend-${Number(item?.checkedAt || 0)}-${index}`}
              title={`${formatProbeTime(item?.checkedAt)} / ${item?.available ? (locale === "zh" ? "正常" : "OK") : (locale === "zh" ? "异常" : "DOWN")}`}
              style={{
                height: item?.available ? "100%" : "34%",
                borderRadius: 999,
                background: item?.available ? "#0f766e" : "#b91c1c",
                opacity: index === historyRows.length - 1 ? 1 : 0.82
              }}
            />
          ))}
        </div>
      </div>
    );
  }, [formatProbeTime, locale]);

  const {
    togglePermissionSelection,
    openPositionPermissionModal,
    closePositionPermissionModal,
    quickSelectPermission,
    focusPermissionSelection,
    togglePermissionGroup,
    applyPositionPermissionSelection
  } = PositionPermissionModalActions({
    normalizePermissionKeys,
    setPositionPermissionSelection,
    positionEditDraft,
    positionDraft,
    setPositionPermissionModal,
    setPositionPermissionSearch,
    groupedPermissionRows,
    setExpandedPermissionGroupByTarget,
    positionPermissionModal,
    permissionItemRefs,
    positionPermissionSelection,
    setPositionEditDraft,
    setPositionDraft
  });
  const operationsChecklist = Array.isArray(scopedWorkspaceSections.publishChecklist)
    ? scopedWorkspaceSections.publishChecklist
    : [];
  const operationsBanners = Array.isArray(scopedWorkspaceSections.banners)
    ? scopedWorkspaceSections.banners
    : [];
  const salesRecentOrders = Array.isArray(scopedWorkspaceSections.recentOrders)
    ? scopedWorkspaceSections.recentOrders
    : [];
  const salesCustomerAccounts = Array.isArray(scopedWorkspaceSections.customerAccounts)
    ? scopedWorkspaceSections.customerAccounts
    : [];
  const salesFollowUpQueue = Array.isArray(scopedWorkspaceSections.followUpQueue)
    ? scopedWorkspaceSections.followUpQueue
    : [];
  const hrStaffRoster = Array.isArray(scopedWorkspaceSections.staffRoster)
    ? scopedWorkspaceSections.staffRoster
    : [];
  const hrPositionCoverage = Array.isArray(scopedWorkspaceSections.positionCoverage)
    ? scopedWorkspaceSections.positionCoverage
    : [];
  const hrTaskBoard = Array.isArray(scopedWorkspaceSections.taskBoard)
    ? scopedWorkspaceSections.taskBoard
    : [];
  const { hrEligibleIdSet, hrRecruitmentRows, hrEmployeeRows } = HrAdminData({
    hrStaffRoster,
    hrTaskBoard,
    hrRecruitmentStageById,
    setHrRecruitmentStageById,
    hrAttendanceOverrideById,
    setHrAttendanceOverrideById,
    hrAnnouncements,
    hrDocumentUploads,
    normalizeUserStatus
  });

  // 提前定义 profileDisplayName，避免未初始化错误
  const profileDisplayName = String(profileData?.name || "").trim() || "-";

  const {
    updateHrRecruitmentStage,
    importHrAttendance,
    publishHrAnnouncement,
    deleteHrAnnouncement,
    uploadHrDocument,
    deleteHrDocument
  } = HrAdminActions({
    hrEligibleIdSet,
    setHrRecruitmentStageById,
    setHrAttendanceOverrideById,
    normalizeUserStatus,
    setHrAttendanceImporting,
    hrAnnouncementDraft,
    setHrAnnouncementDraft,
    setHrAnnouncements,
    profileDisplayName,
    setHrDocumentUploading,
    setHrDocumentUploads,
    locale,
    setAdminError,
    setAdminToast
  });
  const merchandisingBreakdownLabels = {
    ready: locale === "zh" ? "可直接推进" : "Ready to Launch",
    asset_incomplete: locale === "zh" ? "素材待补齐" : "Asset Incomplete",
    translation_risk: locale === "zh" ? "多语言风险" : "Translation Risk",
    pricing_pending: locale === "zh" ? "待核价" : "Pricing Pending"
  };
  const merchandisingPriceBandLabels = {
    entry: locale === "zh" ? "引流款" : "Entry",
    core: locale === "zh" ? "核心款" : "Core",
    premium: locale === "zh" ? "高客单价款" : "Premium"
  };
  const merchandisingAlertLabels = {
    missing_price: locale === "zh" ? "缺少价格" : "Missing Price",
    missing_model: locale === "zh" ? "缺少型号" : "Missing Model",
    missing_main_images: locale === "zh" ? "主图不足" : "Insufficient Main Images",
    missing_detail_assets: locale === "zh" ? "详情素材不足" : "Insufficient Detail Assets",
    missing_parameters: locale === "zh" ? "参数不完整" : "Missing Parameters",
    low_translation_coverage: locale === "zh" ? "多语言覆盖不足" : "Low Translation Coverage"
  };
  const merchandisingActionLabels = {
    prioritize_sample_review: locale === "zh" ? "优先推进样品确认" : "Prioritize sample review",
    complete_compliance_docs: locale === "zh" ? "补齐认证与合规资料" : "Complete compliance documents",
    confirm_packaging_spec: locale === "zh" ? "确认包装规范与装箱资料" : "Confirm packaging specification"
  };
  const merchandisingStatusLabels = {
    sample_confirmed: locale === "zh" ? "样品已确认" : "Sample Confirmed",
    sample_pending: locale === "zh" ? "待打样/待确认" : "Sample Pending",
    docs_ready: locale === "zh" ? "认证资料齐全" : "Docs Ready",
    docs_pending: locale === "zh" ? "认证资料待补" : "Docs Pending",
    packaging_ready: locale === "zh" ? "包装资料已就绪" : "Packaging Ready",
    packaging_pending: locale === "zh" ? "包装资料待确认" : "Packaging Pending",
    high: locale === "zh" ? "高优先级" : "High Priority",
    medium: locale === "zh" ? "中优先级" : "Medium Priority",
    normal: locale === "zh" ? "常规" : "Normal"
  };
  const merchandisingChannelLabels = {
    boutique_studio: locale === "zh" ? "精品工作室/高端零售" : "Boutique Studio / Premium Retail",
    amazon_retail: locale === "zh" ? "平台零售/常规批发" : "Marketplace Retail / Core Wholesale",
    promotion_wholesale: locale === "zh" ? "促销批发/团购渠道" : "Promotional Wholesale"
  };
  const procurementSuggestionLabels = {
    issue_purchase_request: locale === "zh" ? "可直接发起采购" : "Issue purchase request",
    complete_costing: locale === "zh" ? "先补齐成本与报价" : "Complete costing first"
  };
  const qualityStageLabels = {
    sample_and_pre_shipment: locale === "zh" ? "样品确认与出货前验货" : "Sample review and pre-shipment QA",
    waiting_merchandising: locale === "zh" ? "等待选品资料补齐" : "Waiting for merchandising inputs"
  };
  const logisticsStatusLabels = {
    paid: locale === "zh" ? "已付款" : "Paid",
    packed: locale === "zh" ? "已备货" : "Packed",
    in_transit: locale === "zh" ? "运输中" : "In Transit",
    delivered: locale === "zh" ? "已送达" : "Delivered",
    cancelled: locale === "zh" ? "已取消" : "Cancelled"
  };
  const getMerchandisingBadgeTone = useCallback((value) => {
    const key = String(value || "").trim().toLowerCase();
    if (["ready", "sample_confirmed", "docs_ready", "packaging_ready"].includes(key)) {
      return "positive";
    }
    if (["high", "missing_price", "missing_model", "missing_main_images", "missing_detail_assets", "missing_parameters", "low_translation_coverage"].includes(key)) {
      return "danger";
    }
    if (["sample_pending", "docs_pending", "packaging_pending", "medium", "premium"].includes(key)) {
      return "warning";
    }
    if (["core", "entry", "normal", "boutique_studio", "amazon_retail", "promotion_wholesale"].includes(key)) {
      return "accent";
    }
    return "neutral";
  }, []);
  const getLogisticsBadgeTone = useCallback((value) => {
    const key = String(value || "").trim().toLowerCase();
    if (key === "delivered") {
      return "positive";
    }
    if (key === "in_transit") {
      return "accent";
    }
    if (key === "cancelled") {
      return "danger";
    }
    return "warning";
  }, []);
  const openStorefront = useCallback(() => {
    window.open("/", "_blank", "noopener,noreferrer");
  }, []);
  const openMerchandisingDrawer = useCallback((nextView) => {
    setMerchandisingView(nextView);
    setIsMerchandisingDrawerOpen(true);
  }, []);

  // 增加 ROLE_LEVELS 运行时防御和调试日志，彻底避免 undefined 访问
  // 统一安全获取角色等级
  function getRoleLevel(RL, key) {
    if (!RL || typeof RL !== "object") return 0;
    if (!key || typeof key !== "string") return 0;
    return Object.prototype.hasOwnProperty.call(RL, key) ? RL[key] : 0;
  }

  const assignableRoleOptions = useMemo(() => {
    let RL = {};
    if (typeof ROLE_LEVELS === "object" && ROLE_LEVELS !== null) {
      RL = ROLE_LEVELS;
    }

    const serverRoles = Array.isArray(profileData?.assignableRoles)
      ? profileData.assignableRoles.map((item) => String(item || "").trim()).filter(Boolean)
      : [];
    // 防御性处理 roleKey 和 accessRole
    const safeAccessRole = (typeof accessRole === "string" && getRoleLevel(RL, accessRole) > 0) ? accessRole : "guest";
    const roleKeys = serverRoles.length
      ? serverRoles
      : ASSIGNABLE_ROLES.filter((roleKey) => {
          const safeRoleKey = (typeof roleKey === "string" && getRoleLevel(RL, roleKey) > 0) ? roleKey : "guest";
          return getRoleLevel(RL, safeRoleKey) < getRoleLevel(RL, safeAccessRole);
        });

    return roleKeys.map((roleKey) => ({ value: roleKey, label: roleLabels[roleKey] || roleKey }));
  }, [roleLabels, accessRole, profileData]);
  const positionSelectOptions = useMemo(() => {
    return Array.from(
      new Set(
        positionRows
          .map((item) => String(item?.name || "").trim())
          .filter(Boolean)
      ) 
      
    );
  }, [positionRows]);
  const accessCapabilitySet = useMemo(() => new Set(accessCapabilities), [accessCapabilities]);
  const canEditStorefront = accessCapabilitySet.has("admin:storefront:write");
  const canManageTrustContent = canEditStorefront || accessCapabilitySet.has("website:content:write");
  const accessCapabilityLabels = accessCapabilities.map(
    (item) => formatCapabilityLabel(item, locale) || item
  );
  const currentUserRow = useMemo(() => {
    const profileId = Number(profileData?.id || 0);
    const profileUsername = String(profileData?.username || "").trim().toLowerCase();
    const profileEmail = String(profileData?.email || "").trim().toLowerCase();

    return adminUsers.find((item) => {
      const itemId = Number(item?.id || 0);
      const itemUsername = String(item?.username || "").trim().toLowerCase();
      const itemEmail = String(item?.email || "").trim().toLowerCase();

      return (
        (profileId > 0 && itemId === profileId) ||
        (profileUsername && itemUsername && profileUsername === itemUsername) ||
        (profileEmail && itemEmail && profileEmail === itemEmail)
      );
    }) || null;
  }, [adminUsers, profileData]);
  const isCurrentAdminUser = useCallback((userItem) => {
    const currentId = Number(currentUserRow?.id || profileData?.id || 0);
    const itemId = Number(userItem?.id || 0);

    if (currentId > 0 && itemId > 0 && currentId === itemId) {
      return true;
    }

    const currentUsername = String(profileData?.username || currentUserRow?.username || "").trim().toLowerCase();
    const currentEmail = String(profileData?.email || currentUserRow?.email || "").trim().toLowerCase();
    const itemUsername = String(userItem?.username || "").trim().toLowerCase();
    const itemEmail = String(userItem?.email || "").trim().toLowerCase();

    return Boolean(
      (currentUsername && itemUsername && currentUsername === itemUsername) ||
      (currentEmail && itemEmail && currentEmail === itemEmail)
    );
  }, [currentUserRow, profileData]);
  const canManageAdminUser = useCallback((userItem) => {
    if (!userItem) {
      return false;
    }

    if (isCurrentAdminUser(userItem)) {
      return false;
    }

    const actorLevel = getRoleLevel(ROLE_LEVELS, String(accessRole || "guest").trim() || "guest");
    const targetLevel = getRoleLevel(ROLE_LEVELS, String(userItem.role || "guest").trim() || "guest");
    return actorLevel > targetLevel;
  }, [accessRole, isCurrentAdminUser]);
  const canManageUserAccount = useCallback((userItem) => {
    if (!canManageAdminUser(userItem)) {
      return false;
    }

    return accessCapabilitySet.has("admin:users:write") || accessCapabilitySet.has("admin:users:role:write");
  }, [accessCapabilitySet, canManageAdminUser]);
  const canAssignUserPosition = useCallback((userItem) => {
    if (!userItem || isCurrentAdminUser(userItem) || !accessCapabilitySet.has("admin:users:position:assign")) {
      return false;
    }

    const actorRoleKey = String(accessRole || "guest").trim() || "guest";
    const targetRoleKey = String(userItem.role || "guest").trim() || "guest";
    const actorLevel = getRoleLevel(ROLE_LEVELS, actorRoleKey);
    const targetLevel = getRoleLevel(ROLE_LEVELS, targetRoleKey);

    if (targetLevel < getRoleLevel(ROLE_LEVELS, "admin")) {
      return false;
    }

    if (actorLevel > targetLevel) {
      return true;
    }

    return actorRoleKey === "admin" && targetRoleKey === "admin";
  }, [accessCapabilitySet, accessRole, isCurrentAdminUser]);
  const canResetUserPassword = useCallback((userItem) => {
    return canManageAdminUser(userItem) && accessCapabilitySet.has("admin:users:password:reset");
  }, [accessCapabilitySet, canManageAdminUser]);
  const canDeleteAdminUser = useCallback((userItem) => {
    return canManageAdminUser(userItem) && accessCapabilitySet.has("admin:users:delete");
  }, [accessCapabilitySet, canManageAdminUser]);
  const canOpenUserEditModal = useCallback((userItem) => {
    return canManageUserAccount(userItem) || canAssignUserPosition(userItem);
  }, [canAssignUserPosition, canManageUserAccount]);
  const profileRoleKey = String(profileData?.role || adminRole || "customer").trim() || "customer";
  const profileRoleLabel = roleLabels[profileRoleKey] || profileRoleKey;
  const profileAvatarText = profileDisplayName && profileDisplayName !== "-"
    ? profileDisplayName.slice(0, 1).toUpperCase()
    : String(profileRoleLabel || "?").slice(0, 1).toUpperCase();
  const sidebarAssignedPosition = String(
    profileData?.jobTitle ||
    profileData?.position ||
    profileData?.postGroup ||
    profileData?.permissionGroup ||
    currentUserRow?.jobTitle ||
    ""
  ).trim();
  const sidebarDisplayName =
    String(currentUserRow?.name || "").trim() ||
    String(profileData?.name || "").trim() ||
    String(profileData?.username || "").trim() ||
    String(currentUserRow?.username || "").trim() ||
    String(profileData?.email || "").trim().split("@")[0] ||
    "-";
  const sidebarUsername =
    String(profileData?.username || "").trim() ||
    String(currentUserRow?.username || "").trim() ||
    "-";
  const shouldShowSidebarIdentity = true;
  const didHandleSessionExitRef = useRef(false);
  const toggleSidebarGroup = useCallback((groupKey) => {
    setExpandedSidebarGroups((prev) => ({
      ...prev,
      [groupKey]: !(prev[groupKey] !== false)
    }));
  }, []);

  useEffect(() => {
    didHandleSessionExitRef.current = false;
  }, [adminToken]);

  const adminFetch = useCallback(
    async (url, options = {}) => {
      const headers = {
        ...(options.headers || {}),
        Authorization: `Bearer ${adminToken}`
      };

      const requestUrl = String(url || "");
      let response;
      try {
        response = await fetch(url, { ...options, headers });
      } catch (err) {
        const message = String(err?.message || "").trim();
        if (message) {
          throw new Error(message);
        }
        throw err;
      }

      if (response.status === 401) {
        const isProfileProbe = /\/auth\/me(?:\?|$)/.test(requestUrl);

        if (!isProfileProbe) {
          try {
            const confirmResponse = await fetch(`${apiBaseUrl}/auth/me?ts=${Date.now()}`, {
              cache: "no-store",
              headers
            });

            if (confirmResponse.ok) {
              response = await fetch(url, { ...options, headers });
            }
          } catch {
            // Fall through to the logout path when session confirmation fails.
          }
        }

        if (response.status === 401) {
          if (!didHandleSessionExitRef.current) {
            didHandleSessionExitRef.current = true;
            onSessionExpired();
          }
          throw new Error("Session expired");
        }
      }

      if (response.status === 403) {
        let payload = null;
        try {
          payload = await response.clone().json();
        } catch {
          payload = null;
        }

        if (payload?.code === "ACCOUNT_RESTRICTED") {
          onRestrictedAccess?.();
          throw new Error("Restricted account");
        }

        throw new Error(String(payload?.message || "Forbidden"));
      }

      return response;
    },
    [adminToken, apiBaseUrl, onRestrictedAccess, onSessionExpired]
  );

  async function readApiJson(response) {
    const rawText = await response.text();
    if (!rawText) {
      return {};
    }

    try {
      return JSON.parse(rawText);
    } catch {
      const compact = rawText.replace(/\s+/g, " ").trim();
      const preview = compact.slice(0, 180) || "Response body is not valid JSON";
      throw new Error(preview);
    }
  }

  const {
    getPositionPermissionWorkingSelection,
    resetPositionDraft,
    createPosition,
    startEditPosition,
    savePosition,
    deletePosition
  } = PositionAdminActions({
    positionPermissionModal,
    positionPermissionSelection,
    setPositionDraft,
    positionDraft,
    normalizePermissionKeys,
    setAdminError,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    setPositionRows,
    setPositionEditDraft,
    positionEditDraft,
    setEditingPositionId,
    editingPositionId
  });

  const {
    productAnalyticsById,
    productAnalyticsDailyById,
    productAnalyticsLoading,
    productAnalyticsExporting,
    productAnalyticsRange,
    updateProductAnalyticsRange,
    fetchProductsAnalyticsOverview,
    exportProductsAnalyticsDailyCsv
  } = ProductAnalyticsAdmin({
    adminFetch,
    apiBaseUrl,
    canManageProducts: allowedTabs.includes("products") || allowedTabs.includes("productCreate"),
    adminProducts,
    locale,
    setAdminError,
    setAdminToast
  });

  const fetchAdminProducts = useCallback(async () => {
    const canManageProducts = allowedTabs.includes("products") || allowedTabs.includes("productCreate");
    if (!canManageProducts) {
      setAdminProducts([]);
      return;
    }

    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/products?ts=${Date.now()}`);
      const data = await readApiJson(response);
      setAdminProducts(Array.isArray(data?.products) ? data.products : []);
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Product data load failed");
      }
    }
  }, [adminFetch, allowedTabsSignature, apiBaseUrl]);

  const fetchCollectedProducts = useCallback(async () => {
    const canViewCollectedPool = allowedTabs.includes("merchandising");
    if (!canViewCollectedPool || !hasValidatedSession) {
      setCollectedProducts([]);
      return;
    }

    setCollectedProductsLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/collected-products?ts=${Date.now()}`);
      const data = await readApiJson(response);
      setCollectedProducts(Array.isArray(data?.collectedProducts) ? data.collectedProducts : []);
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Collected product data load failed");
      }
    } finally {
      setCollectedProductsLoading(false);
    }
  }, [adminFetch, allowedTabsSignature, apiBaseUrl, hasValidatedSession]);

  const fetchAdminProductReviews = useCallback(async (productIdInput = reviewFilterProductId) => {
    if (!allowedTabs.includes("faqKnowledge")) {
      setProductReviewRows([]);
      return;
    }

    const normalizedProductId = Number(String(productIdInput || "").trim());
    const hasProductFilter = Number.isFinite(normalizedProductId) && normalizedProductId > 0;
    const query = hasProductFilter
      ? `?productId=${encodeURIComponent(String(normalizedProductId))}&limit=100&ts=${Date.now()}`
      : `?limit=100&ts=${Date.now()}`;

    setLoadingProductReviews(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/product-reviews${query}`);
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Product review data load failed");
      }
      setProductReviewRows(Array.isArray(data?.reviews) ? data.reviews : []);
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Product review data load failed");
      }
    } finally {
      setLoadingProductReviews(false);
    }
  }, [adminFetch, allowedTabs, apiBaseUrl, reviewFilterProductId]);

  const fetchNotificationSummary = useCallback(async () => {
    if (!notificationSummaryAvailable) {
      return {
        unreadInquiryCount: 0,
        unreadNotificationCount: 0,
        notifications: [],
        readSystemNotificationKeys: []
      };
    }

    const response = await adminFetch(`${apiBaseUrl}/admin/notification-summary?ts=${Date.now()}`);
    const data = await readApiJson(response);
    if (response.status === 404) {
      setNotificationSummaryAvailable(false);
      setNotificationSummary({
        unreadInquiryCount: 0,
        unreadNotificationCount: 0,
        notifications: [],
        readSystemNotificationKeys: []
      });
      return {
        unreadInquiryCount: 0,
        unreadNotificationCount: 0,
        notifications: [],
        readSystemNotificationKeys: []
      };
    }

    if (!response.ok) {
      throw new Error(data?.message || "Notification summary load failed");
    }

    setNotificationSummary({
      unreadInquiryCount: Math.max(0, Number(data?.unreadInquiryCount || 0) || 0),
      unreadNotificationCount: Math.max(0, Number(data?.unreadNotificationCount || 0) || 0),
      notifications: Array.isArray(data?.notifications) ? data.notifications : [],
      readSystemNotificationKeys: Array.isArray(data?.readSystemNotificationKeys)
        ? data.readSystemNotificationKeys.map((item) => String(item || "").trim()).filter(Boolean)
        : []
    });

    return data;
  }, [adminFetch, apiBaseUrl, notificationSummaryAvailable]);

  const fetchManagedNotifications = useCallback(async () => {
    if (!allowedTabs.includes("notifications")) {
      setAdminManagedNotifications([]);
      return [];
    }

    setAdminNotificationLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/notifications/manage?page=1&pageSize=100&ts=${Date.now()}`);
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Notification list load failed");
      }

      const notifications = Array.isArray(data?.notifications) ? data.notifications : [];
      setAdminManagedNotifications(notifications);
      return notifications;
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminNotificationNotice(err.message || "Notification list load failed");
      }
      return [];
    } finally {
      setAdminNotificationLoading(false);
    }
  }, [adminFetch, allowedTabs, apiBaseUrl]);

  const handleNotificationDraftChange = useCallback((field, value) => {
    setAdminNotificationDraft((prev) => {
      if (field === "targetType") {
        if (value === "all_staff") {
          return {
            ...prev,
            targetType: value,
            targetRoles: ["admin"],
            targetUserIdsText: ""
          };
        }

        if (value === "roles") {
          return {
            ...prev,
            targetType: value,
            targetUserIdsText: ""
          };
        }

        if (value === "users") {
          return {
            ...prev,
            targetType: value,
            targetRoles: []
          };
        }
      }

      return {
        ...prev,
        [field]: value
      };
    });
  }, []);

  const resetNotificationEditor = useCallback(() => {
    setEditingAdminNotificationId(0);
    setAdminNotificationDraft(createDefaultAdminNotificationDraft());
    setAdminNotificationNotice("");
  }, []);

  const startEditNotification = useCallback((item) => {
    setEditingAdminNotificationId(Number(item?.id || 0) || 0);
    setAdminNotificationDraft({
      title: String(item?.title || ""),
      message: String(item?.message || ""),
      level: String(item?.level || "info"),
      targetType: String(item?.targetType || "all_staff"),
      targetRoles: Array.isArray(item?.targetRoles) ? item.targetRoles : [],
      targetUserIdsText: Array.isArray(item?.recipientUserIds) ? item.recipientUserIds.join(", ") : ""
    });
    setAdminNotificationNotice("");
  }, []);

  const submitNotification = useCallback(async () => {
    if (!canManageNotifications) {
      return;
    }

    const payload = toAdminNotificationPayload(adminNotificationDraft);
    if (!payload.title) {
      setAdminNotificationNotice(locale === "zh" ? "请填写通知标题。" : "Please provide a notification title.");
      return;
    }
    if (!payload.message) {
      setAdminNotificationNotice(locale === "zh" ? "请填写通知内容。" : "Please provide a notification message.");
      return;
    }
    if (payload.targetType === "roles" && !payload.targetRoles.length) {
      setAdminNotificationNotice(locale === "zh" ? "请至少选择一个接收角色。" : "Select at least one target role.");
      return;
    }
    if (payload.targetType === "users" && !payload.targetUserIds.length) {
      setAdminNotificationNotice(locale === "zh" ? "请输入至少一个接收人 ID。" : "Provide at least one recipient ID.");
      return;
    }

    setAdminNotificationSaving(true);
    setAdminNotificationNotice("");
    try {
      const endpoint = editingAdminNotificationId
        ? `${apiBaseUrl}/admin/notifications/${editingAdminNotificationId}`
        : `${apiBaseUrl}/admin/notifications`;
      const response = await adminFetch(endpoint, {
        method: editingAdminNotificationId ? "PATCH" : "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(payload)
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Notification save failed");
      }

      setAdminNotificationNotice(
        editingAdminNotificationId
          ? (locale === "zh" ? "通知已更新。" : "Notification updated.")
          : (locale === "zh" ? "通知已发布。" : "Notification published.")
      );
      resetNotificationEditor();
      await Promise.all([fetchManagedNotifications(), fetchNotificationSummary()]);
      setAdminToast(locale === "zh" ? "通知池已同步更新。" : "Notification pool synced.");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminNotificationNotice(err.message || "Notification save failed");
      }
    } finally {
      setAdminNotificationSaving(false);
    }
  }, [adminFetch, adminNotificationDraft, canManageNotifications, editingAdminNotificationId, apiBaseUrl, fetchManagedNotifications, fetchNotificationSummary, locale, resetNotificationEditor]);

  const deleteNotification = useCallback(async (item) => {
    const notificationId = Number(item?.id || 0);
    if (!canManageNotifications || !Number.isFinite(notificationId) || notificationId <= 0) {
      return;
    }

    const confirmed = window.confirm(
      locale === "zh"
        ? `确认删除通知“${item?.title || "未命名通知"}”吗？`
        : `Delete notification "${item?.title || "Untitled notification"}"?`
    );
    if (!confirmed) {
      return;
    }

    setAdminNotificationSaving(true);
    setAdminNotificationNotice("");
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/notifications/${notificationId}`, {
        method: "DELETE"
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Notification delete failed");
      }

      if (editingAdminNotificationId === notificationId) {
        resetNotificationEditor();
      }
      setAdminNotificationNotice(locale === "zh" ? "通知已删除。" : "Notification deleted.");
      await Promise.all([fetchManagedNotifications(), fetchNotificationSummary()]);
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminNotificationNotice(err.message || "Notification delete failed");
      }
    } finally {
      setAdminNotificationSaving(false);
    }
  }, [adminFetch, apiBaseUrl, canManageNotifications, editingAdminNotificationId, fetchManagedNotifications, fetchNotificationSummary, locale, resetNotificationEditor]);

  const fetchAdminInquiries = useCallback(async (pageInput = adminInquiryPage) => {
    if (!allowedTabs.includes("inquiry")) {
      setAdminInquiries([]);
      setAdminInquirySelectedIds([]);
      return;
    }

    const normalizedPage = Number.isFinite(Number(pageInput)) && Number(pageInput) > 0
      ? Math.floor(Number(pageInput))
      : 1;
    const normalizedPageSize = Number(adminInquiryPagination.pageSize || 20) > 0
      ? Math.min(200, Math.floor(Number(adminInquiryPagination.pageSize || 20)))
      : 20;

    setAdminInquiryLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/inquiries?page=${normalizedPage}&pageSize=${normalizedPageSize}&ts=${Date.now()}`);
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Inquiry data load failed");
      }

      const inquiries = Array.isArray(data?.inquiries) ? data.inquiries : [];
      const pagination = data?.pagination && typeof data.pagination === "object"
        ? data.pagination
        : {};

      setAdminInquiries(inquiries);
      setAdminInquirySelectedIds([]);
      setAdminInquiryPagination({
        page: Number(pagination.page || normalizedPage) || normalizedPage,
        pageSize: Number(pagination.pageSize || normalizedPageSize) || normalizedPageSize,
        totalCount: Number(pagination.totalCount || inquiries.length) || 0,
        totalPages: Math.max(1, Number(pagination.totalPages || 1) || 1)
      });
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Inquiry data load failed");
      }
    } finally {
      setAdminInquiryLoading(false);
    }
  }, [adminFetch, adminInquiryPage, adminInquiryPagination.pageSize, allowedTabs, apiBaseUrl]);

  const currentInquiryIds = useMemo(() => {
    return adminInquiries
      .map((item) => Number(item?.id || 0))
      .filter((id) => Number.isFinite(id) && id > 0);
  }, [adminInquiries]);

  const inquirySelectedIdSet = useMemo(() => {
    return new Set(adminInquirySelectedIds);
  }, [adminInquirySelectedIds]);

  const allCurrentInquiriesSelected = useMemo(() => {
    return currentInquiryIds.length > 0 && currentInquiryIds.every((id) => inquirySelectedIdSet.has(id));
  }, [currentInquiryIds, inquirySelectedIdSet]);

  const selectedInquiryCount = adminInquirySelectedIds.length;

  function toggleInquirySelection(inquiryId, checked) {
    const normalizedId = Number(inquiryId || 0);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) {
      return;
    }

    setAdminInquirySelectedIds((prev) => {
      const set = new Set(prev);
      if (checked) {
        set.add(normalizedId);
      } else {
        set.delete(normalizedId);
      }
      return Array.from(set);
    });
  }

  function toggleAllCurrentInquiries(checked) {
    if (!checked) {
      setAdminInquirySelectedIds([]);
      return;
    }

    setAdminInquirySelectedIds(currentInquiryIds);
  }

  async function updateSelectedInquiriesReadState(isRead) {
    if (!selectedInquiryCount || adminInquiryActionLoading) {
      return;
    }

    setAdminInquiryActionLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/inquiries/read`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          inquiryIds: adminInquirySelectedIds,
          isRead: Boolean(isRead)
        })
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Inquiry read state update failed");
      }

      setAdminToast(locale === "zh"
        ? (isRead ? "已标记为已读" : "已标记为未读")
        : (isRead ? "Marked as read" : "Marked as unread"));
      await fetchAdminInquiries(adminInquiryPage);
      await fetchNotificationSummary();
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Inquiry read state update failed");
      }
    } finally {
      setAdminInquiryActionLoading(false);
    }
  }

  async function deleteSelectedInquiries() {
    if (!selectedInquiryCount || adminInquiryActionLoading) {
      return;
    }

    setAdminInquiryActionLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/inquiries`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          inquiryIds: adminInquirySelectedIds
        })
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data?.message || "Inquiry delete failed");
      }

      setAdminToast(locale === "zh" ? "询盘已删除" : "Inquiries deleted");
      setAdminInquiryPage(1);
      await fetchAdminInquiries(1);
      await fetchNotificationSummary();
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Inquiry delete failed");
      }
    } finally {
      setAdminInquiryActionLoading(false);
    }
  }

  useEffect(() => {
    if (adminTab !== "faqKnowledge") {
      return;
    }

    fetchAdminProductReviews(reviewFilterProductId);
  }, [adminTab, fetchAdminProductReviews, reviewFilterProductId]);

  useEffect(() => {
    if (adminTab !== "inquiry") {
      return;
    }

    fetchAdminInquiries(adminInquiryPage);
  }, [adminInquiryPage, adminTab, fetchAdminInquiries]);

  useEffect(() => {
    if (adminTab !== "inquiry" || !allowedTabs.includes("inquiry")) {
      return;
    }

    let active = true;

    (async () => {
      try {
        const response = await adminFetch(`${apiBaseUrl}/admin/inquiries/read`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ markAll: true, isRead: true })
        });
        const data = await readApiJson(response);
        if (!response.ok) {
          throw new Error(data?.message || "Inquiry read state update failed");
        }

        if (active) {
          await fetchNotificationSummary();
          await fetchAdminInquiries(adminInquiryPage);
        }
      } catch (err) {
        // Keep background unread-sync failures silent so they do not leak into unrelated admin pages.
      }
    })();

    return () => {
      active = false;
    };
  }, [adminFetch, adminInquiryPage, adminTab, allowedTabs, apiBaseUrl, fetchAdminInquiries, fetchNotificationSummary]);

  async function saveProductReviewDraft() {
    if (savingProductReview || !canManageTrustContent) {
      return;
    }

    const productIdValue = Number(String(reviewDraft.productId || "").trim());
    if (!Number.isFinite(productIdValue) || productIdValue <= 0) {
      setAdminError(locale === "zh" ? "请输入有效的产品ID。" : "Please enter a valid product ID.");
      return;
    }

    if (!String(reviewDraft.reviewerName || "").trim() || !String(reviewDraft.reviewText || "").trim()) {
      setAdminError(locale === "zh" ? "买家名称和评论内容不能为空。" : "Reviewer name and review text are required.");
      return;
    }

    const payload = {
      productId: productIdValue,
      reviewerName: String(reviewDraft.reviewerName || "").trim(),
      country: String(reviewDraft.country || "").trim(),
      rating: Number(reviewDraft.rating || 5) || 5,
      variationText: String(reviewDraft.variationText || "").trim(),
      reviewText: String(reviewDraft.reviewText || "").trim(),
      reviewDate: String(reviewDraft.reviewDate || "").trim() || undefined,
      helpfulCount: Math.max(0, Number(reviewDraft.helpfulCount || 0) || 0),
      verifiedPurchase: Boolean(reviewDraft.verifiedPurchase),
      repeatBuyer: Boolean(reviewDraft.repeatBuyer),
      isFake: true
    };

    setSavingProductReview(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/product-reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Review save failed");
      }

      setReviewFilterProductId(String(productIdValue));
      setReviewDraft((prev) => ({
        ...prev,
        productId: String(productIdValue),
        reviewerName: "",
        country: "",
        rating: "5",
        variationText: "",
        reviewText: "",
        reviewDate: "",
        helpfulCount: "0",
        verifiedPurchase: true,
        repeatBuyer: false
      }));
      setAdminError("");
      setAdminToast(locale === "zh" ? "评论已保存" : "Review saved");
      await fetchAdminProductReviews(String(productIdValue));
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Review save failed");
      }
    } finally {
      setSavingProductReview(false);
    }
  }

  async function deleteProductReview(reviewId) {
    if (!canManageTrustContent) {
      return;
    }

    const normalizedReviewId = Number(reviewId || 0);
    if (!Number.isFinite(normalizedReviewId) || normalizedReviewId <= 0) {
      return;
    }

    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/product-reviews/${normalizedReviewId}`, {
        method: "DELETE"
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Review remove failed");
      }
      setAdminToast(locale === "zh" ? "评论已删除" : "Review removed");
      await fetchAdminProductReviews(reviewFilterProductId);
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Review remove failed");
      }
    }
  }

  const notifyStorefrontConfigUpdated = useCallback(() => {
    const timestamp = String(Date.now());
    window.localStorage.setItem(STOREFRONT_CONFIG_UPDATED_AT_KEY, timestamp);
    window.dispatchEvent(
      new CustomEvent(STOREFRONT_CONFIG_UPDATED_EVENT, {
        detail: { updatedAt: timestamp }
      })
    );
  }, []);

  const fetchPositionPermissionData = useCallback(async () => {
    const canReadPositions = allowedTabs.includes("siteMaintenance") || allowedTabs.includes("positionMaintenance");
    if (!canReadPositions || !hasValidatedSession) {
      return;
    }

    try {
      const positionResponse = await adminFetch(`${apiBaseUrl}/admin/positions`);
      const positionData = await readApiJson(positionResponse);
      if (positionResponse.ok && Array.isArray(positionData?.positions)) {
        setPositionRows(
          positionData.positions
            .map((item, index) => normalizePositionRow(item, index + 1))
            .filter((item) => item.name && item.key)
        );
      }
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Position data load failed");
      }
    }
  }, [allowedTabsSignature, adminFetch, apiBaseUrl, hasValidatedSession]);

  const fetchPositionWorkspaceData = useCallback(async () => {
    if (!hasValidatedSession || !scopedPositionKey) {
      setPositionWorkspaceData(null);
      return;
    }

    setPositionWorkspaceLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/workspaces/${scopedPositionKey}?ts=${Date.now()}`);
      const data = await readApiJson(response);
      if (response.ok && data?.workspace) {
        setPositionWorkspaceData(data.workspace);
      }
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Workspace data load failed");
      }
    } finally {
      setPositionWorkspaceLoading(false);
    }
  }, [adminFetch, apiBaseUrl, hasValidatedSession, scopedPositionKey]);

  const fetchSiteMaintenanceProbes = useCallback(async () => {
    if (!hasValidatedSession || !allowedTabs.includes("siteMaintenance")) {
      return;
    }

    setSiteMaintenanceProbeLoading(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/site-maintenance/probes?ts=${Date.now()}`);
      const data = await readApiJson(response);

      if (!response.ok) {
        throw new Error(data?.message || "Site maintenance probes load failed");
      }

      setSiteMaintenanceProbeData(data);
      setSiteMaintenanceProbeHistory((prev) => {
        const source = prev && typeof prev === "object" ? prev : {};
        const probes = Array.isArray(data?.probes) ? data.probes : [];
        if (!probes.length) {
          return source;
        }

        const next = { ...source };
        probes.forEach((item) => {
          const key = String(item?.key || "").trim();
          if (!key) {
            return;
          }

          const checkedAtRaw = Number(item?.checkedAt || Date.now());
          const checkedAt = Number.isFinite(checkedAtRaw) && checkedAtRaw > 0 ? checkedAtRaw : Date.now();
          const historyRow = {
            checkedAt,
            available: Boolean(item?.available)
          };

          const current = Array.isArray(source[key]) ? source[key] : [];
          const last = current[current.length - 1];
          const shouldAppend = !last || last.checkedAt !== historyRow.checkedAt || last.available !== historyRow.available;
          next[key] = (shouldAppend ? [...current, historyRow] : [...current]).slice(-20);
        });

        return next;
      });
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Site maintenance probes load failed");
      }
    } finally {
      setSiteMaintenanceProbeLoading(false);
    }
  }, [adminFetch, allowedTabsSignature, apiBaseUrl, hasValidatedSession]);

  const fetchAdminData = useCallback(async () => {
    try {
      const meData = await adminFetch(`${apiBaseUrl}/auth/me?ts=${Date.now()}`).then(readApiJson);
      setHasValidatedSession(true);

      const canViewOverview = allowedTabs.includes("home") || allowedTabs.includes("analytics");
      const canManageProducts = allowedTabs.includes("products") || allowedTabs.includes("productCreate");
      const canManageOrders = allowedTabs.includes("orders");
      const canManageUsers = allowedTabs.includes("customers") || allowedTabs.includes("permissionMaintenance");
      const canAssignPosition = allowedTabs.includes("positionMaintenance");
      const canManageSystemConfig = allowedTabs.includes("settings");
      const canReadStoreMode =
        allowedTabs.includes("storeBase") ||
        allowedTabs.includes("templateStudio") ||
        allowedTabs.includes("articleManagement") ||
        allowedTabs.includes("companyImages") ||
        allowedTabs.includes("faqKnowledge") ||
        allowedTabs.includes("designNav") ||
        allowedTabs.includes("designCarousel");
      const canReadFaqKnowledge = allowedTabs.includes("faqKnowledge");
      const canReadCompanyImages = allowedTabs.includes("companyImages");
      const canReadFinanceWorkspace = allowedTabs.some((tabKey) => String(tabKey || "").startsWith("finance"));

      const requests = [fetchNotificationSummary()];
      if (canViewOverview) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/overview`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ metrics: null }));
      }

      if (canManageProducts) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/products`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ products: [] }));
      }

      if (canManageOrders) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/orders`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ orders: [] }));
      }

      if (canManageUsers || canAssignPosition) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/users`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ users: [] }));
      }

      if (canReadStoreMode) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/store-mode`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ mode: "b2c", navMenu: [], storeSettings: null, banners: [] }));
      }
      if (canManageSystemConfig) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/system-config`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ config: null, configPath: "" }));
      }

      if (canReadFaqKnowledge) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/faq-knowledge`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ faqKnowledge: [] }));
      }

      if (canReadCompanyImages) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/company-images`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ companyImageSections: [] }));
      }

      if (canReadFinanceWorkspace) {
        requests.push(adminFetch(`${apiBaseUrl}/admin/finance-workspace`).then(readApiJson));
      } else {
        requests.push(Promise.resolve({ financeWorkspace: createDefaultAdminFinanceWorkspace() }));
      }

      const [, overviewData, productsData, ordersData, usersData, storeModeData, systemConfigData, faqKnowledgeData, companyImageData, financeWorkspaceResponse] = await Promise.all(requests);

      const nextProfileData = meData?.user || null;
      setProfileData((prev) =>
        isSameProfilePermissionState(prev, nextProfileData) ? prev : nextProfileData
      );

      setAdminOverview(overviewData.metrics || null);
      setAdminProducts(productsData.products || []);
      setAdminOrders(ordersData.orders || []);
      setAdminUsers(usersData.users || []);
      setFinanceWorkspaceData(
        financeWorkspaceResponse?.financeWorkspace && typeof financeWorkspaceResponse.financeWorkspace === "object"
          ? {
              ...createDefaultAdminFinanceWorkspace(),
              ...financeWorkspaceResponse.financeWorkspace
            }
          : createDefaultAdminFinanceWorkspace()
      );
      setStoreMode(storeModeData.mode === "b2b" ? "b2b" : "b2c");
      if (Array.isArray(storeModeData.navMenu)) {
        setNavMenuDraft(
          normalizeNavDraftItems(
            storeModeData.navMenu.filter((item) => item.key !== "home")
          )
        );
      }
      if (storeModeData.storeSettings && typeof storeModeData.storeSettings === "object") {
        setStoreSettingsDraft(toStoreSettingsDraft(storeModeData.storeSettings));
        setBlogArticleDrafts(
          normalizeAdminBlogArticles(storeModeData.storeSettings.blogArticles)
        );
        setFaqKnowledgeDrafts(
          normalizeFaqKnowledgeItems(storeModeData.storeSettings.faqKnowledgeBase)
        );
        setCompanyImageSectionsDraft(
          normalizeCompanyImageSections(storeModeData.storeSettings.companyImageSections)
        );
        setCompanyIntroductionDraft(
          String(storeModeData.storeSettings.companyIntroduction || "")
        );
      }

      if (canReadFaqKnowledge) {
        setFaqKnowledgeDrafts(
          normalizeFaqKnowledgeItems(
            faqKnowledgeData?.faqKnowledge,
            storeModeData?.storeSettings?.faqKnowledgeBase
          )
        );
      }

      if (canReadCompanyImages) {
        setCompanyImageSectionsDraft(
          normalizeCompanyImageSections(
            companyImageData?.companyImageSections,
            storeModeData?.storeSettings?.companyImageSections
          )
        );
        setCompanyIntroductionDraft(
          String(
            companyImageData?.companyIntroduction ??
            storeModeData?.storeSettings?.companyIntroduction ??
            ""
          )
        );
      }
      setBannerDrafts(
        Array.isArray(storeModeData.banners)
          ? storeModeData.banners.map((item, index) => ({
              id: String(item.id || `banner-${index + 1}`),
              title: String(item.title || item.annotation || item.subtitle || ""),
              annotation: String(item.annotation || item.subtitle || ""),
              buttonText: String(item.buttonText || item.cta || ""),
              buttonLink: String(item.buttonLink || ""),
              contentX: Math.max(4, Math.min(78, Math.round(Number(item.contentX ?? item.textX ?? 12) || 12))),
              contentY: Math.max(4, Math.min(78, Math.round(Number(item.contentY ?? item.textY ?? 52) || 52))),
              imageUrl: String(item.imageUrl || "")
            }))
          : []
      );
      if (systemConfigData?.config?.mysql) {
        setSystemConfigDraft({
          mysql: {
            host: String(systemConfigData.config.mysql.host || ""),
            port: Number(systemConfigData.config.mysql.port || 3306),
            database: String(systemConfigData.config.mysql.database || ""),
            user: String(systemConfigData.config.mysql.user || ""),
            password: String(systemConfigData.config.mysql.password || ""),
            poolSize: Number(systemConfigData.config.mysql.poolSize || 10),
            connectTimeoutMs: Number(systemConfigData.config.mysql.connectTimeoutMs || 8000)
          },
          testing: {
            disableFrontendLoginCaptcha: Boolean(systemConfigData.config.Testing?.DisableFrontendLoginCaptcha)
          },
          googleAuth: {
            enabled: Boolean(systemConfigData.config.GoogleAuth?.Enabled),
            clientId: String(systemConfigData.config.GoogleAuth?.ClientId || ""),
            allowedHostedDomains: String(systemConfigData.config.GoogleAuth?.AllowedHostedDomains || ""),
            proxyUrl: String(systemConfigData.config.GoogleAuth?.ProxyUrl || ""),
            allowInsecureOfflineVerify: Boolean(systemConfigData.config.GoogleAuth?.AllowInsecureOfflineVerify)
          },
          site: {
            websiteEnabled: Boolean(systemConfigData.config.Site?.websiteEnabled ?? true),
            allowUserRegistration: Boolean(systemConfigData.config.Site?.allowUserRegistration ?? true),
            minimumLoginRole: String(systemConfigData.config.Site?.minimumLoginRole || "guest")
          },
          emailTemplates: {
            branding: {
              companyName: String(systemConfigData.config.EmailTemplates?.Branding?.companyName || ""),
              accentColor: String(systemConfigData.config.EmailTemplates?.Branding?.accentColor || "#7a5446"),
              backgroundColor: String(systemConfigData.config.EmailTemplates?.Branding?.backgroundColor || "#f6efe8"),
              logoUrl: String(systemConfigData.config.EmailTemplates?.Branding?.logoUrl || ""),
              loginUrl: String(systemConfigData.config.EmailTemplates?.Branding?.loginUrl || ""),
              supportEmail: String(systemConfigData.config.EmailTemplates?.Branding?.supportEmail || ""),
              supportLine: String(systemConfigData.config.EmailTemplates?.Branding?.supportLine || ""),
              footerNote: String(systemConfigData.config.EmailTemplates?.Branding?.footerNote || "")
            },
            registration: {
              subject: String(systemConfigData.config.EmailTemplates?.Registration?.subject || ""),
              text: String(systemConfigData.config.EmailTemplates?.Registration?.text || ""),
              html: String(systemConfigData.config.EmailTemplates?.Registration?.html || "")
            },
            passwordReset: {
              subject: String(systemConfigData.config.EmailTemplates?.PasswordReset?.subject || ""),
              text: String(systemConfigData.config.EmailTemplates?.PasswordReset?.text || ""),
              html: String(systemConfigData.config.EmailTemplates?.PasswordReset?.html || "")
            },
            loginNotice: {
              enabled: Boolean(systemConfigData.config.EmailTemplates?.LoginNotice?.enabled),
              subject: String(systemConfigData.config.EmailTemplates?.LoginNotice?.subject || ""),
              text: String(systemConfigData.config.EmailTemplates?.LoginNotice?.text || ""),
              html: String(systemConfigData.config.EmailTemplates?.LoginNotice?.html || "")
            }
          }
        });
        setSystemConfigPath(String(systemConfigData.configPath || ""));
      }
      setAdminError("");
    } catch (err) {
      setHasValidatedSession(false);
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Admin data load failed");
      }
    }
  }, [apiBaseUrl, adminFetch, allowedTabsSignature]);

  const saveFinanceWorkspace = useCallback(async (nextWorkspace) => {
    const payload = nextWorkspace && typeof nextWorkspace === "object"
      ? { ...createDefaultAdminFinanceWorkspace(), ...nextWorkspace }
      : createDefaultAdminFinanceWorkspace();

    const response = await adminFetch(`${apiBaseUrl}/admin/finance-workspace`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({ financeWorkspace: payload })
    });
    const data = await readApiJson(response);
    if (!response.ok) {
      throw new Error(data?.message || "Finance workspace save failed");
    }

    const normalizedWorkspace = data?.financeWorkspace && typeof data.financeWorkspace === "object"
      ? { ...createDefaultAdminFinanceWorkspace(), ...data.financeWorkspace }
      : payload;
    setFinanceWorkspaceData(normalizedWorkspace);
    return normalizedWorkspace;
  }, [adminFetch, apiBaseUrl]);

  useEffect(() => {
    setHasValidatedSession(false);
  }, [adminToken]);

  useEffect(() => {
    setUserDraftById((prev) => {
      const next = {};
      adminUsers.forEach((item) => {
        next[item.id] = {
          ...(prev[item.id] || {}),
          ...toUserDraft(item)
        };
      });
      return next;
    });
  }, [adminUsers, toUserDraft]);

  useEffect(() => {
    setAdminListPages((prev) => {
      const next = { ...prev };
      let changed = false;
      const pageSources = {
        positions: positionRows,
        userManagement: filteredAdminUsers,
        products: filteredAdminProducts,
        orders: filteredAdminOrders,
        customers: filteredAdminUsers,
        banners: bannerDrafts,
        collectedProducts
      };

      Object.entries(pageSources).forEach(([key, list]) => {
        const totalPages = Math.max(1, Math.ceil((Array.isArray(list) ? list.length : 0) / ADMIN_LIST_PAGE_SIZE));
        const nextPage = Math.min(Math.max(Number(next[key] || 1), 1), totalPages);
        if (nextPage !== next[key]) {
          next[key] = nextPage;
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [bannerDrafts, collectedProducts, filteredAdminOrders, filteredAdminProducts, filteredAdminUsers, positionRows]);

  useEffect(() => {
    setAdminListPages((prev) => ({
      ...prev,
      userManagement: 1,
      products: 1,
      orders: 1,
      customers: 1
    }));
  }, [normalizedAdminQuery]);

  useEffect(() => {
    fetchAdminData();
  }, [fetchAdminData]);

  useEffect(() => {
    fetchPositionPermissionData();
  }, [fetchPositionPermissionData]);

  useEffect(() => {
    fetchPositionWorkspaceData();
  }, [fetchPositionWorkspaceData]);

  useEffect(() => {
    if (adminTab !== "siteMaintenance") {
      return;
    }

    void fetchSiteMaintenanceProbes();
  }, [adminTab, fetchSiteMaintenanceProbes]);

  useEffect(() => {
    if (adminTab !== "siteMaintenance") {
      return undefined;
    }

    const timer = window.setInterval(() => {
      void fetchSiteMaintenanceProbes();
    }, 30000);

    return () => window.clearInterval(timer);
  }, [adminTab, fetchSiteMaintenanceProbes]);

  useEffect(() => {
    fetchCollectedProducts();
  }, [fetchCollectedProducts]);

  useEffect(() => {
    if (!Array.isArray(allowedTabs) || !allowedTabs.length) {
      return;
    }

    if (allowedTabs.includes(adminTab)) {
      return;
    }

    // During refresh, admin profile/capabilities may arrive asynchronously.
    // Keep the stored tab until profile is ready to avoid jumping to profile tab.
    if (isAwaitingAdminProfile) {
      return;
    }

    setAdminTab(allowedTabs[0]);
  }, [allowedTabs, adminTab, isAwaitingAdminProfile]);

  useEffect(() => {
    if (!adminToast) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setAdminToast("");
    }, 2200);

    return () => window.clearTimeout(timer);
  }, [adminToast]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_GROUP_STORAGE_KEY, JSON.stringify(expandedSidebarGroups));
  }, [expandedSidebarGroups]);

  useEffect(() => {
    if (!activeSidebarGroupKey) {
      return;
    }

    setExpandedSidebarGroups((prev) => {
      if (prev[activeSidebarGroupKey] !== false) {
        return prev;
      }

      return {
        ...prev,
        [activeSidebarGroupKey]: true
      };
    });
  }, [activeSidebarGroupKey]);

  useEffect(() => {
    localStorage.setItem("ft_admin_tab", adminTab);
  }, [adminTab]);

  useEffect(() => {
    localStorage.setItem("ft_admin_merchandising_view", merchandisingView);
  }, [merchandisingView]);

  useEffect(() => {
    localStorage.setItem("ft_admin_procurement_view", procurementView);
  }, [procurementView]);

  useEffect(() => {
    localStorage.setItem("ft_admin_quality_view", qualityView);
  }, [qualityView]);

  useEffect(() => {
    localStorage.setItem("ft_admin_logistics_view", logisticsView);
  }, [logisticsView]);

  useEffect(() => {
    localStorage.setItem("ft_admin_site_maintenance_panel_tab", siteMaintenancePanelTab);
  }, [siteMaintenancePanelTab]);

  useEffect(() => {
    localStorage.setItem("ft_admin_show_settings", showSettings ? "1" : "0");
  }, [showSettings]);

  useEffect(() => {
    localStorage.setItem("ft_admin_settings_tab", settingsTab);
  }, [settingsTab]);

  useEffect(() => {
    window.localStorage.setItem(POSITION_LIST_STORAGE_KEY, JSON.stringify(positionRows));
  }, [positionRows]);

  function switchMenu(tab) {
    if (tab === "merchandisingOverview") {
      if (allowedTabs.includes("merchandising")) {
        setAdminTab("merchandising");
        setMerchandisingView("overview");
      }
      return;
    }

    if (tab === "merchandisingCollection") {
      if (allowedTabs.includes("merchandising")) {
        setAdminTab("merchandising");
        setMerchandisingView("collection");
      }
      return;
    }

    if (tab === "settings") {
      if (allowedTabs.includes("settings")) {
        setAdminTab("settings");
      }
      setShowSettings(false);
      return;
    }

    if (allowedTabs.includes(tab)) {
      if (tab === "products") {
        void fetchAdminProducts();
      }
      if (tab === "notifications" || tab === "notificationPlan") {
        void fetchManagedNotifications();
      }
      setAdminTab(tab);
    }
  }

  function parseMultilineUrls(text) {
    return String(text || "")
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }

  function normalizeAdminPermissionMessage(message, fallbackMessage) {
    const text = String(message || "").trim();
    if (!text) {
      return fallbackMessage;
    }

    const normalized = text.toLowerCase();
    if (
      normalized.includes("cannot modify a user with equal or higher role") ||
      normalized.includes("missing capability") ||
      normalized.includes("forbidden") ||
      normalized.includes("equal or higher role")
    ) {
      return texts.userManagementRestrictedHint || fallbackMessage;
    }

    return text;
  }

  const {
    openUserPasswordModal,
    openEditUserModal,
    closeEditUserModal,
    submitEditUser
  } = EditActions({
    canResetUserPassword,
    setAdminError,
    texts,
    setPasswordModalUser,
    setUserPasswordForm,
    setUserPasswordNotice,
    canOpenUserEditModal,
    setEditUserModalUser,
    setEditUserForm,
    normalizeUserStatus,
    setEditUserNotice,
    editUserSaving,
    editUserModalUser,
    setEditUserSaving,
    canManageUserAccount,
    canAssignUserPosition,
    accessCapabilitySet,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    fetchAdminData,
    setAdminToast,
    normalizeAdminPermissionMessage,
    editUserForm
  });
  const { closeUserPasswordModal, submitUserPasswordChange } = PasswordActions({
    userPasswordSaving,
    setPasswordModalUser,
    setUserPasswordForm,
    setUserPasswordNotice,
    passwordModalUser,
    userPasswordForm,
    setUserPasswordSaving,
    apiBaseUrl,
    adminFetch,
    readApiJson,
    setAdminToast,
    texts
  });
  const { openDeleteUserModal, closeDeleteUserModal, submitDeleteUser } = DeleteActions({
    canDeleteAdminUser,
    setAdminError,
    texts,
    setDeleteUserModalUser,
    setDeleteUserConfirmText,
    setDeleteUserNotice,
    deleteUserSaving,
    deleteUserModalUser,
    deleteUserConfirmText,
    setDeleteUserSaving,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    fetchAdminData,
    setAdminToast
  });
  const { saveUserManagement } = ManagementActions({
    userDraftById,
    toUserDraft,
    setUserSavingId,
    canManageUserAccount,
    setAdminError,
    texts,
    normalizeUserStatus,
    ASSIGNABLE_ROLES,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    setAdminUsers,
    setUserDraftById,
    setAdminToast,
    normalizeAdminPermissionMessage
  });
  const selectedMemberIdSet = useMemo(
    () => new Set(memberSelectedIds.map((item) => Number(item || 0)).filter(Boolean)),
    [memberSelectedIds]
  );
  const selectedManageableMemberRows = useMemo(() => {
    return memberFilteredRows.filter((item) => selectedMemberIdSet.has(Number(item?.id || 0)) && canManageUserAccount(item));
  }, [canManageUserAccount, memberFilteredRows, selectedMemberIdSet]);
  const currentMemberIds = useMemo(
    () => paginatedMemberRows.items.map((item) => Number(item?.id || 0)).filter(Boolean),
    [paginatedMemberRows.items]
  );
  const allCurrentMembersSelected = currentMemberIds.length > 0 && currentMemberIds.every((id) => selectedMemberIdSet.has(id));

  useEffect(() => {
    const validIds = new Set(memberFilteredRows.map((item) => Number(item?.id || 0)).filter(Boolean));
    setMemberSelectedIds((prev) => prev.filter((id) => validIds.has(Number(id || 0))));
  }, [memberFilteredRows]);

  useEffect(() => {
    setAdminListPages((prev) => ({ ...prev, userManagement: 1 }));
  }, [memberRoleFilter, memberStatusFilter, setAdminListPages]);

  const toggleMemberSelection = useCallback((userId, checked) => {
    const normalizedUserId = Number(userId || 0);
    if (!normalizedUserId) {
      return;
    }

    setMemberSelectedIds((prev) => {
      const next = new Set(prev.map((item) => Number(item || 0)).filter(Boolean));
      if (checked) {
        next.add(normalizedUserId);
      } else {
        next.delete(normalizedUserId);
      }
      return Array.from(next);
    });
  }, []);

  const toggleAllCurrentMembers = useCallback((checked) => {
    setMemberSelectedIds((prev) => {
      const next = new Set(prev.map((item) => Number(item || 0)).filter(Boolean));
      currentMemberIds.forEach((id) => {
        if (checked) {
          next.add(id);
        } else {
          next.delete(id);
        }
      });
      return Array.from(next);
    });
  }, [currentMemberIds]);

  const applyBulkUserStatus = useCallback(async (nextStatus) => {
    if (!selectedManageableMemberRows.length) {
      return;
    }

    for (const userItem of selectedManageableMemberRows) {
      await saveUserManagement(userItem, { status: nextStatus });
    }

    setMemberSelectedIds([]);
  }, [saveUserManagement, selectedManageableMemberRows]);

  const { formatCollectedPrice, updateOrderStatus } = OrderAdminActions({
    adminFetch,
    apiBaseUrl,
    readApiJson,
    setAdminOrders,
    fetchAdminData,
    setAdminError
  });
  const { submitPasswordChange } = PasswordActionsAdmin({
    setPasswordNotice,
    passwordForm,
    setPasswordSaving,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    setPasswordForm,
    texts
  });
  const { getPermissionPreviewMeta } = PermissionPreviewHelpers({
    normalizePermissionKeys,
    permissionRows,
    texts
  });
  const { updateStoreMode } = StoreModeActions({
    storeMode,
    updatingStoreMode,
    adminRole,
    setUpdatingStoreMode,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    setStoreMode,
    setAdminError,
    setAdminToast,
    texts
  });
  const {
    resetProductEditorState,
    openCreateProductEditor,
    startEditProduct,
    closeProductEditor
  } = ProductEditorActions({
    parseMultilineUrls,
    createEmptyProductEditorDraft,
    toProductEditorDraft,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    newProduct,
    productEditorOriginalUploadUrls,
    setAdminError,
    allowedTabs,
    setAdminTab,
    setEditingProductId,
    setProductEditorOriginalUploadUrls,
    setNewProduct
  });
  const { uploadAdminProductAssets, deleteAdminProductAssets, deleteProduct, toggleProductRecommended } = ProductCatalogActions({
    adminFetch,
    apiBaseUrl,
    readApiJson,
    setAdminProducts,
    editingProductId,
    resetProductEditorState,
    fetchAdminData,
    setAdminError
  });
  const { submitProductEditor } = ProductSubmitActions({
    parseMultilineUrls,
    newProduct,
    setAdminError,
    editingProductId,
    apiBaseUrl,
    adminFetch,
    readApiJson,
    fetchAdminData,
    fetchPositionWorkspaceData,
    resetProductEditorState,
    setAdminTab
  });
  const { importCollectedProduct, removeCollectedProduct } = CollectedProductActions({
    setCollectedProductActionId,
    adminFetch,
    apiBaseUrl,
    readApiJson,
    fetchCollectedProducts,
    fetchAdminProducts,
    fetchPositionWorkspaceData,
    setAdminToast,
    locale,
    setAdminError
  });

  const applyStorefrontThemeTemplate = useCallback(
    async (templateValue) => {
      const nextTemplate = String(templateValue || "").trim() || "style-1";
      if (!canEditStorefront || applyingStorefrontTheme) {
        if (!canEditStorefront) {
          reportStorefrontWriteDenied();
        }
        return;
      }

      setApplyingStorefrontTheme(true);
      try {
        const response = await adminFetch(`${apiBaseUrl}/admin/store-mode`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            storeSettings: {
              themeTemplate: nextTemplate
            }
          })
        });

        const data = await readApiJson(response);
        if (!response.ok) {
          throw new Error(data.message || "Template apply failed");
        }

        if (data?.storeSettings && typeof data.storeSettings === "object") {
          setStoreSettingsDraft(toStoreSettingsDraft(data.storeSettings));
        } else {
          const normalizedTemplate = String(data?.storeSettings?.themeTemplate || "style-1").trim() || "style-1";
          setStoreSettingsDraft((prev) => ({
            ...prev,
            themeTemplate: normalizedTemplate
          }));
        }

        setAdminError("");
        setAdminToast(locale === "zh" ? "模板已应用" : "Template applied");
        notifyStorefrontConfigUpdated();
      } catch (err) {
        if (err.message !== "Session expired") {
          setAdminError(err.message || "Template apply failed");
        }
      } finally {
        setApplyingStorefrontTheme(false);
      }
    },
    [
      adminFetch,
      apiBaseUrl,
      applyingStorefrontTheme,
      canEditStorefront,
      locale,
      notifyStorefrontConfigUpdated,
      readApiJson,
      reportStorefrontWriteDenied
    ]
  );

  const resetStorefrontThemeTemplate = useCallback(async () => {
    await applyStorefrontThemeTemplate("style-1");
  }, [applyStorefrontThemeTemplate]);

  const saveTemplateStudioSettings = useCallback(async (nextDraft = null) => {
    const draftToSave = nextDraft && typeof nextDraft === "object" ? nextDraft : storeSettingsDraft;
    if (savingStoreSettings || !canEditStorefront) {
      if (!canEditStorefront) {
        reportStorefrontWriteDenied();
      }
      return false;
    }

    setSavingStoreSettings(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/store-mode`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeSettings: {
            ...toStoreSettingsPayload(draftToSave)
          }
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Template studio save failed");
      }

      if (data.storeSettings && typeof data.storeSettings === "object") {
        setStoreSettingsDraft(toStoreSettingsDraft(data.storeSettings));
      }

      setAdminError("");
      setAdminToast(locale === "zh" ? "模板工作台已保存" : "Template studio saved");
      notifyStorefrontConfigUpdated();
      return true;
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Template studio save failed");
      }
      return false;
    } finally {
      setSavingStoreSettings(false);
    }
  }, [
    adminFetch,
    apiBaseUrl,
    canEditStorefront,
    locale,
    notifyStorefrontConfigUpdated,
    readApiJson,
    reportStorefrontWriteDenied,
    savingStoreSettings,
    storeSettingsDraft
  ]);

  const saveAsCustomThemeTemplate = useCallback(async (templateName) => {
    const name = String(templateName || "").trim();
    if (!name) {
      setAdminError(locale === "zh" ? "请输入模板名称" : "Please enter a template name");
      return false;
    }

    const nextTemplate = {
      id: `custom-${Date.now()}`,
      name,
      themeTemplate: String(storeSettingsDraft.themeTemplate || "style-1"),
      themeLayoutConfig: normalizeStoreThemeLayoutDraft(
        storeSettingsDraft.themeLayoutConfig,
        createDefaultStoreSettingsDraft().themeLayoutConfig
      ),
      themePageConfigs: normalizeThemePageConfigsDraft(
        storeSettingsDraft.themePageConfigs,
        createDefaultStoreSettingsDraft().themePageConfigs
      )
    };

    const nextDraft = {
      ...storeSettingsDraft,
      customThemeTemplates: [
        ...(Array.isArray(storeSettingsDraft.customThemeTemplates) ? storeSettingsDraft.customThemeTemplates : []),
        nextTemplate
      ],
      activeCustomThemeTemplateId: nextTemplate.id
    };
    setStoreSettingsDraft(nextDraft);

    return saveTemplateStudioSettings(nextDraft);
  }, [locale, saveTemplateStudioSettings, storeSettingsDraft]);

  const applyCustomThemeTemplate = useCallback(async (templateId) => {
    const id = String(templateId || "").trim();
    if (!id) {
      return;
    }

    const template = (Array.isArray(storeSettingsDraft.customThemeTemplates) ? storeSettingsDraft.customThemeTemplates : [])
      .find((item) => String(item?.id || "").trim() === id);
    if (!template) {
      return;
    }

    const nextDraft = {
      ...storeSettingsDraft,
      activeCustomThemeTemplateId: id,
      themeTemplate: String(template.themeTemplate || template.baseThemeTemplate || storeSettingsDraft.themeTemplate || "style-1"),
      themeLayoutConfig: normalizeStoreThemeLayoutDraft(template.themeLayoutConfig, storeSettingsDraft.themeLayoutConfig),
      themePageConfigs: normalizeThemePageConfigsDraft(template.themePageConfigs, storeSettingsDraft.themePageConfigs)
    };
    setStoreSettingsDraft(nextDraft);

    return saveTemplateStudioSettings(nextDraft);
  }, [saveTemplateStudioSettings, storeSettingsDraft]);

  const clearActiveCustomThemeTemplate = useCallback(async () => {
    const nextDraft = {
      ...storeSettingsDraft,
      activeCustomThemeTemplateId: ""
    };
    setStoreSettingsDraft(nextDraft);
    return saveTemplateStudioSettings(nextDraft);
  }, [saveTemplateStudioSettings, storeSettingsDraft]);

  async function saveStoreBaseSettings(event) {
    event.preventDefault();

    if (savingStoreSettings || !canEditStorefront) {
      if (!canEditStorefront) {
        reportStorefrontWriteDenied();
      }
      return;
    }

    setSavingStoreSettings(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/store-mode`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeSettings: {
            ...toStoreSettingsPayload(storeSettingsDraft)
          }
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Store base settings save failed");
      }

      if (data.storeSettings && typeof data.storeSettings === "object") {
        setStoreSettingsDraft(toStoreSettingsDraft(data.storeSettings));
        setFaqKnowledgeDrafts(
          normalizeFaqKnowledgeItems(data.storeSettings.faqKnowledgeBase)
        );
      }

      setAdminError("");
      setAdminToast(texts.baseSavedToast);
      notifyStorefrontConfigUpdated();
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Store base settings save failed");
      }
    } finally {
      setSavingStoreSettings(false);
    }
  }

  async function saveArticleManagementSettings(options = {}) {
    const { closeAfterSave = false } = options;

    if (savingStoreSettings || !canManageTrustContent) {
      return;
    }

    const sanitizedBlogArticles = normalizeAdminBlogArticles(blogArticleDrafts)
      .map((item) => ({
        title: String(item.title || "").trim().slice(0, 140),
        category: normalizeAdminBlogCategory(item.category),
        summary: String(item.summary || "").trim().slice(0, 320),
        body: String(item.body || item.summary || "").trim().slice(0, 3000),
        cover: String(item.cover || "").trim().slice(0, 600),
        date: normalizeAdminBlogDate(item.date),
        readMoreUrl: String(item.readMoreUrl || "").trim().slice(0, 600)
      }))
      .filter((item) => item.title)
      .slice(0, MAX_BLOG_ARTICLE_ITEMS);

    if (!sanitizedBlogArticles.length) {
      setAdminError(locale === "zh" ? "请至少保留一条博客标题" : "Please keep at least one blog article title");
      return;
    }

    setSavingStoreSettings(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/store-mode`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          storeSettings: {
            blogArticles: sanitizedBlogArticles
          }
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Article settings save failed");
      }

      if (data.storeSettings && typeof data.storeSettings === "object") {
        const normalizedArticles = normalizeAdminBlogArticles(data.storeSettings.blogArticles);
        setBlogArticleDrafts(normalizedArticles);
        if (editingBlogArticleIndex >= 0 && editingBlogArticleIndex < normalizedArticles.length) {
          setEditingBlogArticleSnapshot(getBlogArticleEditableSnapshot(normalizedArticles[editingBlogArticleIndex]));
        }
      }

      setAdminError("");
      setAdminToast(locale === "zh" ? "文章已保存" : "Articles saved");
      if (closeAfterSave) {
        setEditingBlogArticleIndex(-1);
      }
      notifyStorefrontConfigUpdated();
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Article settings save failed");
      }
    } finally {
      setSavingStoreSettings(false);
    }
  }

  function openBlogArticleEditor(index) {
    const normalizedIndex = Number(index);
    if (!Number.isInteger(normalizedIndex) || normalizedIndex < 0 || normalizedIndex >= blogArticleDrafts.length) {
      return;
    }
    setEditingBlogArticleIndex(normalizedIndex);
    setEditingBlogArticleSnapshot(getBlogArticleEditableSnapshot(blogArticleDrafts[normalizedIndex]));
    setBlogCoverPreviewFailed(false);
  }

  function hasEditingBlogArticleUnsavedChanges() {
    if (editingBlogArticleIndex < 0 || editingBlogArticleIndex >= blogArticleDrafts.length) {
      return false;
    }

    if (!editingBlogArticleSnapshot) {
      return false;
    }

    const currentSnapshot = getBlogArticleEditableSnapshot(blogArticleDrafts[editingBlogArticleIndex]);
    return JSON.stringify(currentSnapshot) !== JSON.stringify(editingBlogArticleSnapshot);
  }

  function confirmDiscardEditingChanges() {
    if (!hasEditingBlogArticleUnsavedChanges()) {
      return true;
    }

    return window.confirm(locale === "zh" ? "当前文章有未保存修改，确定继续吗？" : "You have unsaved changes for this article. Continue anyway?");
  }

  function closeBlogArticleEditor() {
    if (!confirmDiscardEditingChanges()) {
      return;
    }
    setEditingBlogArticleIndex(-1);
    setEditingBlogArticleSnapshot(null);
  }

  function openPreviousBlogArticleEditor() {
    if (!confirmDiscardEditingChanges()) {
      return;
    }

    setEditingBlogArticleIndex((prev) => {
      const next = prev <= 0 ? prev : prev - 1;
      if (next >= 0 && next < blogArticleDrafts.length) {
        setEditingBlogArticleSnapshot(getBlogArticleEditableSnapshot(blogArticleDrafts[next]));
        setBlogCoverPreviewFailed(false);
      }
      return next;
    });
  }

  function openNextBlogArticleEditor() {
    if (!confirmDiscardEditingChanges()) {
      return;
    }

    setEditingBlogArticleIndex((prev) => {
      if (prev < 0) {
        return prev;
      }

      const lastIndex = Math.max(0, blogArticleDrafts.length - 1);
      const next = prev >= lastIndex ? prev : prev + 1;
      if (next >= 0 && next < blogArticleDrafts.length) {
        setEditingBlogArticleSnapshot(getBlogArticleEditableSnapshot(blogArticleDrafts[next]));
        setBlogCoverPreviewFailed(false);
      }
      return next;
    });
  }

  function updateBlogArticleDraft(index, field, value) {
    const editableFields = new Set(["title", "category", "summary", "body", "cover", "date", "readMoreUrl"]);
    if (!editableFields.has(field)) {
      return;
    }

    setBlogArticleDrafts((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        if (field === "category") {
          return {
            ...item,
            category: normalizeAdminBlogCategory(value)
          };
        }

        if (field === "date") {
          return {
            ...item,
            date: normalizeAdminBlogDate(value)
          };
        }

        const limits = {
          title: 140,
          summary: 320,
          body: 3000,
          cover: 600,
          readMoreUrl: 600
        };

        return {
          ...item,
          [field]: String(value || "").slice(0, limits[field] || 3000)
        };
      })
    );
  }

  function addBlogArticleDraft() {
    setBlogArticleDrafts((prev) => {
      if (prev.length >= MAX_BLOG_ARTICLE_ITEMS) {
        return prev;
      }

      return [
        ...prev,
        {
          id: `blog-${Date.now()}-${prev.length + 1}`,
          title: "",
          category: "Sourcing",
          summary: "",
          body: "",
          cover: "",
          date: "",
          readMoreUrl: ""
        }
      ];
    });
  }

  function removeBlogArticleDraft(index) {
    setBlogArticleDrafts((prev) => {
      if (prev.length <= 1) {
        return prev;
      }

      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  }

  function updateFaqDraftField(index, field, value) {
    if (!["question", "answer"].includes(field)) {
      return;
    }

    setFaqKnowledgeDrafts((prev) =>
      prev.map((item, itemIndex) => {
        if (itemIndex !== index) {
          return item;
        }

        const limit = field === "question" ? MAX_FAQ_QUESTION_LENGTH : MAX_FAQ_ANSWER_LENGTH;
        return {
          ...item,
          [field]: String(value || "").slice(0, limit)
        };
      })
    );
  }

  function addFaqDraft() {
    setFaqKnowledgeDrafts((prev) => {
      if (prev.length >= MAX_FAQ_KNOWLEDGE_ITEMS) {
        return prev;
      }

      return [
        ...prev,
        {
          id: String(Date.now()),
          question: "",
          answer: ""
        }
      ];
    });
  }

  function removeFaqDraft(index) {
    setFaqKnowledgeDrafts((prev) => {
      if (prev.length <= 1) {
        return prev;
      }
      return prev.filter((_, itemIndex) => itemIndex !== index);
    });
  }

  function updateCompanyImageSectionEnabled(sectionKey, enabled) {
    setCompanyImageSectionsDraft((prev) =>
      prev.map((section) => (
        section.key === sectionKey
          ? {
              ...section,
              enabled: Boolean(enabled)
            }
          : section
      ))
    );
  }

  function updateCompanyImageUrl(sectionKey, imageIndex, nextUrl) {
    setCompanyImageSectionsDraft((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) {
          return section;
        }

        const nextImages = Array.isArray(section.images) ? [...section.images] : [];
        if (imageIndex < 0 || imageIndex >= nextImages.length) {
          return section;
        }

        nextImages[imageIndex] = String(nextUrl || "");
        return {
          ...section,
          images: nextImages
        };
      })
    );
  }

  function addCompanyImageUrl(sectionKey) {
    if (!canEditStorefront) {
      setAdminError(locale === "zh" ? "当前账号没有店铺编辑权限" : "Current account does not have storefront write permission");
      return;
    }

    setCompanyImageSectionsDraft((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) {
          return section;
        }

        const currentTotal = prev.reduce(
          (sum, item) => sum + (Array.isArray(item.images) ? item.images.length : 0),
          0
        );
        if (currentTotal >= MAX_COMPANY_IMAGE_ITEMS) {
          setAdminError(locale === "zh" ? `公司图片最多 ${MAX_COMPANY_IMAGE_ITEMS} 张` : `At most ${MAX_COMPANY_IMAGE_ITEMS} company images are allowed`);
          return section;
        }

        const nextImages = Array.isArray(section.images) ? [...section.images] : [];
        if (nextImages.length >= MAX_COMPANY_IMAGE_ITEMS) {
          return section;
        }

        return {
          ...section,
          images: [...nextImages, ""]
        };
      })
    );
  }

  function removeCompanyImageUrl(sectionKey, imageIndex) {
    setCompanyImageSectionsDraft((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) {
          return section;
        }

        const nextImages = Array.isArray(section.images) ? [...section.images] : [];
        if (imageIndex < 0 || imageIndex >= nextImages.length) {
          return section;
        }

        nextImages.splice(imageIndex, 1);
        return {
          ...section,
          images: nextImages
        };
      })
    );
  }

  function moveCompanyImageUrl(sectionKey, imageIndex, direction) {
    const delta = direction === "up" ? -1 : 1;
    setCompanyImageSectionsDraft((prev) =>
      prev.map((section) => {
        if (section.key !== sectionKey) {
          return section;
        }

        const nextImages = Array.isArray(section.images) ? [...section.images] : [];
        const targetIndex = imageIndex + delta;
        if (
          imageIndex < 0 ||
          imageIndex >= nextImages.length ||
          targetIndex < 0 ||
          targetIndex >= nextImages.length
        ) {
          return section;
        }

        const temp = nextImages[targetIndex];
        nextImages[targetIndex] = nextImages[imageIndex];
        nextImages[imageIndex] = temp;

        return {
          ...section,
          images: nextImages
        };
      })
    );
  }

  function clearCompanyImageSection(sectionKey) {
    setCompanyImageSectionsDraft((prev) =>
      prev.map((section) => (
        section.key === sectionKey
          ? {
              ...section,
              images: []
            }
          : section
      ))
    );
  }

  function updateCompanyImageLinkDraft(sectionKey, value) {
    setCompanyImageLinkDrafts((prev) => ({
      ...prev,
      [sectionKey]: String(value || "")
    }));
  }

  function buildReorderedCompanyImageSections(sourceSections, sectionKey, fromIndex, toIndex) {
    const sections = Array.isArray(sourceSections) ? sourceSections : [];
    let changed = false;

    const nextSections = sections.map((section) => {
      if (section.key !== sectionKey) {
        return section;
      }

      const nextImages = Array.isArray(section.images) ? [...section.images] : [];
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= nextImages.length ||
        toIndex >= nextImages.length ||
        fromIndex === toIndex
      ) {
        return section;
      }

      const [moved] = nextImages.splice(fromIndex, 1);
      nextImages.splice(toIndex, 0, moved);
      changed = true;

      return {
        ...section,
        images: nextImages
      };
    });

    return {
      changed,
      sections: nextSections
    };
  }

  function buildRemovedCompanyImageSections(sourceSections, sectionKey, imageIndex) {
    const sections = Array.isArray(sourceSections) ? sourceSections : [];
    let changed = false;

    const nextSections = sections.map((section) => {
      if (section.key !== sectionKey) {
        return section;
      }

      const nextImages = Array.isArray(section.images) ? [...section.images] : [];
      if (imageIndex < 0 || imageIndex >= nextImages.length) {
        return section;
      }

      nextImages.splice(imageIndex, 1);
      changed = true;
      return {
        ...section,
        images: nextImages
      };
    });

    return {
      changed,
      sections: nextSections
    };
  }

  function buildClearedCompanyImageSections(sourceSections, sectionKey) {
    const sections = Array.isArray(sourceSections) ? sourceSections : [];
    let changed = false;

    const nextSections = sections.map((section) => {
      if (section.key !== sectionKey) {
        return section;
      }

      const currentImages = Array.isArray(section.images) ? section.images : [];
      if (!currentImages.length) {
        return section;
      }

      changed = true;
      return {
        ...section,
        images: []
      };
    });

    return {
      changed,
      sections: nextSections
    };
  }

  function addCustomCompanyImageSection() {
    const rawName = String(customCompanySectionName || "").trim();
    if (!rawName) {
      setAdminError(locale === "zh" ? "请先输入自定义图集名称" : "Please enter a custom section name first");
      return;
    }

    const nextLabelZh = rawName.slice(0, 40);
    const nextLabelEn = rawName.slice(0, 60);

    setCompanyImageSectionsDraft((prev) => {
      const existingByName = prev.some((item) => {
        const zh = String(item.labelZh || item.label || "").trim();
        const en = String(item.labelEn || item.label || "").trim();
        return zh === nextLabelZh || en.toLowerCase() === nextLabelEn.toLowerCase();
      });
      if (existingByName) {
        setAdminError(locale === "zh" ? "该图集名称已存在" : "A section with the same name already exists");
        return prev;
      }

      let nextKey = createCompanyImageSectionKey(nextLabelEn);
      let suffix = 1;
      const keySet = new Set(prev.map((item) => String(item.key || "").trim()));
      while (keySet.has(nextKey)) {
        suffix += 1;
        nextKey = `${createCompanyImageSectionKey(nextLabelEn)}-${suffix}`;
      }

      return [
        ...prev,
        {
          key: nextKey,
          labelZh: nextLabelZh,
          labelEn: nextLabelEn,
          enabled: true,
          images: []
        }
      ];
    });

    setCustomCompanySectionName("");
    setShowCustomCompanySectionInput(false);
  }

  function buildSanitizedCompanyImageSections(sourceSections = companyImageSectionsDraft) {
    return normalizeCompanyImageSections(sourceSections).map((section) => ({
      key: section.key,
      label: locale === "zh"
        ? String(section.labelZh || section.labelEn || section.key)
        : String(section.labelEn || section.labelZh || section.key),
      enabled: section.enabled !== false,
      images: (Array.isArray(section.images) ? section.images : [])
        .map(normalizeCompanyImageValue)
        .filter(Boolean)
        .slice(0, MAX_COMPANY_IMAGE_ITEMS)
    }));
  }

  function toCompanyImagePreviewUrl(value) {
    const normalized = normalizeCompanyImageValue(value);
    if (!normalized || normalized === "[object Object]") {
      return "";
    }

    if (/^data:image\//i.test(normalized)) {
      return normalized;
    }

    const base = String(apiBaseUrl || "").replace(/\/$/, "");
    const configuredBackendOrigin = String(process.env.REACT_APP_BACKEND_ORIGIN || "").trim().replace(/\/$/, "");
    const configuredBackendPort = String(process.env.REACT_APP_BACKEND_PORT || "").trim();
    const isLocalHostName = (hostName) => {
      const host = String(hostName || "").toLowerCase();
      return host === "localhost" || host === "127.0.0.1" || host === "::1";
    };
    const isCompanyImagePath = (pathname) => {
      const path = String(pathname || "");
      return (
        path.startsWith("/uploads/company-images/") ||
        path.startsWith("/api/uploads/company-images/") ||
        path.startsWith("/assets/company-images/") ||
        path.startsWith("/api/assets/company-images/")
      );
    };
    const resolveBackendOrigin = () => {
      if (configuredBackendOrigin) {
        return configuredBackendOrigin;
      }

      try {
        if (/^https?:\/\//i.test(base)) {
          const parsedBase = new URL(base);
          return parsedBase.origin;
        }
      } catch {
        // Ignore malformed base value and fallback to window location.
      }

      if (typeof window !== "undefined") {
        const protocol = String(window.location.protocol || "http:");
        const hostname = String(window.location.hostname || "localhost");
        if (configuredBackendPort && isLocalHostName(hostname)) {
          return `${protocol}//${hostname}:${configuredBackendPort}`;
        }
        return String(window.location.origin || "");
      }

      return "";
    };
    const backendOrigin = resolveBackendOrigin();
    const toBackendAbsolutePath = (pathname) => {
      const nextPath = String(pathname || "").trim();
      if (!nextPath) {
        return "";
      }
      if (!backendOrigin) {
        return nextPath;
      }
      return `${backendOrigin}${nextPath.startsWith("/") ? nextPath : `/${nextPath}`}`;
    };
    const joinWithApiBase = (pathname) => {
      const nextPath = String(pathname || "").trim();
      if (!nextPath) {
        return "";
      }

      if (!base) {
        return nextPath;
      }

      const shouldStripApiPrefix = base.endsWith("/api") && nextPath.startsWith("/api/");
      const normalizedPath = shouldStripApiPrefix ? nextPath.slice(4) : nextPath;
      return `${base}${normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`}`;
    };

    if (/^https?:\/\//i.test(normalized)) {
      try {
        const parsed = new URL(normalized);
        const host = String(parsed.hostname || "").toLowerCase();
        const pathname = String(parsed.pathname || "");
        if (isLocalHostName(host) && isCompanyImagePath(pathname)) {
          return toBackendAbsolutePath(pathname);
        }
      } catch {
        return normalized;
      }

      return normalized;
    }

    if (normalized.startsWith("/")) {
      if (isCompanyImagePath(normalized)) {
        return toBackendAbsolutePath(normalized);
      }
      return joinWithApiBase(normalized);
    }

    return normalizeExternalLink(normalized);
  }

  async function copyCompanyImageLink(link) {
    const normalized = String(link || "").trim();
    if (!normalized) {
      return;
    }

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalized);
      } else {
        const helper = document.createElement("textarea");
        helper.value = normalized;
        helper.setAttribute("readonly", "readonly");
        helper.style.position = "fixed";
        helper.style.opacity = "0";
        document.body.appendChild(helper);
        helper.focus();
        helper.select();
        document.execCommand("copy");
        document.body.removeChild(helper);
      }

      setAdminToast(locale === "zh" ? "图片链接已复制" : "Image link copied");
      setAdminError("");
    } catch {
      setAdminError(locale === "zh" ? "复制链接失败" : "Failed to copy image link");
    }
  }

  function openCompanyImagePreview(link, altText) {
    const normalized = String(link || "").trim();
    if (!normalized) {
      return;
    }

    setCompanyImagePreviewModal({
      src: normalized,
      alt: String(altText || "").trim() || "company-image"
    });
  }

  function closeCompanyImagePreview() {
    setCompanyImagePreviewModal(null);
  }

  function reportStorefrontWriteDenied() {
    const message = locale === "zh" ? "当前账号没有店铺编辑权限" : "Current account does not have storefront write permission";
    setAdminError(message);
    setAdminToast(message);
  }

  async function uploadCompanyImages(sectionKey, event) {
    const files = Array.from(event?.target?.files || []);
    event.target.value = "";
    if (!files.length) {
      return;
    }

    if (!canEditStorefront) {
      reportStorefrontWriteDenied();
      return;
    }

    if (uploadingCompanyImageSectionKey) {
      return;
    }

    const currentTotal = companyImageSectionsDraft.reduce(
      (sum, section) => sum + (Array.isArray(section.images) ? section.images.length : 0),
      0
    );
    if (currentTotal >= MAX_COMPANY_IMAGE_ITEMS) {
      const message = locale === "zh" ? `公司图片最多 ${MAX_COMPANY_IMAGE_ITEMS} 张` : `At most ${MAX_COMPANY_IMAGE_ITEMS} company images are allowed`;
      setAdminError(message);
      setAdminToast(message);
      return;
    }

    const availableSlots = Math.max(0, MAX_COMPANY_IMAGE_ITEMS - currentTotal);
    const selectedFiles = files.slice(0, availableSlots);

    const formData = new FormData();
    formData.append("sectionKey", sectionKey);
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    setUploadingCompanyImageSectionKey(sectionKey);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/uploads/company-images`, {
        method: "POST",
        body: formData
      });
      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Upload company images failed");
      }

      const uploadedPaths = Array.isArray(data?.files)
        ? data.files
            .map((item) => String(item?.relativePath || item?.url || "").trim())
            .filter(Boolean)
        : [];

      if (uploadedPaths.length) {
        setCompanyImageSectionsDraft((prev) =>
          prev.map((section) => {
            if (section.key !== sectionKey) {
              return section;
            }

            const existing = Array.isArray(section.images)
              ? section.images.map((item) => String(item || "").trim()).filter(Boolean)
              : [];

            const merged = [...existing, ...uploadedPaths].slice(0, MAX_COMPANY_IMAGE_ITEMS);
            return {
              ...section,
              images: merged
            };
          })
        );
      }

      // Prefer backend's latest snapshot; fallback to an immediate refetch for multi-user concurrency.
      let latestSections = Array.isArray(data?.companyImageSections) ? data.companyImageSections : [];
      const latestResponse = await adminFetch(`${apiBaseUrl}/admin/company-images`);
      const latestData = await readApiJson(latestResponse);
      if (!latestResponse.ok) {
        throw new Error(latestData.message || "Company image config load failed");
      }
      latestSections = Array.isArray(latestData?.companyImageSections)
        ? latestData.companyImageSections
        : latestSections;

      if (latestSections.length) {
        setCompanyImageSectionsDraft((prev) =>
          normalizeCompanyImageSections(latestSections, prev)
        );
      }
      setAdminError("");
      setAdminToast(
        locale === "zh"
          ? `已上传 ${uploadedPaths.length || selectedFiles.length} 张图片`
          : `Uploaded ${uploadedPaths.length || selectedFiles.length} image(s)`
      );
    } catch (err) {
      if (err.message !== "Session expired") {
        const message = err.message || "Upload company images failed";
        setAdminError(message);
        setAdminToast(message);
      }
    } finally {
      setUploadingCompanyImageSectionKey("");
    }
  }

  async function saveCompanyImages(event) {
    event?.preventDefault?.();

    if (!canEditStorefront) {
      reportStorefrontWriteDenied();
      return;
    }

    await persistCompanyImageSections(companyImageSectionsDraft);
  }

  async function persistCompanyImageSections(nextSections, options = {}) {
    const sections = buildSanitizedCompanyImageSections(nextSections);
    const imageCount = sections.reduce((sum, section) => sum + section.images.length, 0);
    if (imageCount > MAX_COMPANY_IMAGE_ITEMS) {
      const message = locale === "zh" ? `公司图片最多 ${MAX_COMPANY_IMAGE_ITEMS} 张` : `At most ${MAX_COMPANY_IMAGE_ITEMS} company images are allowed`;
      setAdminError(message);
      setAdminToast(message);
      return false;
    }

    if (savingCompanyImages) {
      return false;
    }

    setSavingCompanyImages(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/company-images`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          companyImageSections: sections,
          companyIntroduction: String(companyIntroductionDraft || "").slice(0, 8000)
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Company image save failed");
      }

      setCompanyImageSectionsDraft(
        normalizeCompanyImageSections(data?.companyImageSections, sections)
      );
      setCompanyIntroductionDraft(String(data?.companyIntroduction ?? companyIntroductionDraft ?? ""));
      setAdminError("");
      if (options.silent !== true) {
        setAdminToast(options.successMessage || texts.companyImagesSavedToast || texts.save);
      }
      notifyStorefrontConfigUpdated();
      return true;
    } catch (err) {
      if (err.message !== "Session expired") {
        const message = err.message || "Company image save failed";
        setAdminError(message);
        setAdminToast(message);
      }
      return false;
    } finally {
      setSavingCompanyImages(false);
    }
  }

  function handleCompanyImageDragStart(sectionKey, imageIndex) {
    if (!canEditStorefront) {
      return;
    }

    setCompanyImageDragging({ sectionKey, imageIndex });
  }

  function handleCompanyImageDragEnd() {
    setCompanyImageDragging(null);
  }

  async function handleCompanyImageDrop(sectionKey, targetIndex) {
    const dragging = companyImageDragging;
    setCompanyImageDragging(null);

    if (!canEditStorefront || !dragging) {
      return;
    }

    if (dragging.sectionKey !== sectionKey) {
      return;
    }

    const reordered = buildReorderedCompanyImageSections(
      companyImageSectionsDraft,
      sectionKey,
      dragging.imageIndex,
      targetIndex
    );

    if (!reordered.changed) {
      return;
    }

    setCompanyImageSectionsDraft(reordered.sections);
    await persistCompanyImageSections(reordered.sections, {
      successMessage: locale === "zh" ? "图片顺序已同步保存" : "Image order synced"
    });
  }

  async function handleRemoveCompanyImage(sectionKey, imageIndex) {
    const nextState = buildRemovedCompanyImageSections(companyImageSectionsDraft, sectionKey, imageIndex);
    if (!nextState.changed) {
      return;
    }

    setCompanyImageSectionsDraft(nextState.sections);
    await persistCompanyImageSections(nextState.sections, {
      successMessage: locale === "zh" ? "图片已删除并同步" : "Image removed and synced"
    });
  }

  async function handleClearCompanyImageSection(sectionKey) {
    const nextState = buildClearedCompanyImageSections(companyImageSectionsDraft, sectionKey);
    if (!nextState.changed) {
      return;
    }

    setCompanyImageSectionsDraft(nextState.sections);
    await persistCompanyImageSections(nextState.sections, {
      successMessage: locale === "zh" ? "图集已清空并同步" : "Section cleared and synced"
    });
  }

  async function handleAddCompanyImageLink(sectionKey) {
    if (!canEditStorefront) {
      reportStorefrontWriteDenied();
      return;
    }

    const rawValue = String(companyImageLinkDrafts?.[sectionKey] || "").trim();
    if (!rawValue) {
      const message = locale === "zh" ? "请输入图片网址" : "Enter an image URL";
      setAdminError(message);
      setAdminToast(message);
      return;
    }

    const normalizedLink = normalizeExternalLink(rawValue);
    try {
      const parsed = new URL(normalizedLink);
      if (!/^https?:$/i.test(parsed.protocol)) {
        throw new Error("invalid_protocol");
      }
    } catch {
      const message = locale === "zh" ? "请输入有效的图片网址" : "Enter a valid image URL";
      setAdminError(message);
      setAdminToast(message);
      return;
    }

    const currentTotal = companyImageSectionsDraft.reduce(
      (sum, section) => sum + (Array.isArray(section.images) ? section.images.length : 0),
      0
    );
    if (currentTotal >= MAX_COMPANY_IMAGE_ITEMS) {
      const message = locale === "zh" ? `公司图片最多 ${MAX_COMPANY_IMAGE_ITEMS} 张` : `At most ${MAX_COMPANY_IMAGE_ITEMS} company images are allowed`;
      setAdminError(message);
      setAdminToast(message);
      return;
    }

    let changed = false;
    const nextSections = companyImageSectionsDraft.map((section) => {
      if (section.key !== sectionKey) {
        return section;
      }

      const existing = Array.isArray(section.images)
        ? section.images.map((item) => String(item || "").trim()).filter(Boolean)
        : [];

      changed = true;
      return {
        ...section,
        images: [...existing, normalizedLink].slice(0, MAX_COMPANY_IMAGE_ITEMS)
      };
    });

    if (!changed) {
      return;
    }

    setCompanyImageSectionsDraft(nextSections);
    const saved = await persistCompanyImageSections(nextSections, {
      successMessage: locale === "zh" ? "网址图片已添加" : "Image link added"
    });

    if (saved) {
      setCompanyImageLinkDrafts((prev) => ({
        ...prev,
        [sectionKey]: ""
      }));
    }
  }

  async function saveFaqKnowledge() {
    if (savingFaqKnowledge || adminRole !== "admin") {
      return;
    }

    const payloadItems = faqKnowledgeDrafts
      .map((item) => ({
        question: String(item.question || "").trim().slice(0, MAX_FAQ_QUESTION_LENGTH),
        answer: String(item.answer || "").trim().slice(0, MAX_FAQ_ANSWER_LENGTH)
      }))
      .filter((item) => item.question && item.answer)
      .slice(0, MAX_FAQ_KNOWLEDGE_ITEMS);

    if (!payloadItems.length) {
      setAdminError(locale === "zh" ? "请至少保留一条完整FAQ（问题和答案都不能为空）。" : "Please keep at least one complete FAQ item.");
      return;
    }

    setSavingFaqKnowledge(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/faq-knowledge`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          faqKnowledge: payloadItems
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "FAQ knowledge save failed");
      }

      setFaqKnowledgeDrafts(
        normalizeFaqKnowledgeItems(data?.faqKnowledge, payloadItems)
      );
      setAdminError("");
      setAdminToast(texts.faqKnowledgeSavedToast);
      notifyStorefrontConfigUpdated();
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "FAQ knowledge save failed");
      }
    } finally {
      setSavingFaqKnowledge(false);
    }
  }

  async function saveSystemConfig(event) {
    event.preventDefault();
    if (savingSystemConfig) {
      return;
    }

    setSavingSystemConfig(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/system-config`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mysql: {
            host: systemConfigDraft.mysql.host,
            port: Number(systemConfigDraft.mysql.port),
            database: systemConfigDraft.mysql.database,
            user: systemConfigDraft.mysql.user,
            password: systemConfigDraft.mysql.password,
            poolSize: Number(systemConfigDraft.mysql.poolSize),
            connectTimeoutMs: Number(systemConfigDraft.mysql.connectTimeoutMs)
          },
          Testing: {
            DisableFrontendLoginCaptcha: Boolean(systemConfigDraft.testing?.disableFrontendLoginCaptcha)
          },
          GoogleAuth: {
            Enabled: Boolean(systemConfigDraft.googleAuth?.enabled),
            ClientId: String(systemConfigDraft.googleAuth?.clientId || "").trim(),
            AllowedHostedDomains: String(systemConfigDraft.googleAuth?.allowedHostedDomains || "").trim(),
            ProxyUrl: String(systemConfigDraft.googleAuth?.proxyUrl || "").trim(),
            AllowInsecureOfflineVerify: Boolean(systemConfigDraft.googleAuth?.allowInsecureOfflineVerify)
          },
          Site: {
            websiteEnabled: Boolean(systemConfigDraft.site?.websiteEnabled),
            allowUserRegistration: Boolean(systemConfigDraft.site?.allowUserRegistration),
            minimumLoginRole: String(systemConfigDraft.site?.minimumLoginRole || "guest")
          },
          EmailTemplates: {
            Branding: {
              companyName: systemConfigDraft.emailTemplates.branding.companyName,
              accentColor: systemConfigDraft.emailTemplates.branding.accentColor,
              backgroundColor: systemConfigDraft.emailTemplates.branding.backgroundColor,
              logoUrl: systemConfigDraft.emailTemplates.branding.logoUrl,
              loginUrl: systemConfigDraft.emailTemplates.branding.loginUrl,
              supportEmail: systemConfigDraft.emailTemplates.branding.supportEmail,
              supportLine: systemConfigDraft.emailTemplates.branding.supportLine,
              footerNote: systemConfigDraft.emailTemplates.branding.footerNote
            },
            Registration: {
              subject: systemConfigDraft.emailTemplates.registration.subject,
              text: systemConfigDraft.emailTemplates.registration.text,
              html: systemConfigDraft.emailTemplates.registration.html
            },
            PasswordReset: {
              subject: systemConfigDraft.emailTemplates.passwordReset.subject,
              text: systemConfigDraft.emailTemplates.passwordReset.text,
              html: systemConfigDraft.emailTemplates.passwordReset.html
            },
            LoginNotice: {
              enabled: Boolean(systemConfigDraft.emailTemplates.loginNotice.enabled),
              subject: systemConfigDraft.emailTemplates.loginNotice.subject,
              text: systemConfigDraft.emailTemplates.loginNotice.text,
              html: systemConfigDraft.emailTemplates.loginNotice.html
            }
          }
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "System config save failed");
      }

      if (data?.config?.mysql) {
        setSystemConfigDraft({
          mysql: {
            host: String(data.config.mysql.host || ""),
            port: Number(data.config.mysql.port || 3306),
            database: String(data.config.mysql.database || ""),
            user: String(data.config.mysql.user || ""),
            password: String(data.config.mysql.password || ""),
            poolSize: Number(data.config.mysql.poolSize || 10),
            connectTimeoutMs: Number(data.config.mysql.connectTimeoutMs || 8000)
          },
          testing: {
            disableFrontendLoginCaptcha: Boolean(data.config.Testing?.DisableFrontendLoginCaptcha)
          },
          googleAuth: {
            enabled: Boolean(data.config.GoogleAuth?.Enabled),
            clientId: String(data.config.GoogleAuth?.ClientId || ""),
            allowedHostedDomains: String(data.config.GoogleAuth?.AllowedHostedDomains || ""),
            proxyUrl: String(data.config.GoogleAuth?.ProxyUrl || ""),
            allowInsecureOfflineVerify: Boolean(data.config.GoogleAuth?.AllowInsecureOfflineVerify)
          },
          site: {
            websiteEnabled: Boolean(data.config.Site?.websiteEnabled ?? true),
            allowUserRegistration: Boolean(data.config.Site?.allowUserRegistration ?? true),
            minimumLoginRole: String(data.config.Site?.minimumLoginRole || "guest")
          },
          emailTemplates: {
            branding: {
              companyName: String(data.config.EmailTemplates?.Branding?.companyName || ""),
              accentColor: String(data.config.EmailTemplates?.Branding?.accentColor || "#7a5446"),
              backgroundColor: String(data.config.EmailTemplates?.Branding?.backgroundColor || "#f6efe8"),
              logoUrl: String(data.config.EmailTemplates?.Branding?.logoUrl || ""),
              loginUrl: String(data.config.EmailTemplates?.Branding?.loginUrl || ""),
              supportEmail: String(data.config.EmailTemplates?.Branding?.supportEmail || ""),
              supportLine: String(data.config.EmailTemplates?.Branding?.supportLine || ""),
              footerNote: String(data.config.EmailTemplates?.Branding?.footerNote || "")
            },
            registration: {
              subject: String(data.config.EmailTemplates?.Registration?.subject || ""),
              text: String(data.config.EmailTemplates?.Registration?.text || ""),
              html: String(data.config.EmailTemplates?.Registration?.html || "")
            },
            passwordReset: {
              subject: String(data.config.EmailTemplates?.PasswordReset?.subject || ""),
              text: String(data.config.EmailTemplates?.PasswordReset?.text || ""),
              html: String(data.config.EmailTemplates?.PasswordReset?.html || "")
            },
            loginNotice: {
              enabled: Boolean(data.config.EmailTemplates?.LoginNotice?.enabled),
              subject: String(data.config.EmailTemplates?.LoginNotice?.subject || ""),
              text: String(data.config.EmailTemplates?.LoginNotice?.text || ""),
              html: String(data.config.EmailTemplates?.LoginNotice?.html || "")
            }
          }
        });
        setSystemConfigPath(String(data.configPath || ""));
      }

      setAdminError("");
      setAdminToast(texts.systemConfigSavedToast);
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "System config save failed");
      }
    } finally {
      setSavingSystemConfig(false);
    }
  }

  async function saveNavigationMenu() {

    if (savingNavMenu || !canEditStorefront) {
      if (!canEditStorefront) {
        reportStorefrontWriteDenied();
      }
      return false;
    }

    const sanitized = sanitizeNavDraftItems(navMenuDraft);

    if (!sanitized.length) {
      setAdminError("At least one submenu is required");
      return false;
    }

    const countNodes = (items) =>
      items.reduce(
        (sum, item) => sum + 1 + (Array.isArray(item.children) ? countNodes(item.children) : 0),
        0
      );
    const hasInvalid = countNodes(sanitized) < countNodes(normalizeNavDraftItems(navMenuDraft));
    if (hasInvalid) {
      setAdminError("Each submenu requires name and link");
      return false;
    }

    setSavingNavMenu(true);
    try {
      const productsItem = sanitized.find((item) => item.key === "products") || null;
      if (productsItem) {
        const productGroupResponse = await adminFetch(`${apiBaseUrl}/admin/product-groups`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            items: Array.isArray(productsItem.children) ? productsItem.children : []
          })
        });

        const productGroupData = await readApiJson(productGroupResponse);
        if (!productGroupResponse.ok) {
          throw new Error(productGroupData.message || "Product group save failed");
        }
      }

      const response = await adminFetch(`${apiBaseUrl}/admin/store-mode`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          navMenu: sanitized
        })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || "Navigation save failed");
      }

      if (Array.isArray(data.navMenu)) {
        setNavMenuDraft(
          normalizeNavDraftItems(
            data.navMenu.filter((item) => item.key !== "home")
          )
        );
      }
      setAdminError("");
      setAdminToast(texts.navSavedToast);
      return true;
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || "Navigation save failed");
      }
      return false;
    } finally {
      setSavingNavMenu(false);
    }
  }

  function updateNavMenuByPath(source, path, updater) {
    if (!Array.isArray(source) || !path.length) {
      return source;
    }

    const [currentIndex, ...restPath] = path;
    return source.map((item, index) => {
      if (index !== currentIndex) {
        return item;
      }

      if (!restPath.length) {
        return updater(item);
      }

      return {
        ...item,
        children: updateNavMenuByPath(Array.isArray(item.children) ? item.children : [], restPath, updater)
      };
    });
  }

  function removeNavMenuByPath(source, path) {
    if (!Array.isArray(source) || !path.length) {
      return source;
    }

    const [currentIndex, ...restPath] = path;
    if (!restPath.length) {
      return source.filter((_, index) => index !== currentIndex);
    }

    return source.map((item, index) => {
      if (index !== currentIndex) {
        return item;
      }

      return {
        ...item,
        children: removeNavMenuByPath(Array.isArray(item.children) ? item.children : [], restPath)
      };
    });
  }

  function getNavMenuDepth(path) {
    return path.length + 1;
  }

  function getNavMenuItemByPath(source, path) {
    if (!Array.isArray(source) || !Array.isArray(path) || !path.length) {
      return null;
    }

    let current = null;
    let cursor = source;

    for (const index of path) {
      if (!Array.isArray(cursor) || index < 0 || index >= cursor.length) {
        return null;
      }
      current = cursor[index];
      cursor = Array.isArray(current?.children) ? current.children : [];
    }

    return current;
  }

  function pathToKey(path) {
    return Array.isArray(path) ? path.join("-") : "";
  }

  function isProductsNavBranch(path = []) {
    if (!Array.isArray(path) || !path.length) {
      return false;
    }

    const rootItem = getNavMenuItemByPath(navMenuDraft, [path[0]]);
    return String(rootItem?.key || "").trim() === "products";
  }

  function addNavMenuItem(parentPath = null) {
    const isProductsBranch = Array.isArray(parentPath) && parentPath.length ? isProductsNavBranch(parentPath) : false;
    const parentItem = isProductsBranch && Array.isArray(parentPath) && parentPath.length
      ? getNavMenuItemByPath(navMenuDraft, parentPath)
      : null;
    const productsRoot = isProductsBranch && Array.isArray(parentPath) && parentPath.length
      ? getNavMenuItemByPath(navMenuDraft, [parentPath[0]])
      : null;
    const firstExistingProductChild = Array.isArray(productsRoot?.children) ? productsRoot.children[0] : null;
    const nextDraftGroupId = createDraftProductGroupId();
    const template = isProductsBranch
      ? {
          key: `products-group-${nextDraftGroupId}`,
          groupId: nextDraftGroupId,
          parentGroupId: Number(parentItem?.groupId || 0) || 0,
          customerId: Number(parentItem?.customerId || firstExistingProductChild?.customerId || 0) || 0,
          sourcePlatform: String(parentItem?.sourcePlatform || firstExistingProductChild?.sourcePlatform || "").trim(),
          iconImage: "",
          label: "",
          path: buildProductGroupDraftPath(nextDraftGroupId, ""),
          visible: true
        }
      : {
          key: `menu-${Date.now()}`,
          label: "",
          path: "/",
          visible: true
        };

    if (!parentPath || !parentPath.length) {
      setNavMenuDraft((prev) => {
        const next = [...prev, template];
        setEditingNavPath(pathToKey([next.length - 1]));
        return next;
      });
      return;
    }

    if (getNavMenuDepth(parentPath) >= MAX_NAV_DEPTH) {
      return;
    }

    setNavMenuDraft((prev) => {
      const parentItem = getNavMenuItemByPath(prev, parentPath);
      const nextChildIndex = Array.isArray(parentItem?.children) ? parentItem.children.length : 0;

      const next = updateNavMenuByPath(prev, parentPath, (item) => ({
        ...item,
        children: [...(Array.isArray(item.children) ? item.children : []), template]
      }));

      setEditingNavPath(pathToKey([...parentPath, nextChildIndex]));
      return next;
    });
    setExpandedNavRows((prev) => {
      const next = new Set(prev);
      next.add(pathToKey(parentPath));
      return next;
    });
  }

  function removeNavMenuItem(path) {
    setNavMenuDraft((prev) => removeNavMenuByPath(prev, path));
    setExpandedNavRows((prev) => {
      const key = pathToKey(path);
      const next = new Set();
      prev.forEach((item) => {
        if (item !== key && !item.startsWith(`${key}-`)) {
          next.add(item);
        }
      });
      return next;
    });
    setEditingNavPath((current) => {
      const key = pathToKey(path);
      return current === key || current.startsWith(`${key}-`) ? "" : current;
    });
  }

  function updateNavMenuItem(path, field, value) {
    setNavMenuDraft((prev) =>
      updateNavMenuByPath(prev, path, (item) => ({
        ...item,
        [field]: value
      }))
    );
  }

  function toggleNavRow(path) {
    const key = pathToKey(path);
    setExpandedNavRows((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }

  function expandAllNavRows() {
    setExpandedNavRows(new Set(collectExpandablePaths(navMenuDraft)));
  }

  function collapseAllNavRows() {
    setExpandedNavRows(new Set());
  }

  function addBannerDraft() {
    if (!canEditStorefront) {
      reportStorefrontWriteDenied();
      return;
    }

    setPendingBannerDraft({
      id: `banner-${Date.now()}`,
      title: "",
      annotation: "",
      buttonText: "",
      buttonLink: "",
      contentX: 12,
      contentY: 52,
      imageUrl: ""
    });
    setBannerEditSnapshot(null);
    setUploadingBannerIndex(null);
    setEditingBannerIndex(-1);
  }

  function buildBannerDraftsAfterRemoval(sourceBanners, index) {
    if (!Array.isArray(sourceBanners)) {
      return [];
    }
    return sourceBanners.filter((_, itemIndex) => itemIndex !== index);
  }

  function getEditingBannerIndexAfterRemoval(current, index) {
    if (current === null) {
      return current;
    }
    if (current === index) {
      return null;
    }
    if (current > index) {
      return current - 1;
    }
    return current;
  }

  function getBannerEditSnapshotAfterRemoval(current, index) {
    if (!current || current.index === null || current.index === undefined) {
      return current;
    }
    if (current.index === index) {
      return null;
    }
    if (current.index > index) {
      return { ...current, index: current.index - 1 };
    }
    return current;
  }

  async function removeBannerDraft(index) {
    if (savingBanners || !canEditStorefront) {
      if (!canEditStorefront) {
        reportStorefrontWriteDenied();
      }
      return;
    }

    const nextBannerDrafts = buildBannerDraftsAfterRemoval(bannerDrafts, index);
    const saved = await persistBannerDrafts(nextBannerDrafts, {
      errorMessage: "Banner delete failed",
      successMessage: texts.bannersSavedToast
    });

    if (!saved) {
      return;
    }

    setEditingBannerIndex((current) => getEditingBannerIndexAfterRemoval(current, index));
    setBannerEditSnapshot((current) => getBannerEditSnapshotAfterRemoval(current, index));
  }

  function updateBannerDraft(index, field, value) {
    if (index === -1) {
      setPendingBannerDraft((prev) => (prev
        ? {
            ...prev,
            [field]: value
          }
        : prev));
      return;
    }

    setBannerDrafts((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: value
            }
          : item
      )
    );
  }

  function getActiveBannerEditorDraft() {
    if (editingBannerIndex === -1) {
      return pendingBannerDraft;
    }

    if (editingBannerIndex === null || editingBannerIndex === undefined) {
      return null;
    }

    return bannerDrafts[editingBannerIndex] || null;
  }

  function buildSanitizedBanners(sourceBanners = bannerDrafts) {
    return sourceBanners
      .map((item, index) => ({
        id: String(item.id || `banner-${index + 1}`).trim(),
        title: String(item.title || "").trim(),
        annotation: String(item.annotation || "").trim(),
        buttonText: String(item.buttonText || "").trim(),
        buttonLink: String(item.buttonLink || "").trim(),
        contentX: Math.max(4, Math.min(78, Math.round(Number(item.contentX) || 12))),
        contentY: Math.max(4, Math.min(78, Math.round(Number(item.contentY) || 52))),
        imageUrl: String(item.imageUrl || "").trim()
      }))
      .filter((item) => item.imageUrl);
  }

  function mapBannerFromApi(item, index) {
    return {
      id: String(item.id || `banner-${index + 1}`),
      title: String(item.title || item.annotation || item.subtitle || ""),
      annotation: String(item.annotation || item.subtitle || ""),
      buttonText: String(item.buttonText || item.cta || ""),
      buttonLink: String(item.buttonLink || ""),
      contentX: Math.max(4, Math.min(78, Math.round(Number(item.contentX ?? item.textX ?? 12) || 12))),
      contentY: Math.max(4, Math.min(78, Math.round(Number(item.contentY ?? item.textY ?? 52) || 52))),
      imageUrl: String(item.imageUrl || "")
    };
  }

  async function persistBannerDrafts(sourceBanners, options = {}) {
    const {
      errorMessage = "Banner save failed",
      successMessage = texts.bannersSavedToast
    } = options;

    const sanitized = buildSanitizedBanners(sourceBanners);

    setSavingBanners(true);
    try {
      const response = await adminFetch(`${apiBaseUrl}/admin/store-mode`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ banners: sanitized })
      });

      const data = await readApiJson(response);
      if (!response.ok) {
        throw new Error(data.message || errorMessage);
      }

      setBannerDrafts(Array.isArray(data.banners) ? data.banners.map(mapBannerFromApi) : []);

      notifyStorefrontConfigUpdated();
      setAdminError("");
      setAdminToast(successMessage);
      return true;
    } catch (err) {
      if (err.message !== "Session expired") {
        setAdminError(err.message || errorMessage);
      }
      return false;
    } finally {
      setSavingBanners(false);
    }
  }

  function updateBannerCopyPosition(index, nextX, nextY) {
    updateBannerDraft(index, "contentX", Math.max(4, Math.min(78, Math.round(Number(nextX) || 12))));
    updateBannerDraft(index, "contentY", Math.max(4, Math.min(78, Math.round(Number(nextY) || 52))));
  }

  function handleBannerPositionPointer(index, event) {
    const rect = event.currentTarget.getBoundingClientRect();
    if (!rect.width || !rect.height) {
      return;
    }

    const relativeX = ((event.clientX - rect.left) / rect.width) * 100;
    const relativeY = ((event.clientY - rect.top) / rect.height) * 100;
    updateBannerCopyPosition(index, relativeX, relativeY);
  }

  async function saveCarouselBanners(event) {
    event.preventDefault();

    if (savingBanners || !canEditStorefront) {
      if (!canEditStorefront) {
        reportStorefrontWriteDenied();
      }
      return;
    }

    await persistBannerDrafts(bannerDrafts, {
      errorMessage: "Banner save failed",
      successMessage: texts.bannersSavedToast
    });
  }

  async function uploadBannerImage(index, event) {
    const files = Array.from(event?.target?.files || []);
    event.target.value = "";
    if (!files.length) {
      return;
    }

    if (!canEditStorefront) {
      reportStorefrontWriteDenied();
      return;
    }

    if (uploadingBannerIndex !== null) {
      return;
    }

    const file = files[0];
    const formData = new FormData();
    formData.append("file", file);

    const normalizedBase = String(apiBaseUrl || "").replace(/\/$/, "");
    const uploadEndpoints = Array.from(new Set([
      `${normalizedBase}/admin/uploads/banners`,
      normalizedBase.endsWith("/api")
        ? `${normalizedBase.slice(0, -4)}/admin/uploads/banners`
        : `${normalizedBase}/api/admin/uploads/banners`
    ]));

    setUploadingBannerIndex(index);
    try {
      let data = null;
      let uploadedPath = "";
      let lastError = null;

      for (const endpoint of uploadEndpoints) {
        try {
          const response = await adminFetch(endpoint, {
            method: "POST",
            body: formData
          });

          let payload = {};
          try {
            payload = await readApiJson(response);
          } catch (parseErr) {
            if (response.status === 404) {
              lastError = parseErr;
              continue;
            }
            throw parseErr;
          }

          if (response.status === 404) {
            lastError = new Error(payload.message || "Banner image upload endpoint not found");
            continue;
          }

          if (!response.ok) {
            throw new Error(payload.message || "Banner image upload failed");
          }

          data = payload;
          uploadedPath = String(payload?.files?.[0]?.relativePath || payload?.files?.[0]?.url || "").trim();
          if (!uploadedPath) {
            throw new Error("Banner image upload failed");
          }
          break;
        } catch (err) {
          if (err.message === "Session expired") {
            throw err;
          }
          lastError = err;
        }
      }

      if (!data || !uploadedPath) {
        throw lastError || new Error("Banner image upload failed");
      }

      updateBannerDraft(index, "imageUrl", uploadedPath);
      setAdminError("");
      setAdminToast(locale === "zh" ? "横幅图片上传成功" : "Banner image uploaded successfully");
    } catch (err) {
      if (err.message !== "Session expired") {
        const message = err.message || "Banner image upload failed";
        setAdminError(message);
        setAdminToast(message);
      }
    } finally {
      setUploadingBannerIndex(null);
    }
  }

  function beginBannerEdit(index) {
    if (!canEditStorefront) {
      reportStorefrontWriteDenied();
      return;
    }

    const target = bannerDrafts[index];
    if (!target) {
      return;
    }

    setBannerEditSnapshot({ index, banner: { ...target } });
    setPendingBannerDraft(null);
    setUploadingBannerIndex(null);
    setEditingBannerIndex(index);
  }

  async function saveBannerEdit() {
    const activeBannerDraft = getActiveBannerEditorDraft();
    if (!activeBannerDraft) {
      setAdminError(locale === "zh" ? "横幅草稿不存在，请重新打开编辑窗口。" : "Banner draft is missing. Please reopen the editor.");
      return;
    }

    if (!String(activeBannerDraft.imageUrl || "").trim()) {
      const message = locale === "zh"
        ? "请先上传横幅图片或填写远程图片地址，再保存横幅。"
        : "Please upload a banner image or enter a remote image URL before saving.";
      setAdminError(message);
      setAdminToast(message);
      return;
    }

    const nextBannerDrafts = editingBannerIndex === -1
      ? [...bannerDrafts, { ...activeBannerDraft }]
      : bannerDrafts;

    const saved = await persistBannerDrafts(nextBannerDrafts, {
      errorMessage: "Banner save failed",
      successMessage: texts.bannersSavedToast
    });

    if (!saved) {
      return;
    }

    setEditingBannerIndex(null);
    setPendingBannerDraft(null);
    setBannerEditSnapshot(null);
    setUploadingBannerIndex(null);
  }

  function cancelBannerEdit() {
    if (editingBannerIndex === -1) {
      setEditingBannerIndex(null);
      setPendingBannerDraft(null);
      setBannerEditSnapshot(null);
      setUploadingBannerIndex(null);
      setBannerPositionDragging(false);
      return;
    }

    setBannerDrafts((prev) => {
      if (!bannerEditSnapshot || bannerEditSnapshot.index === null || bannerEditSnapshot.index === undefined) {
        return prev;
      }

      return prev.map((item, itemIndex) =>
        itemIndex === bannerEditSnapshot.index
          ? { ...bannerEditSnapshot.banner }
          : item
      );
    });

    setEditingBannerIndex(null);
    setPendingBannerDraft(null);
    setBannerEditSnapshot(null);
    setUploadingBannerIndex(null);
    setBannerPositionDragging(false);
  }

  function renderBannerListView(onSubmit, isSaving, saveLabel, options = {}) {
    const { showSaveButton = true, showAddButton = true } = options;
    const WrapperTag = showSaveButton ? "form" : "div";
    const wrapperProps = showSaveButton
      ? { className: "controlhub-banner-settings-form", onSubmit, "aria-busy": isSaving }
      : { className: "controlhub-banner-settings-form", role: "group", "aria-busy": isSaving };

    return (
      <WrapperTag {...wrapperProps}>
        <div className="table-wrap controlhub-listview-wrap">
          <table className="controlhub-table controlhub-listview-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{texts.bannerImageUrl}</th>
                <th>{texts.bannerTitle}</th>
                <th>{texts.bannerAnnotation}</th>
                <th>{texts.bannerButtonText}</th>
                <th>{texts.bannerButtonLink}</th>
                <th>{texts.actions}</th>
              </tr>
            </thead>
            <tbody>
              {paginatedBannerRows.items.map((banner, index) => {
                const actualIndex = paginatedBannerRows.startIndex + index;
                return (
                  <tr key={`listview-${banner.id}-${index}`}>
                    <td>{actualIndex + 1}</td>
                    <td>
                      {banner.imageUrl ? (
                        <span className="controlhub-banner-thumb-wrap" title={banner.imageUrl}>
                          <img className="controlhub-banner-thumb" src={banner.imageUrl} alt={banner.title || `banner-${actualIndex + 1}`} loading="lazy" />
                        </span>
                      ) : (
                        <span className="controlhub-list-cell-text">-</span>
                      )}
                    </td>
                    <td>
                      <span className="controlhub-list-cell-text">{banner.title || "-"}</span>
                    </td>
                    <td>
                      <span className="controlhub-list-cell-text">{banner.annotation || "-"}</span>
                    </td>
                    <td>
                      <span className="controlhub-list-cell-text">{banner.buttonText || "-"}</span>
                    </td>
                    <td>
                      <span className="controlhub-list-cell-text">{banner.buttonLink || "-"}</span>
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="controlhub-button ghost"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            beginBannerEdit(actualIndex);
                          }}
                          disabled={!canEditStorefront}
                        >
                          {texts.edit}
                        </button>
                        <button
                          type="button"
                          className="controlhub-button danger"
                          onClick={() => removeBannerDraft(actualIndex)}
                          disabled={!canEditStorefront}
                        >
                          {texts.removeBanner}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {isSaving ? (
            <div className="controlhub-banner-saving-overlay" role="status" aria-live="polite">
              <div className="controlhub-banner-saving-card">
                <span className="controlhub-banner-saving-spinner" aria-hidden="true" />
                <span>{locale === "zh" ? "正在保存横幅配置..." : "Saving banner configuration..."}</span>
              </div>
            </div>
          ) : null}
        </div>
        {renderPagination("banners", paginatedBannerRows)}

        <div className="row-actions">
          {showAddButton ? (
            <button type="button" className="controlhub-button ghost" onClick={addBannerDraft} disabled={!canEditStorefront}>
              {texts.addBanner}
            </button>
          ) : null}
          {showSaveButton ? (
            <button type="submit" className="controlhub-button" disabled={isSaving || !canEditStorefront}>
              {saveLabel}
            </button>
          ) : null}
        </div>
      </WrapperTag>
    );
  }

  function renderNavMenuRows(items, level = 1, parentPath = []) {
    return items.flatMap((item, index) => {
      const currentPath = [...parentPath, index];
      const rowKey = pathToKey(currentPath);
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;
      const isExpanded = expandedNavRows.has(rowKey);
      const isEditing = editingNavPath === rowKey;
      const isProductsBranch = isProductsNavBranch(currentPath);
      const isProductsRoot = isProductsBranch && parentPath.length === 0 && String(item?.key || "").trim() === "products";
      const isProductGroup = isProductsBranch && !isProductsRoot;
      const canAddChild = level < MAX_NAV_DEPTH;
      const labelDisabled = !canEditStorefront;
      const pathDisabled = !canEditStorefront || isProductGroup;
      const iconDisabled = !canEditStorefront || !isProductGroup;
      const visibleDisabled = !canEditStorefront;
      const structureControlsDisabled = !canEditStorefront;

      const row = (
        <tr key={`nav-row-${rowKey}`}>
          <td>
            <div className="controlhub-nav-tree-cell" style={{ paddingLeft: `${(level - 1) * 20}px` }}>
              {hasChildren ? (
                <button
                  type="button"
                  className="controlhub-nav-toggle"
                  onClick={() => toggleNavRow(currentPath)}
                  aria-label={isExpanded ? "Collapse row" : "Expand row"}
                >
                  {isExpanded ? "▾" : "▸"}
                </button>
              ) : (
                <span className="controlhub-nav-toggle-placeholder" aria-hidden="true">•</span>
              )}
              {isEditing ? (
                <input
                  value={item.label}
                  onChange={(event) => updateNavMenuItem(currentPath, "label", event.target.value)}
                  disabled={labelDisabled}
                />
              ) : (
                <span>
                  {item.label}
                  {isProductGroup ? " (DB)" : ""}
                </span>
              )}
            </div>
          </td>
          <td>
            {isEditing ? (
              <input
                value={isProductGroup ? buildProductGroupDraftPath(item.groupId, item.label) : item.path}
                onChange={(event) => updateNavMenuItem(currentPath, "path", event.target.value)}
                disabled={pathDisabled}
              />
            ) : (
              isProductGroup ? buildProductGroupDraftPath(item.groupId, item.label) : item.path
            )}
          </td>
          <td>
            {isProductGroup ? (
              isEditing ? (
                <input
                  value={String(item.iconImage || "")}
                  onChange={(event) => updateNavMenuItem(currentPath, "iconImage", event.target.value.slice(0, 200))}
                  placeholder={locale === "zh" ? "图标地址 / 相对路径" : "Icon URL / relative path"}
                  disabled={iconDisabled}
                />
              ) : (
                String(item.iconImage || "").trim() ? (
                  <span title={String(item.iconImage || "").trim()}>{String(item.iconImage || "").trim()}</span>
                ) : (
                  <span className="muted">-</span>
                )
              )
            ) : (
              <span className="muted">-</span>
            )}
          </td>
          <td>
            <button
              type="button"
              className={`controlhub-switch ${item.visible !== false ? "is-on" : "is-off"}`}
              onClick={() => updateNavMenuItem(currentPath, "visible", !(item.visible !== false))}
              disabled={visibleDisabled}
              role="switch"
              aria-checked={item.visible !== false}
              aria-label={item.visible !== false ? (locale === "zh" ? "当前为显示，点击隐藏" : "Currently visible, click to hide") : (locale === "zh" ? "当前为隐藏，点击显示" : "Currently hidden, click to show")}
            >
              <span className="controlhub-switch-track" aria-hidden="true">
                <span className="controlhub-switch-thumb" />
              </span>
              <span className="controlhub-switch-label">
                {item.visible !== false ? (locale === "zh" ? "显示" : "Visible") : (locale === "zh" ? "隐藏" : "Hidden")}
              </span>
            </button>
          </td>
          <td>
            <div className="row-actions">
              <button
                type="button"
                className="controlhub-button ghost"
                onClick={async () => {
                  if (!isEditing) {
                    setEditingNavPath(rowKey);
                    return;
                  }

                  const saved = await saveNavigationMenu();
                  if (saved) {
                    setEditingNavPath("");
                  }
                }}
                disabled={!canEditStorefront}
              >
                {isEditing ? texts.save : texts.edit}
              </button>
              {canAddChild ? (
                <button
                  type="button"
                  className="controlhub-button ghost"
                  onClick={() => addNavMenuItem(currentPath)}
                  disabled={structureControlsDisabled}
                >
                  {texts.addSubmenu}
                </button>
              ) : null}
              <button
                type="button"
                className="controlhub-button danger"
                onClick={() => removeNavMenuItem(currentPath)}
                disabled={structureControlsDisabled || (!parentPath.length && navMenuDraft.length <= 1)}
              >
                {texts.remove}
              </button>
            </div>
          </td>
        </tr>
      );

      if (!hasChildren || !isExpanded) {
        return [row];
      }

      return [row, ...renderNavMenuRows(item.children, level + 1, currentPath)];
    });
  }

  const homeDashboard = (
    <AdminDashboardHome
      adminRole={adminRole}
      profileData={profileData}
      locale={locale}
      onChangeLocale={onChangeLocale}
      onNavigate={switchMenu}
      onLogout={onLogout}
      brandCard={{
        adminTitle: storeSettingsDraft.adminTitle,
        logoUrl: storeSettingsDraft.logoUrl,
        logoAlt: storeSettingsDraft.logoAlt
      }}
    />
  );

  const shellMeta = {
    profile: {
      title: locale === "zh" ? "个人资料" : "Profile",
      subtitle: locale === "zh" ? "查看账号、角色与个人档案信息。" : "Review account, role, and profile details."
    },
    notifications: {
      title: locale === "zh" ? "通知管理" : "Notification Management",
      subtitle: locale === "zh" ? "发布、修改、删除通知，并维护接收范围。" : "Publish, update, delete notifications, and maintain audience scope."
    },
    notificationPlan: {
      title: locale === "zh" ? "通知计划" : "Notification Plan",
      subtitle: locale === "zh" ? "查看当前通知池的覆盖范围、未读压力与发布节奏。" : "Review coverage, unread pressure, and release cadence across the notification pool."
    },
    password: {
      title: locale === "zh" ? "密码安全" : "Password Security",
      subtitle: locale === "zh" ? "更新管理员登录密码与账户安全设置。" : "Update admin password and account security settings."
    },
    inquiry: {
      title: locale === "zh" ? "询盘中心" : "Inquiry Center",
      subtitle: locale === "zh" ? "集中处理询盘、读取状态与批量动作。" : "Handle inquiries, read states, and batch actions from one place."
    },
    products: {
      title: locale === "zh" ? "商品管理" : "Product Management",
      subtitle: locale === "zh" ? "围绕商品资料、分析与发布效率组织页面。" : "Organize the page around product content, analytics, and publish efficiency."
    },
    productCreate: {
      title: locale === "zh" ? "商品发布工作台" : "Product Publish Workspace",
      subtitle: locale === "zh" ? "集中完成商品资料上传、配置与发布。" : "Upload, configure, and publish product detail content from one workspace."
    },
    merchandising: {
      title: locale === "zh" ? "选品中心" : "Merchandising Workspace",
      subtitle: locale === "zh" ? "围绕市场、利润带与准备度组织选品决策。" : "Organize merchandising decisions around markets, margin bands, and readiness."
    },
    procurement: {
      title: locale === "zh" ? "采购协同" : "Procurement Collaboration",
      subtitle: locale === "zh" ? "聚焦采购需求、供应推进与到货前准备。" : "Focus on sourcing demand, supplier progress, and pre-arrival readiness."
    },
    solutions: {
      title: locale === "zh" ? "质检与方案" : "QA and Solutions",
      subtitle: locale === "zh" ? "跟进样品、来料验收与发货前质检。" : "Track samples, inbound inspection, and pre-shipment QA."
    },
    logistics: {
      title: locale === "zh" ? "物流协同" : "Logistics Coordination",
      subtitle: locale === "zh" ? "围绕发运准备、装箱资料和物流追踪协同。" : "Coordinate shipment readiness, packing docs, and logistics tracking."
    },
    orders: {
      title: locale === "zh" ? "订单管理" : "Order Management",
      subtitle: locale === "zh" ? "查看履约进度、状态与订单金额。" : "Review fulfillment progress, status, and order values."
    },
    ordersPaidQueue: {
      title: locale === "zh" ? "待履约订单" : "Pending Fulfillment",
      subtitle: locale === "zh" ? "聚焦已付款待处理订单，方便推进打包和发货。" : "Focus on paid orders still waiting for fulfillment action."
    },
    ordersInTransit: {
      title: locale === "zh" ? "运输跟踪" : "Transit Tracking",
      subtitle: locale === "zh" ? "集中查看运输中订单，跟进物流节点与异常风险。" : "Track in-transit orders and monitor logistics risk in one view."
    },
    ordersCompleted: {
      title: locale === "zh" ? "已完成订单" : "Completed Orders",
      subtitle: locale === "zh" ? "复盘已交付订单与成交兑现情况。" : "Review delivered orders and realized revenue."
    },
    ordersAfterSales: {
      title: locale === "zh" ? "取消与售后" : "Cancelled and After-sales",
      subtitle: locale === "zh" ? "把取消订单收拢为售后与履约风险观察位。" : "Treat cancelled orders as a compact after-sales and fulfillment risk slice."
    },
    customers: {
      title: locale === "zh" ? "客户管理" : "Customer Management",
      subtitle: locale === "zh" ? "统一查看客户档案、角色与活跃度。" : "Review customer records, roles, and activity from one view."
    },
    customersSegments: {
      title: locale === "zh" ? "客户分层" : "Customer Segments",
      subtitle: locale === "zh" ? "把角色结构与活跃度整理成可读的客户分层视图。" : "Turn role structure and activity into a readable segmentation view."
    },
    customersTags: {
      title: locale === "zh" ? "标签洞察" : "Tag Insights",
      subtitle: locale === "zh" ? "用购物车和状态信号承接客户标签洞察。" : "Use cart and account signals as a practical tag-insight panel."
    },
    articleManagement: {
      title: locale === "zh" ? "内容管理" : "Content Management",
      subtitle: locale === "zh" ? "维护文章、FAQ 与信任内容。" : "Manage articles, FAQ, and trust-building content."
    },
    marketing: {
      title: locale === "zh" ? "营销中心" : "Marketing Center",
      subtitle: locale === "zh" ? "聚合活动素材、流量入口与增长动作。" : "Group campaigns, traffic entries, and growth actions together."
    },
    marketingPoster: {
      title: locale === "zh" ? "素材排期" : "Creative Scheduling",
      subtitle: locale === "zh" ? "把横幅、导航和活动素材放进同一排期视图。" : "Bring banners, navigation, and campaign creatives into one scheduling view."
    },
    marketingEmail: {
      title: locale === "zh" ? "触达邮件" : "Lifecycle Email Outreach",
      subtitle: locale === "zh" ? "把营销、召回和通知邮件放进同一运营节奏里。" : "Frame campaign, reactivation, and notice emails inside one operating rhythm."
    },
    discounts: {
      title: locale === "zh" ? "优惠券与折扣" : "Coupons and Discounts",
      subtitle: locale === "zh" ? "查看促销覆盖、力度与复购机会。" : "Review promotion coverage, depth, and repeat-buy opportunities."
    },
    analytics: {
      title: locale === "zh" ? "数据分析" : "Analytics",
      subtitle: locale === "zh" ? "将访问、订单与客户指标压缩在同一页。" : "Bring traffic, order, and customer signals onto one page."
    },
    analyticsSales: {
      title: locale === "zh" ? "销售分析" : "Sales Analytics",
      subtitle: locale === "zh" ? "聚焦订单规模、营收和成交兑现。" : "Focus on order volume, revenue, and sales realization."
    },
    analyticsProducts: {
      title: locale === "zh" ? "商品分析" : "Product Analytics",
      subtitle: locale === "zh" ? "从曝光位、订单和客户反应看商品表现。" : "Read product performance through placements, orders, and customer response."
    },
    analyticsCustomers: {
      title: locale === "zh" ? "客户分析" : "Customer Analytics",
      subtitle: locale === "zh" ? "集中看客户规模、转化和复购潜力。" : "Review customer scale, conversion, and repeat-buy potential together."
    },
    finance: {
      title: locale === "zh" ? "财务中心" : "Finance Center",
      subtitle: locale === "zh" ? "快速判断营收、订单兑现与投入产出。" : "Assess revenue, fulfillment, and ROI quickly."
    },
    financeIncome: {
      title: locale === "zh" ? "入账流水" : "Income Ledger",
      subtitle: locale === "zh" ? "按订单和回款口径查看公司入账记录。" : "Review income records by order and collection context."
    },
    financeExpenses: {
      title: locale === "zh" ? "支出账单" : "Expense Bills",
      subtitle: locale === "zh" ? "登记采购、物流、营销和行政等公司支出。" : "Track purchasing, logistics, marketing, and office expenses."
    },
    financeReceivables: {
      title: locale === "zh" ? "应收账款" : "Receivables",
      subtitle: locale === "zh" ? "按客户、订单和账龄跟踪待收款项。" : "Track outstanding collections by customer, order, and aging bucket."
    },
    financePayables: {
      title: locale === "zh" ? "应付账款" : "Payables",
      subtitle: locale === "zh" ? "管理供应商、物流和服务商的待付款项。" : "Manage supplier, logistics, and service-provider payables."
    },
    financeInvoices: {
      title: locale === "zh" ? "发票管理" : "Invoice Management",
      subtitle: locale === "zh" ? "跟进开票、收票、票据状态与异常。" : "Track issued invoices, received invoices, receipts, and exceptions."
    },
    financeTax: {
      title: locale === "zh" ? "税务管理" : "Tax Management",
      subtitle: locale === "zh" ? "复核销项、进项、税费预估与申报准备。" : "Review output tax, input tax, estimates, and filing readiness."
    },
    financeBudget: {
      title: locale === "zh" ? "预算费用" : "Budget & Expenses",
      subtitle: locale === "zh" ? "按部门和费用科目跟踪预算、占用与超支风险。" : "Track budgets, commitments, and overspend risk by department and cost category."
    },
    financeDaily: {
      title: locale === "zh" ? "日账单" : "Daily Bills",
      subtitle: locale === "zh" ? "按天查看入账、开销和净现金变化。" : "Review income, expenses, and net cash changes by day."
    },
    financeMonthly: {
      title: locale === "zh" ? "月度结账" : "Monthly Closing",
      subtitle: locale === "zh" ? "按月完成收支结账、利润复盘和预算偏差分析。" : "Close monthly income, spend, profit, and budget variance."
    },
    financeYearly: {
      title: locale === "zh" ? "财务年报" : "Annual Statements",
      subtitle: locale === "zh" ? "按年查看营收、费用、税费与净利润趋势。" : "Review annual revenue, expenses, tax, and net-profit trends."
    },
    financeReconciliation: {
      title: locale === "zh" ? "收支对账" : "Reconciliation",
      subtitle: locale === "zh" ? "核对应收、已收、应付、开票与税费状态。" : "Reconcile receivables, collections, payables, invoices, and tax status."
    },
    financeApprovals: {
      title: locale === "zh" ? "审批审计" : "Approvals & Audit",
      subtitle: locale === "zh" ? "集中处理付款、退款、费用和异常凭证的审批记录。" : "Centralize payment, refund, expense, and exception-voucher approvals."
    },
    financeReports: {
      title: locale === "zh" ? "报表导出" : "Reports & Export",
      subtitle: locale === "zh" ? "沉淀财务报表、经营指标和导出任务。" : "Prepare finance statements, operating metrics, and export jobs."
    },
    wishlist: {
      title: locale === "zh" ? "心愿单视图" : "Wishlist View",
      subtitle: locale === "zh" ? "统一承接收藏与后续转化动作。" : "Carry wishlist and follow-up conversion work in the unified shell."
    },
    orderProcessing: {
      title: locale === "zh" ? "订单处理" : "Order Processing",
      subtitle: locale === "zh" ? "把履约待办与处理节奏放进统一运营页。" : "Bring fulfillment queues and action rhythm into the shared operations page."
    },
    afterSales: {
      title: locale === "zh" ? "售后服务" : "After-sales Service",
      subtitle: locale === "zh" ? "统一查看售后占位、后续动作和扩展入口。" : "Unify after-sales placeholders, next actions, and expansion entry points."
    },
    workTargets: {
      title: locale === "zh" ? "岗位目标" : "Work Targets",
      subtitle: locale === "zh" ? "围绕岗位覆盖与执行目标组织页面。" : "Organize the page around position coverage and execution targets."
    },
    workProcessing: {
      title: locale === "zh" ? "工作进度" : "Work Progress",
      subtitle: locale === "zh" ? "让跟进队列和执行进度使用统一后台视觉。" : "Give execution queues and progress the same admin visual language."
    },
    settings: {
      title: locale === "zh" ? "系统设置" : "System Settings",
      subtitle: locale === "zh" ? "管理数据库、邮件与缓存等运行配置。" : "Manage database, mail, cache, and runtime configuration."
    },
    siteMaintenance: {
      title: locale === "zh" ? "站点维护" : "Site Maintenance",
      subtitle: locale === "zh" ? "统一查看岗位权限维护与运行探针状态。" : "Review position maintenance and runtime probe health in one place."
    },
    positionMaintenance: {
      title: locale === "zh" ? "成员与权限" : "Members and Permissions",
      subtitle: locale === "zh" ? "集中处理成员账号、角色状态与权限动作。" : "Manage member accounts, role state, and permission actions from one workspace."
    },
    hrEmployee: {
      title: locale === "zh" ? "员工档案" : "Employee Profiles",
      subtitle: locale === "zh" ? "统一查看具备岗位的员工信息与账号状态。" : "Review positioned employee records and account status in one place."
    },
    hrRecruitment: {
      title: locale === "zh" ? "招聘与入岗" : "Recruitment and Onboarding",
      subtitle: locale === "zh" ? "统一推进招聘状态与入岗阶段。" : "Track recruitment status and onboarding stages inside the same shell."
    },
    hrAttendance: {
      title: locale === "zh" ? "考勤与活跃" : "Attendance and Activity",
      subtitle: locale === "zh" ? "聚焦人员活跃与考勤导入。" : "Focus attendance imports and employee activity in one page."
    },
    hrOffice: {
      title: locale === "zh" ? "行政协同" : "Office Administration",
      subtitle: locale === "zh" ? "统一承接公告、制度文件与岗位覆盖。" : "Unify announcements, policy files, and position coverage in one workspace."
    }
  }[adminTab] || {
    title: texts[adminTab] || (locale === "zh" ? "管理后台" : "Admin Console"),
    subtitle: ADMIN_TAB_DESCRIPTIONS?.[adminTab]?.[locale] || (locale === "zh" ? "统一后台运营视图。" : "Unified admin operations view.")
  };

  const activeManagedUserCount = memberFilteredRows.filter(
    (item) => normalizeUserStatus(item?.status) === "active"
  ).length;
  const canQuickCreateProduct = allowedTabs.includes("productCreate")
    && ["products", "productCreate", "merchandising", "procurement", "solutions", "logistics"].includes(adminTab);
  const canQuickRefreshInquiries = adminTab === "inquiry";
  const canQuickManageMembers = ["positionMaintenance", "siteMaintenance"].includes(adminTab) && adminRole === "admin";
  const canQuickRefreshRuntime = adminTab === "siteMaintenance";

  const shellQuickActions = [
    {
      key: "open-storefront",
      label: locale === "zh" ? "查看前台" : "Open Storefront",
      icon: "dashboard",
      onClick: openStorefront
    },
    {
      key: "refresh-admin",
      label: texts.refresh,
      icon: "analytics",
      onClick: () => {
        void fetchAdminData();
        void fetchPositionWorkspaceData();
      }
    },
    ...(canQuickRefreshInquiries
      ? [{
          key: "refresh-inquiries",
          label: locale === "zh" ? "刷新询盘" : "Refresh Inquiries",
          icon: "customers",
          onClick: () => {
            setAdminInquiryPage(1);
            void fetchAdminInquiries(1);
          }
        }]
      : []),
    ...(canQuickManageMembers
      ? [{
          key: "create-position",
          label: texts.addPosition,
          icon: "team",
          onClick: () => openPositionPermissionModal("create")
        }]
      : []),
    ...(canQuickRefreshRuntime
      ? [{
          key: "refresh-runtime",
          label: locale === "zh" ? "刷新探针" : "Refresh Probes",
          icon: "settings",
          onClick: () => {
            void fetchSiteMaintenanceProbes();
          }
        }]
      : []),
    ...(canQuickCreateProduct
      ? [{
          key: "quick-create-product",
          label: locale === "zh" ? "新增商品" : "Create Product",
          icon: "products",
          tone: "primary",
          onClick: openCreateProductEditor
        }]
      : [])
  ];

  const systemConfigForm = (
    <SystemConfigForm
      texts={texts}
      locale={locale}
      systemConfigDraft={systemConfigDraft}
      setSystemConfigDraft={setSystemConfigDraft}
      onSubmit={saveSystemConfig}
      savingSystemConfig={savingSystemConfig}
      systemConfigPath={systemConfigPath}
      accessRole={accessRole}
    />
  );

  const resolveOrderValue = useCallback((item) => {
    const rawValue = Number(
      item?.totalAmount
      ?? item?.total
      ?? item?.totalPrice
      ?? item?.amount
      ?? item?.orderAmount
      ?? 0
    );
    return Number.isFinite(rawValue) ? rawValue : 0;
  }, []);

  const formatUsdAmount = useCallback((value) => `US $${Number(value || 0).toFixed(2)}`, []);

  const unreadInquiryCount = Math.max(0, Number(notificationSummary.unreadInquiryCount || 0) || 0);

  const activeMerchandisingFilterEntries = useMemo(
    () => Object.entries(merchandisingFilters).filter(([, value]) => Boolean(String(value || "").trim())),
    [merchandisingFilters]
  );

  const activeMerchandisingFilterCount = activeMerchandisingFilterEntries.length;

  const merchandisingFilterPillLabels = useMemo(() => ({
    targetMarket: locale === "zh" ? "目标市场" : "Target Market",
    priceBand: locale === "zh" ? "价格带" : "Price Band",
    moqRange: "MOQ",
    sampleStatus: locale === "zh" ? "样品状态" : "Sample Status",
    complianceStatus: locale === "zh" ? "合规状态" : "Compliance Status"
  }), [locale]);

  const clearMerchandisingFilters = useCallback(() => {
    setMerchandisingFilters({
      targetMarket: "",
      priceBand: "",
      moqRange: "",
      sampleStatus: "",
      complianceStatus: ""
    });
  }, []);

  const readSystemNotificationKeySet = useMemo(
    () => new Set((Array.isArray(notificationSummary.readSystemNotificationKeys) ? notificationSummary.readSystemNotificationKeys : []).map((item) => String(item || "").trim()).filter(Boolean)),
    [notificationSummary.readSystemNotificationKeys]
  );

  const systemNotificationItems = useMemo(() => {
    const candidates = [
      {
        key: `notification-inquiry-follow-up:${unreadInquiryCount}`,
        title: locale === "zh" ? "询盘中心待跟进" : "Inquiry Follow-up Queue",
        detail: locale === "zh"
          ? `询盘或即时消息入口已有 ${unreadInquiryCount} 条待跟进信号。`
          : `${unreadInquiryCount} inquiry or instant-message signals are waiting for follow-up.`,
        target: "inquiry",
        tag: locale === "zh" ? "客户沟通" : "Customer Comms"
      },
      {
        key: "notification-product-publish",
        title: locale === "zh" ? "商品发布工作台" : "Product Publish Workspace",
        detail: locale === "zh"
          ? "检查主图、详情图和价格字段，避免发布链路断点。"
          : "Review hero images, detail assets, and pricing fields before publish handoff.",
        target: "productCreate",
        tag: locale === "zh" ? "商品" : "Products"
      },
      {
        key: "notification-product-catalog",
        title: locale === "zh" ? "商品目录校对" : "Catalog Review",
        detail: locale === "zh"
          ? "回到商品管理面板，检查 SKU、规格和上架状态。"
          : "Jump back to product management to inspect SKUs, specs, and publish state.",
        target: "products",
        tag: locale === "zh" ? "目录" : "Catalog"
      },
      {
        key: "notification-orders",
        title: locale === "zh" ? "履约订单处理" : "Fulfillment Queue",
        detail: locale === "zh"
          ? "优先处理待履约与在途订单，避免客户催单。"
          : "Review pending and in-transit orders before they turn into escalations.",
        target: allowedTabs.includes("ordersPaidQueue") ? "ordersPaidQueue" : "orders",
        tag: locale === "zh" ? "订单" : "Orders"
      },
      {
        key: "notification-runtime",
        title: locale === "zh" ? "运行探针检查" : "Runtime Probe Check",
        detail: locale === "zh"
          ? "数据库、缓存和站点探针建议每天至少巡检一次。"
          : "Database, cache, and site probes should be checked at least once per day.",
        target: allowedTabs.includes("siteMaintenance") ? "siteMaintenance" : "settings",
        tag: locale === "zh" ? "系统" : "Runtime"
      },
      {
        key: "notification-members",
        title: locale === "zh" ? "成员与权限同步" : "Member Permission Sync",
        detail: locale === "zh"
          ? "检查岗位权限模板和成员账号状态，避免菜单与权限不一致。"
          : "Review position templates and member states to avoid permission drift.",
        target: "positionMaintenance",
        tag: locale === "zh" ? "权限" : "Permissions"
      },
      {
        key: "notification-marketing",
        title: locale === "zh" ? "营销触达排期" : "Marketing Outreach Schedule",
        detail: locale === "zh"
          ? "核对邮件、横幅和内容投放节奏。"
          : "Check the cadence across email, banners, and content distribution.",
        target: allowedTabs.includes("marketingEmail") ? "marketingEmail" : "marketing",
        tag: locale === "zh" ? "营销" : "Marketing"
      },
      {
        key: "notification-analytics",
        title: locale === "zh" ? "经营数据快照" : "Analytics Snapshot",
        detail: locale === "zh"
          ? "查看今日销售、商品与客户信号，决定下一轮动作。"
          : "Review sales, product, and customer signals before setting the next action plan.",
        target: allowedTabs.includes("analyticsProducts") ? "analyticsProducts" : "analytics",
        tag: locale === "zh" ? "数据" : "Analytics"
      }
    ];

    return candidates.filter((item) => {
      if (item.target !== "notifications" && !allowedTabs.includes(item.target)) {
        return false;
      }
      if (item.target === "inquiry" && unreadInquiryCount <= 0) {
        return false;
      }
      return !readSystemNotificationKeySet.has(item.key);
    });
  }, [allowedTabs, locale, readSystemNotificationKeySet, unreadInquiryCount]);

  const unreadNotificationCount = Math.max(0, Number(notificationSummary?.unreadNotificationCount || 0) || 0);
  const systemNotificationCount = adminTab === "notifications" || adminTab === "notificationPlan"
    ? 0
    : Math.max(systemNotificationItems.length, unreadNotificationCount);
  const inquiryMessageCount = adminTab === "inquiry" ? 0 : (allowedTabs.includes("inquiry") ? unreadInquiryCount : 0);

  const openSystemNotifications = useCallback(() => {
    setAdminTab("notifications");
  }, []);

  const openInquiryWorkspace = useCallback(() => {
    if (!allowedTabs.includes("inquiry")) {
      return;
    }
    setAdminTab("inquiry");
  }, [allowedTabs]);

  useEffect(() => {
    if (!notificationSummaryAvailable || adminTab !== "notifications" || !systemNotificationItems.length) {
      return;
    }

    let active = true;

    (async () => {
      try {
        const response = await adminFetch(`${apiBaseUrl}/admin/notifications/read`, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            keys: systemNotificationItems.map((item) => item.key)
          })
        });
        const data = await readApiJson(response);
        if (!response.ok) {
          throw new Error(data?.message || "Notification read state update failed");
        }

        if (active) {
          await fetchNotificationSummary();
        }
      } catch (err) {
        // Keep background notification sync failures silent so they do not leak into unrelated admin pages.
      }
    })();

    return () => {
      active = false;
    };
  }, [adminFetch, adminTab, apiBaseUrl, fetchNotificationSummary, notificationSummaryAvailable, systemNotificationItems]);

  useEffect(() => {
    if (!hasValidatedSession || !allowedTabs.includes("notifications")) {
      return;
    }

    if (adminTab !== "notifications" && adminTab !== "notificationPlan") {
      return;
    }

    void fetchManagedNotifications();
  }, [adminTab, allowedTabs, fetchManagedNotifications, hasValidatedSession]);

  const wishlistLeadRows = useMemo(() => {
    const baseRows = filteredAdminUsers.map((item) => {
      const savedCount = Math.max(
        0,
        Number(item?.wishlistCount || 0),
        Number(item?.favoriteCount || 0),
        Number(item?.savedItems || 0),
        Number(item?.cartItems || 0)
      );
      const orderCount = Math.max(0, Number(item?.orderCount || item?.orders || 0));
      return {
        ...item,
        savedCount,
        orderCount,
        intentScore: savedCount * 3 + orderCount
      };
    });

    const rankedRows = baseRows
      .filter((item) => item.intentScore > 0)
      .sort((left, right) => {
        if (right.intentScore !== left.intentScore) {
          return right.intentScore - left.intentScore;
        }

        return Number(right?.lastActiveAt || 0) - Number(left?.lastActiveAt || 0);
      });

    return (rankedRows.length ? rankedRows : baseRows.filter((item) => item.email || item.name || item.username)).slice(0, 8);
  }, [filteredAdminUsers]);

  const wishlistTotalSaved = useMemo(
    () => wishlistLeadRows.reduce((sum, item) => sum + Number(item?.savedCount || 0), 0),
    [wishlistLeadRows]
  );

  const orderProcessingRows = useMemo(
    () => filteredAdminOrders.filter((item) => !["delivered", "cancelled"].includes(String(item?.status || "").toLowerCase())).slice(0, 8),
    [filteredAdminOrders]
  );

  const orderProcessingCounts = useMemo(() => ({
    paidQueue: filteredAdminOrders.filter((item) => ["paid", "pending", "processing", "confirmed", "packed"].includes(String(item?.status || "").toLowerCase())).length,
    inTransit: filteredAdminOrders.filter((item) => String(item?.status || "").toLowerCase() === "in_transit").length,
    completed: filteredAdminOrders.filter((item) => String(item?.status || "").toLowerCase() === "delivered").length
  }), [filteredAdminOrders]);

  const orderProcessingValue = useMemo(
    () => orderProcessingRows.reduce((sum, item) => sum + resolveOrderValue(item), 0),
    [orderProcessingRows, resolveOrderValue]
  );

  const afterSalesRows = useMemo(
    () => filteredAdminOrders.filter((item) => ["cancelled", "returned", "refunded", "after_sales"].includes(String(item?.status || "").toLowerCase())).slice(0, 8),
    [filteredAdminOrders]
  );

  const afterSalesValue = useMemo(
    () => afterSalesRows.reduce((sum, item) => sum + resolveOrderValue(item), 0),
    [afterSalesRows, resolveOrderValue]
  );

  const serviceSignalRows = useMemo(
    () => adminInquiries.filter((item) => !Boolean(item?.isRead)).slice(0, 6),
    [adminInquiries]
  );

  const getFulfillmentNextAction = useCallback((status) => {
    const normalized = String(status || "").toLowerCase();
    if (["paid", "pending", "processing", "confirmed"].includes(normalized)) {
      return locale === "zh" ? "确认拣货与打包" : "Confirm picking and packing";
    }
    if (normalized === "packed") {
      return locale === "zh" ? "安排发运并回填单号" : "Arrange shipment and update tracking";
    }
    if (normalized === "in_transit") {
      return locale === "zh" ? "跟进在途节点与异常" : "Monitor transit milestones and exceptions";
    }
    return locale === "zh" ? "检查订单状态" : "Review order state";
  }, [locale]);

  const getAfterSalesNextAction = useCallback((status) => {
    const normalized = String(status || "").toLowerCase();
    if (["cancelled", "refunded"].includes(normalized)) {
      return locale === "zh" ? "确认退款与客户解释" : "Confirm refund and customer explanation";
    }
    if (["returned", "after_sales"].includes(normalized)) {
      return locale === "zh" ? "跟进回收与责任判定" : "Track return handling and ownership";
    }
    return locale === "zh" ? "登记售后动作" : "Log follow-up action";
  }, [locale]);

  const workTargetLaneRows = useMemo(() => {
    const revenueValue = Number(scopedWorkspaceMetrics?.revenueUSD ?? adminOverview?.revenueUSD ?? 0) || 0;
    const definitions = [
      {
        key: "products",
        label: texts.products,
        target: locale === "zh" ? "稳定商品资料与主推清单" : "Keep product data and featured assortments stable",
        signal: locale === "zh" ? `${filteredAdminProducts.length} 个商品` : `${filteredAdminProducts.length} products`
      },
      {
        key: "orders",
        label: texts.orders,
        target: locale === "zh" ? "压缩待履约和在途异常" : "Reduce pending fulfillment and transit exceptions",
        signal: locale === "zh" ? `${filteredAdminOrders.length} 条订单` : `${filteredAdminOrders.length} orders`
      },
      {
        key: "customers",
        label: texts.customers,
        target: locale === "zh" ? "优先识别高意向与复购客户" : "Identify high-intent and repeat-buy customers first",
        signal: locale === "zh" ? `${filteredAdminUsers.length} 位客户` : `${filteredAdminUsers.length} customers`
      },
      {
        key: "inquiry",
        label: texts.inquiry,
        target: locale === "zh" ? "保持询盘响应与批量处理节奏" : "Keep inquiry response and batch handling on rhythm",
        signal: locale === "zh" ? `${adminInquiryPagination.totalCount || adminInquiries.length} 条询盘` : `${adminInquiryPagination.totalCount || adminInquiries.length} inquiries`
      },
      {
        key: "marketing",
        label: texts.marketing,
        target: locale === "zh" ? "统一活动素材与流量入口" : "Align campaign assets and traffic entries",
        signal: locale === "zh" ? `${operationsBanners.length} 个运营入口` : `${operationsBanners.length} campaign entries`
      },
      {
        key: "analytics",
        label: texts.analytics,
        target: locale === "zh" ? "用经营数据校准推进顺序" : "Use business signals to rank execution priorities",
        signal: formatUsdAmount(revenueValue)
      },
      {
        key: "settings",
        label: texts.settings,
        target: locale === "zh" ? "保证配置与运行状态可回看" : "Keep config and runtime state reviewable",
        signal: locale === "zh" ? `${positionRows.length} 个维护对象` : `${positionRows.length} maintenance rows`
      },
      {
        key: "procurement",
        label: texts.procurement,
        target: locale === "zh" ? "推进采购准备与成本确认" : "Advance sourcing readiness and cost confirmation",
        signal: locale === "zh" ? `${procurementQueueRows.length} 条采购协同` : `${procurementQueueRows.length} sourcing items`
      },
      {
        key: "solutions",
        label: texts.quality,
        target: locale === "zh" ? "压缩质检阻塞与方案确认" : "Compress QA blockers and solution confirmation",
        signal: locale === "zh" ? `${qualityQueueRows.length} 条质检任务` : `${qualityQueueRows.length} QA items`
      },
      {
        key: "logistics",
        label: texts.logistics,
        target: locale === "zh" ? "同步发运准备与物流追踪" : "Keep shipment readiness and logistics tracking aligned",
        signal: locale === "zh" ? `${logisticsQueueRows.length} 条物流任务` : `${logisticsQueueRows.length} logistics items`
      }
    ];

    return definitions.filter((item) => allowedTabs.includes(item.key)).slice(0, 6);
  }, [
    adminInquiries.length,
    adminInquiryPagination.totalCount,
    adminOverview?.revenueUSD,
    allowedTabs,
    filteredAdminOrders.length,
    filteredAdminProducts.length,
    filteredAdminUsers.length,
    formatUsdAmount,
    locale,
    logisticsQueueRows.length,
    operationsBanners.length,
    positionRows.length,
    procurementQueueRows.length,
    qualityQueueRows.length,
    scopedWorkspaceMetrics?.revenueUSD,
    texts
  ]);

  const workTargetChecklistRows = useMemo(() => {
    if (Array.isArray(operationsChecklist) && operationsChecklist.length) {
      return operationsChecklist.slice(0, 6).map((item, index) => ({
        key: String(item?.key || item?.id || `checklist-${index + 1}`),
        title: String(item?.title || item?.label || item?.name || (locale === "zh" ? `任务 ${index + 1}` : `Task ${index + 1}`)),
        detail: String(item?.description || item?.detail || item?.note || (locale === "zh" ? "纳入本周目标追踪。" : "Included in this week's target tracking."))
      }));
    }

    return workTargetLaneRows.map((item) => ({
      key: item.key,
      title: item.label,
      detail: item.target
    }));
  }, [locale, operationsChecklist, workTargetLaneRows]);

  const workProcessingPanel = scopedPositionKey === "sales-executive" && scopedWorkspace ? (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "销售执行视图" : "Sales Execution Workspace"}
      title={locale === "zh" ? "业务跟进清单" : "Sales Follow-up Queue"}
      description={locale === "zh" ? "用统一视觉查看业务待跟进队列。" : "Review sales follow-up queues in the shared visual shell."}
      metrics={[
        { title: locale === "zh" ? "待跟进" : "Follow-ups", value: salesFollowUpQueue.length, detail: locale === "zh" ? "当前业务队列条目" : "Current queue rows", tone: "blue" }
      ]}
    >
      <section className="admin-ops-surface">
        <div className="table-wrap admin-ops-table-scroll">
          <table className="controlhub-table">
            <thead>
              <tr>
                <th>{texts.orderId}</th>
                <th>{texts.userId}</th>
                <th>{texts.status}</th>
                <th>{locale === "zh" ? "下一动作" : "Next Action"}</th>
              </tr>
            </thead>
            <tbody>
              {salesFollowUpQueue.map((item) => (
                <tr key={`sales-follow-${item.id}`}>
                  <td>{item.id}</td>
                  <td>{item.userId}</td>
                  <td>{item.status}</td>
                  <td>{item.nextAction}</td>
                </tr>
              ))}
              {!salesFollowUpQueue.length ? <tr><td colSpan="4">{texts.none}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </AdminOpsShell>
  ) : scopedPositionKey === "hr-admin-specialist" && scopedWorkspace ? (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "人力任务视图" : "HR Task Workspace"}
      title={locale === "zh" ? "人员任务板" : "Staff Task Board"}
      description={locale === "zh" ? "把人力任务与入岗进度收进统一工作台。" : "Bring HR tasks and onboarding progress into one shared workspace."}
      metrics={[
        { title: locale === "zh" ? "任务条目" : "Task Rows", value: hrTaskBoard.length, detail: locale === "zh" ? "当前任务板记录" : "Current task board rows", tone: "blue" }
      ]}
    >
      <section className="admin-ops-surface">
        <div className="table-wrap admin-ops-table-scroll">
          <table className="controlhub-table">
            <thead>
              <tr>
                <th>{texts.nickname}</th>
                <th>{texts.userRole}</th>
                <th>{texts.position}</th>
                <th>{locale === "zh" ? "状态" : "Status"}</th>
                <th>{locale === "zh" ? "入岗阶段" : "Onboarding"}</th>
              </tr>
            </thead>
            <tbody>
              {hrTaskBoard.map((item) => (
                <tr key={`hr-task-${item.id}`}>
                  <td>{item.name || item.id}</td>
                  <td>{item.role}</td>
                  <td>{item.jobTitle || "-"}</td>
                  <td>{item.status}</td>
                  <td>{item.onboardingStage}</td>
                </tr>
              ))}
              {!hrTaskBoard.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>
    </AdminOpsShell>
  ) : (
    <AdminOpsShell
      eyebrow={locale === "zh" ? "进度视图" : "Progress Workspace"}
      title={texts.workProcessing}
      description={locale === "zh" ? "把当前岗位的跟进队列、任务板和执行顺序整理成一张进度面板。" : "Turn follow-up queues, task boards, and execution order into a concrete progress panel."}
      metrics={[
        { title: locale === "zh" ? "跟进队列" : "Follow-up Queue", value: salesFollowUpQueue.length, detail: locale === "zh" ? "当前待推进业务条目" : "Current business rows needing action", tone: "blue" },
        { title: locale === "zh" ? "任务板" : "Task Board", value: hrTaskBoard.length, detail: locale === "zh" ? "可复用的人力任务条目" : "Reusable HR task rows", tone: "violet" },
        { title: locale === "zh" ? "执行清单" : "Execution Checklist", value: workTargetChecklistRows.length, detail: locale === "zh" ? "本周建议优先动作" : "Recommended actions for this week", tone: "amber" }
      ]}
    >
      <section className="admin-ops-section-grid">
        <div className="admin-ops-surface">
          <div className="admin-ops-surface-head">
            <div>
              <h4>{locale === "zh" ? "当前推进队列" : "Current Progress Queue"}</h4>
              <p>{locale === "zh" ? "没有专属岗位数据时，先用现有业务跟进和人力任务信号组成统一进度视图。" : "When a dedicated role dataset is unavailable, use business follow-up and HR task signals as a shared progress view."}</p>
            </div>
          </div>
          <div className="table-wrap admin-ops-table-scroll">
            <table className="controlhub-table">
              <thead>
                <tr>
                  <th>{locale === "zh" ? "来源" : "Source"}</th>
                  <th>{locale === "zh" ? "对象" : "Target"}</th>
                  <th>{locale === "zh" ? "当前状态" : "Current State"}</th>
                  <th>{locale === "zh" ? "下一动作" : "Next Action"}</th>
                </tr>
              </thead>
              <tbody>
                {salesFollowUpQueue.slice(0, 4).map((item) => (
                  <tr key={`generic-progress-sales-${item.id}`}>
                    <td>{locale === "zh" ? "业务跟进" : "Sales Follow-up"}</td>
                    <td>{item.id || "-"}</td>
                    <td>{item.status || "-"}</td>
                    <td>{item.nextAction || (locale === "zh" ? "继续跟进" : "Continue follow-up")}</td>
                  </tr>
                ))}
                {hrTaskBoard.slice(0, 4).map((item) => (
                  <tr key={`generic-progress-hr-${item.id}`}>
                    <td>{locale === "zh" ? "任务板" : "Task Board"}</td>
                    <td>{item.name || item.id || "-"}</td>
                    <td>{item.status || item.onboardingStage || "-"}</td>
                    <td>{item.onboardingStage || (locale === "zh" ? "核对进度" : "Review progress")}</td>
                  </tr>
                ))}
                {!salesFollowUpQueue.length && !hrTaskBoard.length ? <tr><td colSpan="4">{texts.none}</td></tr> : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-ops-surface">
          <h5>{locale === "zh" ? "执行顺序建议" : "Suggested Execution Order"}</h5>
          <div style={{ display: "grid", gap: 12 }}>
            {workTargetChecklistRows.slice(0, 4).map((item) => (
              <div key={`generic-progress-check-${item.key}`} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                <strong>{item.title}</strong>
                <p style={{ margin: "6px 0 0", color: "#475569" }}>{item.detail}</p>
              </div>
            ))}
            {!workTargetChecklistRows.length ? <p>{texts.none}</p> : null}
          </div>
        </div>
      </section>
    </AdminOpsShell>
  );

  if (adminTab === "home") {
    return homeDashboard;
  }

  return (
    <>
      <AdminDashboardShell
        adminRole={adminRole}
        profileData={profileData}
        onNavigate={switchMenu}
        onLogout={onLogout}
        onChangeLocale={onChangeLocale}
        locale={locale}
        activeTab={adminTab}
        title={shellMeta.title}
        subtitle={shellMeta.subtitle}
        brandCard={{
          adminTitle: storeSettingsDraft.adminTitle,
          logoUrl: storeSettingsDraft.logoUrl,
          logoAlt: storeSettingsDraft.logoAlt
        }}
        searchValue={adminQuery}
        onSearchChange={setAdminQuery}
        quickActions={shellQuickActions}
        notificationButton={{
          label: locale === "zh" ? "打开系统通知面板" : "Open system notifications",
          count: systemNotificationCount,
          onClick: openSystemNotifications,
          disabled: !systemNotificationCount
        }}
        messageButton={{
          label: locale === "zh" ? "打开询盘与即时消息面板" : "Open inquiry and instant-message panel",
          count: inquiryMessageCount,
          onClick: openInquiryWorkspace,
          disabled: !allowedTabs.includes("inquiry")
        }}
        allowedTabs={allowedTabs}
      >
        {adminToast ? <div className="controlhub-top-toast">{adminToast}</div> : null}

          {adminTab === "notifications" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "通知管理中心" : "Notification Management Center"}
              title={locale === "zh" ? "通知管理" : "Notification Management"}
              description={locale === "zh"
                ? "在这里发布、修改、删除通知，并统一维护通知接收范围。"
                : "Publish, edit, delete, and manage notification audiences from one workspace."}
              metrics={[
                {
                  title: locale === "zh" ? "通知池" : "Notification Pool",
                  value: adminManagedNotifications.length,
                  detail: locale === "zh" ? "当前已发布通知数量" : "Currently published notifications",
                  tone: "blue"
                },
                {
                  title: locale === "zh" ? "未读通知" : "Unread Notifications",
                  value: Number(notificationSummary?.unreadNotificationCount || 0),
                  detail: locale === "zh" ? "来自数据库通知接收状态" : "Backed by notification recipient read state",
                  tone: "violet"
                },
                {
                  title: locale === "zh" ? "未读接收压力" : "Unread Pressure",
                  value: adminManagedNotifications.reduce((sum, item) => sum + Number(item?.unreadRecipientCount || 0), 0),
                  detail: locale === "zh" ? "统计全部通知的未读接收人" : "Unread recipients across the published pool",
                  tone: "amber"
                }
              ]}
            >
              <NotificationManagementPanel
                locale={locale}
                viewMode="manage"
                canManageNotifications={canManageNotifications}
                notificationDraft={adminNotificationDraft}
                onChangeNotificationDraft={handleNotificationDraftChange}
                notificationLoading={adminNotificationLoading}
                notificationSaving={adminNotificationSaving}
                notificationNotice={adminNotificationNotice}
                editingNotificationId={editingAdminNotificationId}
                managedNotifications={adminManagedNotifications}
                teamOptions={adminUsers}
                onStartEditNotification={startEditNotification}
                onCancelEditNotification={resetNotificationEditor}
                onSubmitNotification={submitNotification}
                onDeleteNotification={deleteNotification}
                onRefreshNotifications={fetchManagedNotifications}
              />
            </AdminOpsShell>
          ) : null}

          {adminTab === "notificationPlan" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "通知节奏视图" : "Notification Cadence Workspace"}
              title={locale === "zh" ? "通知计划" : "Notification Plan"}
              description={locale === "zh"
                ? "把当前通知池整理成覆盖范围、未读压力和发布节奏的统一面板。"
                : "Organize the current notification pool into one cadence, coverage, and unread-pressure workspace."}
              metrics={[
                {
                  title: locale === "zh" ? "当前通知" : "Current Notifications",
                  value: adminManagedNotifications.length,
                  detail: locale === "zh" ? "来自通知管理池" : "From the managed notification pool",
                  tone: "blue"
                },
                {
                  title: locale === "zh" ? "定向通知" : "Direct Targeting",
                  value: adminManagedNotifications.filter((item) => item.targetType === "users").length,
                  detail: locale === "zh" ? "针对具体人员的通知数" : "Notifications aimed at specific users",
                  tone: "violet"
                },
                {
                  title: locale === "zh" ? "未读通知" : "Unread Notifications",
                  value: Number(notificationSummary?.unreadNotificationCount || 0),
                  detail: locale === "zh" ? "当前账号仍未读的通知数" : "Unread notifications for the current account",
                  tone: "amber"
                }
              ]}
            >
              <NotificationManagementPanel
                locale={locale}
                viewMode="plan"
                canManageNotifications={canManageNotifications}
                notificationDraft={adminNotificationDraft}
                onChangeNotificationDraft={handleNotificationDraftChange}
                notificationLoading={adminNotificationLoading}
                notificationSaving={adminNotificationSaving}
                notificationNotice={adminNotificationNotice}
                editingNotificationId={editingAdminNotificationId}
                managedNotifications={adminManagedNotifications}
                teamOptions={adminUsers}
                onStartEditNotification={startEditNotification}
                onCancelEditNotification={resetNotificationEditor}
                onSubmitNotification={submitNotification}
                onDeleteNotification={deleteNotification}
                onRefreshNotifications={fetchManagedNotifications}
              />
            </AdminOpsShell>
          ) : null}

          {adminTab === "home" ? (
            <AdminDashboardHomeWorkspaces
              locale={locale}
              texts={texts}
              scopedPositionKey={scopedPositionKey}
              scopedWorkspace={scopedWorkspace}
              scopedWorkspaceMetrics={scopedWorkspaceMetrics}
              operationsChecklist={operationsChecklist}
              salesFollowUpQueue={salesFollowUpQueue}
              hrTaskBoard={hrTaskBoard}
              adminOverview={adminOverview}
              adminOrders={adminOrders}
              allowedTabs={allowedTabs}
              openCreateProductEditor={openCreateProductEditor}
            />
          ) : null}

          {adminTab === "profile" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "账户中心" : "Account Center"}
              title={locale === "zh" ? "个人资料" : "Profile"}
              description={locale === "zh" ? "把账号基础信息纳入统一后台视觉，便于快速确认身份与权限。" : "Bring account identity details into the shared admin visual shell for faster review."}
              metrics={[
                { title: texts.profileRole, value: profileRoleLabel, detail: locale === "zh" ? "当前管理角色" : "Current management role", tone: "blue" },
                { title: texts.profileName, value: profileDisplayName, detail: locale === "zh" ? "当前展示名称" : "Current display name", tone: "violet" },
                { title: texts.accountId, value: profileData?.id || "-", detail: locale === "zh" ? "账号唯一标识" : "Account identifier", tone: "emerald" }
              ]}
            >
            <section className="admin-ops-surface controlhub-card controlhub-wide-card controlhub-profile-panel">
              <div className="controlhub-profile-panel-head">
                <div className="controlhub-profile-panel-avatar" aria-hidden="true">{profileAvatarText}</div>
                <div className="controlhub-profile-panel-meta">
                  <h4>{texts.profileCardTitle}</h4>
                  <p>{texts.profileRole}: {profileRoleLabel}</p>
                </div>
              </div>
              <div className="controlhub-profile-panel-grid">
                <div className="controlhub-profile-panel-item">
                  <span>{texts.accountId}</span>
                  <strong>{profileData?.id || "-"}</strong>
                </div>
                <div className="controlhub-profile-panel-item">
                  <span>{texts.profileName}</span>
                  <strong>{profileDisplayName}</strong>
                </div>
                <div className="controlhub-profile-panel-item">
                  <span>{texts.profileEmail}</span>
                  <strong>{profileData?.email || "-"}</strong>
                </div>
                <div className="controlhub-profile-panel-item">
                  <span>{texts.profileRole}</span>
                  <strong>{profileRoleLabel}</strong>
                </div>
              </div>
            </section>
            </AdminOpsShell>
          ) : null}

          {adminTab === "password" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "安全中心" : "Security Center"}
              title={locale === "zh" ? "密码安全" : "Password Security"}
              description={locale === "zh" ? "统一维护管理员登录密码与账户安全设置。" : "Maintain admin sign-in password and account security settings in the shared shell."}
              metrics={[
                { title: locale === "zh" ? "安全项" : "Security Items", value: 3, detail: locale === "zh" ? "当前需要确认的密码字段" : "Password fields to confirm", tone: "blue" },
                { title: locale === "zh" ? "提交状态" : "Submit Status", value: passwordSaving ? (locale === "zh" ? "保存中" : "Saving") : (locale === "zh" ? "待提交" : "Ready"), detail: locale === "zh" ? "当前表单处理状态" : "Current form processing state", tone: "amber" },
                { title: locale === "zh" ? "账号角色" : "Role", value: profileRoleLabel, detail: locale === "zh" ? "当前账户上下文" : "Current account context", tone: "emerald" }
              ]}
            >
            <section className="admin-ops-surface controlhub-card controlhub-wide-card">
              <h4>{texts.passwordCardTitle}</h4>
              <form className="stacked-form" onSubmit={submitPasswordChange}>
                <label>
                  <span>{texts.currentPassword}</span>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={(event) =>
                      setPasswordForm((prev) => ({ ...prev, currentPassword: event.target.value }))
                    }
                    required
                  />
                </label>
                <label>
                  <span>{texts.newPassword}</span>
                  <input
                    type="password"
                    value={passwordForm.newPassword}
                    onChange={(event) =>
                      setPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))
                    }
                    required
                  />
                </label>
                <label>
                  <span>{texts.confirmPassword}</span>
                  <input
                    type="password"
                    value={passwordForm.confirmPassword}
                    onChange={(event) =>
                      setPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                    }
                    required
                  />
                </label>
                <div className="row-actions">
                  <button type="submit" className="controlhub-button" disabled={passwordSaving}>
                    {texts.savePassword}
                  </button>
                </div>
              </form>
              {passwordNotice ? <p className="state">{passwordNotice}</p> : null}
            </section>
            </AdminOpsShell>
          ) : null}

          {adminTab === "inquiry" ? (
            <AdminInquiryHubPage
              locale={locale}
              selectedInquiryCount={selectedInquiryCount}
              inquirySummary={{
                pageCount: adminInquiries.length,
                totalCount: adminInquiryPagination.totalCount || 0
              }}
              toolbar={(
                <div className="row-actions">
                  <button
                    type="button"
                    className="controlhub-button"
                    onClick={() => {
                      setAdminInquiryPage(1);
                      void fetchAdminInquiries(1);
                    }}
                    disabled={adminInquiryLoading || adminInquiryActionLoading}
                  >
                    {locale === "zh" ? "刷新询盘" : "Refresh Inquiries"}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={() => void updateSelectedInquiriesReadState(true)}
                    disabled={!selectedInquiryCount || adminInquiryLoading || adminInquiryActionLoading}
                  >
                    {locale === "zh" ? "标记已读" : "Mark Read"}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={() => void updateSelectedInquiriesReadState(false)}
                    disabled={!selectedInquiryCount || adminInquiryLoading || adminInquiryActionLoading}
                  >
                    {locale === "zh" ? "标记未读" : "Mark Unread"}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={() => void deleteSelectedInquiries()}
                    disabled={!selectedInquiryCount || adminInquiryLoading || adminInquiryActionLoading}
                  >
                    {locale === "zh" ? "删除所选" : "Delete Selected"}
                  </button>
                  <button type="button" className="controlhub-button ghost" onClick={() => window.open("/contact", "_blank", "noopener,noreferrer")}>
                    {texts.openInquiryCenter}
                  </button>
                  <label className="small muted" style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                    <span>{locale === "zh" ? "每页" : "Page Size"}</span>
                    <select
                      value={adminInquiryPagination.pageSize || 20}
                      onChange={(event) => {
                        const nextSize = Number(event.target.value || 20);
                        setAdminInquiryPagination((prev) => ({
                          ...prev,
                          pageSize: Number.isFinite(nextSize) && nextSize > 0 ? nextSize : 20
                        }));
                        setAdminInquiryPage(1);
                      }}
                      disabled={adminInquiryLoading || adminInquiryActionLoading}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </label>
                </div>
              )}
              table={(
                <>
                  <div className="table-wrap">
                    <table className="controlhub-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={allCurrentInquiriesSelected}
                              onChange={(event) => toggleAllCurrentInquiries(event.target.checked)}
                              disabled={!currentInquiryIds.length || adminInquiryLoading || adminInquiryActionLoading}
                            />
                          </th>
                          <th>{locale === "zh" ? "提交时间" : "Submitted At"}</th>
                          <th>{locale === "zh" ? "联系人" : "Contact"}</th>
                          <th>{locale === "zh" ? "产品" : "Product"}</th>
                          <th>{locale === "zh" ? "内容" : "Message"}</th>
                          <th>{locale === "zh" ? "参考文件" : "Reference File"}</th>
                          <th>{locale === "zh" ? "来源页" : "Source Page"}</th>
                          <th>{locale === "zh" ? "来源IP" : "IP"}</th>
                          <th>{locale === "zh" ? "地址" : "Location"}</th>
                          <th>{locale === "zh" ? "操作" : "Actions"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adminInquiries.map((item) => {
                          const inquiryId = Number(item?.id || 0);
                          const isChecked = inquirySelectedIdSet.has(inquiryId);
                          const isRead = Boolean(item?.isRead);
                          const locationLabel = [item?.ipCountry, item?.ipRegion, item?.ipCity]
                            .map((segment) => String(segment || "").trim())
                            .filter(Boolean)
                            .join(" / ") || "-";
                          const submittedAtLabel = item?.createdAt
                            ? new Date(Number(item.createdAt) * 1000).toLocaleString()
                            : "-";
                          const productTitle = String(item?.inquiryProduct || "-");
                          const quantityLabel = locale === "zh" ? "数量" : "Qty";
                          const companyLabel = locale === "zh" ? "公司" : "Company";
                          const quantityValue = String(item?.inquiryQuantity || "-");
                          const companyValue = String(item?.contactCompany || "-");
                          const productPreviewText = `${productTitle}\n${quantityLabel}: ${quantityValue}`;
                          const productHoverText = `${productTitle}\n${quantityLabel}: ${quantityValue}\n${companyLabel}: ${companyValue}`;
                          return (
                            <tr
                              key={`inquiry-${item?.id || item?.createdAt || item?.contactEmail || "row"}`}
                              style={isRead ? { color: "#8f95a3", fontWeight: 400 } : { fontWeight: 700 }}
                            >
                              <td>
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(event) => toggleInquirySelection(inquiryId, event.target.checked)}
                                  disabled={adminInquiryLoading || adminInquiryActionLoading || !inquiryId}
                                />
                              </td>
                              <td>{submittedAtLabel}</td>
                              <td>
                                <strong>{item?.contactName || "-"}</strong>
                                <div className="small muted">{item?.contactEmail || "-"}</div>
                                {item?.contactWhatsapp ? <div className="small muted">WhatsApp: {item.contactWhatsapp}</div> : null}
                              </td>
                              <td style={{ maxWidth: "240px" }}>
                                <div
                                  title={productHoverText}
                                  style={{
                                    display: "-webkit-box",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    whiteSpace: "pre-line",
                                    lineHeight: 1.4
                                  }}
                                >
                                  {productPreviewText}
                                </div>
                              </td>
                              <td style={{ maxWidth: "360px" }}>
                                <div
                                  title={item?.message || "-"}
                                  style={{
                                    overflow: "hidden",
                                    whiteSpace: "nowrap",
                                    textOverflow: "ellipsis"
                                  }}
                                >
                                  {item?.message || "-"}
                                </div>
                              </td>
                              <td style={{ maxWidth: "220px", wordBreak: "break-all" }}>{item?.referenceFileName || "-"}</td>
                              <td style={{ maxWidth: "260px", wordBreak: "break-all" }}>{item?.sourcePage || "-"}</td>
                              <td>{item?.ipAddress || "-"}</td>
                              <td>{locationLabel}</td>
                              <td>
                                <button
                                  type="button"
                                  className="controlhub-button ghost"
                                  onClick={() => setAdminInquiryDetail(item || null)}
                                >
                                  {locale === "zh" ? "详细" : "Detail"}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {!adminInquiries.length && !adminInquiryLoading ? (
                          <tr>
                            <td colSpan={10}>{locale === "zh" ? "暂无询盘数据" : "No inquiries yet"}</td>
                          </tr>
                        ) : null}
                        {adminInquiryLoading ? (
                          <tr>
                            <td colSpan={10}>{locale === "zh" ? "加载中..." : "Loading..."}</td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                  <div className="row-actions" style={{ justifyContent: "space-between" }}>
                    <span className="small muted">
                      {locale === "zh"
                        ? `共 ${adminInquiryPagination.totalCount || 0} 条，已选 ${selectedInquiryCount} 条`
                        : `Total ${adminInquiryPagination.totalCount || 0}, selected ${selectedInquiryCount}`}
                    </span>
                    <div className="row-actions">
                      <button
                        type="button"
                        className="controlhub-button ghost"
                        onClick={() => setAdminInquiryPage((prev) => Math.max(1, prev - 1))}
                        disabled={adminInquiryPage <= 1 || adminInquiryLoading || adminInquiryActionLoading}
                      >
                        {locale === "zh" ? "上一页" : "Prev"}
                      </button>
                      <span className="small muted">
                        {locale === "zh"
                          ? `第 ${adminInquiryPagination.page || 1} / ${adminInquiryPagination.totalPages || 1} 页`
                          : `Page ${adminInquiryPagination.page || 1} / ${adminInquiryPagination.totalPages || 1}`}
                      </span>
                      <button
                        type="button"
                        className="controlhub-button ghost"
                        onClick={() => setAdminInquiryPage((prev) => Math.min(adminInquiryPagination.totalPages || 1, prev + 1))}
                        disabled={adminInquiryPage >= (adminInquiryPagination.totalPages || 1) || adminInquiryLoading || adminInquiryActionLoading}
                      >
                        {locale === "zh" ? "下一页" : "Next"}
                      </button>
                    </div>
                  </div>
                </>
              )}
            />
          ) : null}

          {adminInquiryDetail ? (
            <div
              className="controlhub-user-password-overlay"
              role="dialog"
              aria-modal="true"
              onClick={() => setAdminInquiryDetail(null)}
            >
              <div className="controlhub-user-password-modal" onClick={(event) => event.stopPropagation()}>
                <div className="controlhub-user-password-head">
                  <h3>{locale === "zh" ? "询盘详情" : "Inquiry Detail"}</h3>
                  <button
                    type="button"
                    className="controlhub-user-password-close"
                    onClick={() => setAdminInquiryDetail(null)}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ display: "grid", gap: "8px" }}>
                  <p><strong>ID:</strong> {adminInquiryDetail?.id || "-"}</p>
                  <p><strong>{locale === "zh" ? "提交时间" : "Submitted At"}:</strong> {adminInquiryDetail?.createdAt ? new Date(Number(adminInquiryDetail.createdAt) * 1000).toLocaleString() : "-"}</p>
                  <p><strong>{locale === "zh" ? "联系人" : "Contact"}:</strong> {adminInquiryDetail?.contactName || "-"}</p>
                  <p><strong>Email:</strong> {adminInquiryDetail?.contactEmail || "-"}</p>
                  <p><strong>WhatsApp:</strong> {adminInquiryDetail?.contactWhatsapp || "-"}</p>
                  <p><strong>{locale === "zh" ? "公司" : "Company"}:</strong> {adminInquiryDetail?.contactCompany || "-"}</p>
                  <p><strong>{locale === "zh" ? "产品" : "Product"}:</strong> {adminInquiryDetail?.inquiryProduct || "-"}</p>
                  <p><strong>{locale === "zh" ? "数量" : "Quantity"}:</strong> {adminInquiryDetail?.inquiryQuantity || "-"}</p>
                  <p><strong>{locale === "zh" ? "参考文件" : "Reference File"}:</strong> {adminInquiryDetail?.referenceFileName || "-"}</p>
                  <p><strong>{locale === "zh" ? "来源页" : "Source Page"}:</strong> {adminInquiryDetail?.sourcePage || "-"}</p>
                  <p><strong>{locale === "zh" ? "来源IP" : "IP"}:</strong> {adminInquiryDetail?.ipAddress || "-"}</p>
                  <p>
                    <strong>{locale === "zh" ? "地址" : "Location"}:</strong> {[adminInquiryDetail?.ipCountry, adminInquiryDetail?.ipRegion, adminInquiryDetail?.ipCity]
                      .map((segment) => String(segment || "").trim())
                      .filter(Boolean)
                      .join(" / ") || "-"}
                  </p>
                  <div>
                    <strong>{locale === "zh" ? "询盘内容" : "Message"}:</strong>
                    <pre style={{ marginTop: "6px", whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                      {adminInquiryDetail?.message || "-"}
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          {adminTab === "wishlist" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "客户意向视图" : "Intent Workspace"}
              title={texts.wishlist}
              description={locale === "zh" ? "把收藏、购物车和近期活跃信号整理成一张可执行的意向跟进面板。" : "Turn saved-item, cart, and recent-activity signals into an actionable intent follow-up panel."}
              metrics={[
                { title: locale === "zh" ? "高意向客户" : "Intent Accounts", value: wishlistLeadRows.length, detail: locale === "zh" ? "当前可跟进的客户样本" : "Customers currently worth follow-up", tone: "blue" },
                { title: locale === "zh" ? "收藏信号" : "Saved Signals", value: wishlistTotalSaved, detail: locale === "zh" ? "收藏/购物车等意向累计" : "Saved or cart-like interest signals", tone: "violet" },
                { title: locale === "zh" ? "未读询盘" : "Unread Inquiries", value: unreadInquiryCount, detail: locale === "zh" ? "可与意向客户联动排查" : "Can be paired with intent follow-up", tone: "amber" }
              ]}
            >
              <section className="admin-ops-section-grid">
                <div className="admin-ops-surface">
                  <div className="admin-ops-surface-head">
                    <div>
                      <h4>{locale === "zh" ? "高意向客户队列" : "High-intent Customer Queue"}</h4>
                      <p>{locale === "zh" ? "优先看收藏/购物车信号高、且近期仍有活跃痕迹的客户。" : "Prioritize customers with stronger saved/cart signals and recent activity."}</p>
                    </div>
                  </div>
                  <div className="table-wrap admin-ops-table-scroll">
                    <table className="controlhub-table">
                      <thead>
                        <tr>
                          <th>{locale === "zh" ? "客户" : "Customer"}</th>
                          <th>{texts.userRole}</th>
                          <th>{locale === "zh" ? "意向信号" : "Intent Signal"}</th>
                          <th>{locale === "zh" ? "订单基线" : "Order Baseline"}</th>
                          <th>{texts.lastActiveAt}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wishlistLeadRows.map((item) => (
                          <tr key={`wishlist-lead-${item.id || item.email || item.username}`}>
                            <td>
                              <strong>{item.name || item.username || item.email || "-"}</strong>
                              <div className="small muted">{item.email || item.phone || "-"}</div>
                            </td>
                            <td>{roleLabels[item.role] || item.role || "-"}</td>
                            <td>{locale === "zh" ? `收藏/购物车 ${item.savedCount}` : `Saved/Cart ${item.savedCount}`}</td>
                            <td>{locale === "zh" ? `${item.orderCount} 笔历史订单` : `${item.orderCount} prior orders`}</td>
                            <td>{formatUserTime(item.lastActiveAt)}</td>
                          </tr>
                        ))}
                        {!wishlistLeadRows.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-ops-surface">
                  <h5>{locale === "zh" ? "转化建议" : "Conversion Suggestions"}</h5>
                  <div style={{ display: "grid", gap: 12 }}>
                    {wishlistLeadRows.slice(0, 4).map((item) => {
                      const suggestion = item.savedCount > 0 && item.orderCount === 0
                        ? (locale === "zh" ? "优先补一轮报价或样品邀约。" : "Start with a quote or sample invitation.")
                        : item.orderCount > 0
                          ? (locale === "zh" ? "适合推进组合推荐和复购提醒。" : "Good candidate for bundle offers and repeat-buy reminders.")
                          : (locale === "zh" ? "先确认最近浏览或收藏方向。" : "Confirm the most recent saved-item direction first.");

                      return (
                        <div key={`wishlist-action-${item.id || item.email || item.username}`} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                          <strong>{item.name || item.username || item.email || "-"}</strong>
                          <p style={{ margin: "6px 0 0", color: "#475569" }}>{suggestion}</p>
                        </div>
                      );
                    })}
                    {!wishlistLeadRows.length ? <p>{locale === "zh" ? "当前暂无高意向客户信号，可先从客户管理页建立分层。" : "No strong intent signals yet. Start by segmenting customers from the customer workspace."}</p> : null}
                  </div>
                </div>
              </section>
            </AdminOpsShell>
          ) : null}

          {adminTab === "orderProcessing" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "履约处理视图" : "Fulfillment Workspace"}
              title={texts.orderProcessing}
              description={locale === "zh" ? "按待履约、在途和已兑现三段节奏查看订单处理压力，而不是停留在空白占位页。" : "Read fulfillment pressure across pending, in-transit, and realized stages instead of landing on a blank placeholder."}
              metrics={[
                { title: locale === "zh" ? "履约队列" : "Fulfillment Queue", value: orderProcessingRows.length, detail: locale === "zh" ? "当前未完结订单" : "Open orders requiring handling", tone: "blue" },
                { title: locale === "zh" ? "在途订单" : "In Transit", value: orderProcessingCounts.inTransit, detail: locale === "zh" ? "需要跟踪的物流批次" : "Shipments currently in motion", tone: "violet" },
                { title: locale === "zh" ? "队列金额" : "Queue Value", value: formatUsdAmount(orderProcessingValue), detail: locale === "zh" ? "当前队列估算金额" : "Estimated value of the live queue", tone: "amber" }
              ]}
            >
              <section className="admin-ops-section-grid">
                <div className="admin-ops-surface">
                  <div className="admin-ops-surface-head">
                    <div>
                      <h4>{locale === "zh" ? "履约处理队列" : "Fulfillment Queue"}</h4>
                      <p>{locale === "zh" ? "把未完成订单集中成一张处理表，便于仓配与运营同步。" : "Turn open orders into one handling table for warehouse and operations sync."}</p>
                    </div>
                  </div>
                  <div className="table-wrap admin-ops-table-scroll">
                    <table className="controlhub-table">
                      <thead>
                        <tr>
                          <th>{texts.orderId}</th>
                          <th>{texts.userId}</th>
                          <th>{texts.status}</th>
                          <th>{locale === "zh" ? "金额" : "Amount"}</th>
                          <th>{locale === "zh" ? "下一动作" : "Next Action"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orderProcessingRows.map((item) => (
                          <tr key={`order-processing-${item.id}`}>
                            <td>{item.id}</td>
                            <td>{item.userId || "-"}</td>
                            <td>{statusLabels[item.status] || item.status || "-"}</td>
                            <td>{formatUsdAmount(resolveOrderValue(item))}</td>
                            <td>{getFulfillmentNextAction(item.status)}</td>
                          </tr>
                        ))}
                        {!orderProcessingRows.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-ops-surface">
                  <h5>{locale === "zh" ? "处理节奏" : "Handling Rhythm"}</h5>
                  <div style={{ display: "grid", gap: 12 }}>
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                      <strong>{locale === "zh" ? "待履约" : "Pending Fulfillment"}</strong>
                      <p style={{ margin: "6px 0 0", color: "#475569" }}>
                        {locale === "zh" ? `${orderProcessingCounts.paidQueue} 条订单需要确认拣货、打包或出运准备。` : `${orderProcessingCounts.paidQueue} orders still need picking, packing, or shipment preparation.`}
                      </p>
                    </div>
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                      <strong>{locale === "zh" ? "运输跟踪" : "Transit Tracking"}</strong>
                      <p style={{ margin: "6px 0 0", color: "#475569" }}>
                        {locale === "zh" ? `${orderProcessingCounts.inTransit} 条订单正在运输中，优先检查节点超时和异常签收。` : `${orderProcessingCounts.inTransit} orders are in transit. Prioritize timeout and delivery exceptions.`}
                      </p>
                    </div>
                    <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                      <strong>{locale === "zh" ? "已兑现基线" : "Realized Baseline"}</strong>
                      <p style={{ margin: "6px 0 0", color: "#475569" }}>
                        {locale === "zh" ? `${orderProcessingCounts.completed} 条订单已交付，可对照当前队列反推履约压力。` : `${orderProcessingCounts.completed} orders are delivered. Use that baseline to judge current queue pressure.`}
                      </p>
                    </div>
                  </div>
                </div>
              </section>
            </AdminOpsShell>
          ) : null}

          {adminTab === "afterSales" ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "售后处理视图" : "After-sales Workspace"}
              title={texts.afterSales}
              description={locale === "zh" ? "把取消单、退款回收和未读服务信号放进一页里看，方便售后和履约协同。" : "Read cancelled orders, refund recovery, and unread service signals in one page for tighter after-sales coordination."}
              metrics={[
                { title: locale === "zh" ? "售后订单" : "After-sales Orders", value: afterSalesRows.length, detail: locale === "zh" ? "取消/退款/退货订单" : "Cancelled, refunded, or returned orders", tone: "blue" },
                { title: locale === "zh" ? "未读服务信号" : "Unread Service Signals", value: unreadInquiryCount, detail: locale === "zh" ? "需人工继续跟进的外部联系" : "External contacts still needing follow-up", tone: "violet" },
                { title: locale === "zh" ? "涉险金额" : "At-risk Value", value: formatUsdAmount(afterSalesValue), detail: locale === "zh" ? "当前售后队列估算金额" : "Estimated value across the live after-sales queue", tone: "amber" }
              ]}
            >
              <section className="admin-ops-section-grid">
                <div className="admin-ops-surface">
                  <div className="admin-ops-surface-head">
                    <div>
                      <h4>{locale === "zh" ? "售后订单队列" : "After-sales Queue"}</h4>
                      <p>{locale === "zh" ? "优先处理取消、退款和退货状态的订单，避免金额和客户体验继续扩大风险。" : "Prioritize cancelled, refunded, and returned orders before risk grows in value or customer experience."}</p>
                    </div>
                  </div>
                  <div className="table-wrap admin-ops-table-scroll">
                    <table className="controlhub-table">
                      <thead>
                        <tr>
                          <th>{texts.orderId}</th>
                          <th>{texts.userId}</th>
                          <th>{texts.status}</th>
                          <th>{locale === "zh" ? "金额" : "Amount"}</th>
                          <th>{locale === "zh" ? "跟进动作" : "Follow-up"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {afterSalesRows.map((item) => (
                          <tr key={`after-sales-${item.id}`}>
                            <td>{item.id}</td>
                            <td>{item.userId || "-"}</td>
                            <td>{statusLabels[item.status] || item.status || "-"}</td>
                            <td>{formatUsdAmount(resolveOrderValue(item))}</td>
                            <td>{getAfterSalesNextAction(item.status)}</td>
                          </tr>
                        ))}
                        {!afterSalesRows.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="admin-ops-surface">
                  <h5>{locale === "zh" ? "服务信号与处置" : "Service Signals and Handling"}</h5>
                  <div style={{ display: "grid", gap: 12 }}>
                    {serviceSignalRows.map((item) => (
                      <div key={`service-signal-${item.id || item.contactEmail || item.createdAt}`} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                        <strong>{item.contactName || item.contactEmail || "-"}</strong>
                        <p style={{ margin: "6px 0 0", color: "#475569" }}>
                          {String(item.message || locale === "zh" ? "待补充服务说明。" : "Service follow-up pending.").slice(0, 120)}
                        </p>
                      </div>
                    ))}
                    {!serviceSignalRows.length ? (
                      <div style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                        <strong>{locale === "zh" ? "当前售后节奏" : "Current After-sales Rhythm"}</strong>
                        <p style={{ margin: "6px 0 0", color: "#475569" }}>
                          {locale === "zh" ? "当前没有未读服务信号，建议用取消订单队列复盘退款、补发和责任归因。" : "No unread service signals right now. Use the cancelled-order queue to review refunds, reshipments, and ownership."}
                        </p>
                      </div>
                    ) : null}
                  </div>
                </div>
              </section>
            </AdminOpsShell>
          ) : null}

          {adminTab === "workTargets" ? (
            scopedPositionKey === "hr-admin-specialist" && scopedWorkspace ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "岗位覆盖视图" : "Coverage Workspace"}
              title={locale === "zh" ? "岗位覆盖情况" : "Position Coverage"}
              description={locale === "zh" ? "用统一视觉查看岗位配置与分配覆盖。" : "Review position configuration and staffing coverage in the shared visual shell."}
              metrics={[
                { title: locale === "zh" ? "岗位数" : "Positions", value: hrPositionCoverage.length, detail: locale === "zh" ? "当前覆盖岗位模板" : "Current covered positions", tone: "blue" },
                { title: locale === "zh" ? "分配人数" : "Assigned", value: hrPositionCoverage.reduce((sum, item) => sum + Number(item.assignedCount || 0), 0), detail: locale === "zh" ? "岗位总分配人数" : "Total assigned headcount", tone: "emerald" }
              ]}
            >
            <section className="admin-ops-surface">
              <div className="table-wrap admin-ops-table-scroll">
                <table className="controlhub-table">
                  <thead>
                    <tr>
                      <th>{texts.positionName}</th>
                      <th>{texts.department}</th>
                      <th>{locale === "zh" ? "已分配人数" : "Assigned"}</th>
                      <th>{locale === "zh" ? "权限数" : "Permissions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrPositionCoverage.map((item) => (
                      <tr key={`hr-position-${item.id}`}>
                        <td>{item.name}</td>
                        <td>{item.department}</td>
                        <td>{item.assignedCount}</td>
                        <td>{item.permissionCount}</td>
                      </tr>
                    ))}
                    {!hrPositionCoverage.length ? <tr><td colSpan="4">{texts.none}</td></tr> : null}
                  </tbody>
                </table>
              </div>
            </section>
            </AdminOpsShell>
            ) : (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "目标视图" : "Target Workspace"}
              title={texts.workTargets}
              description={locale === "zh" ? "把当前岗位可操作模块、实时信号和本周动作整理成一张目标面板。" : "Turn current role lanes, live signals, and this week's actions into a concrete target dashboard."}
              metrics={[
                { title: locale === "zh" ? "目标泳道" : "Target Lanes", value: workTargetLaneRows.length, detail: locale === "zh" ? "当前岗位可直接推进的模块" : "Modules directly actionable for the current role", tone: "blue" },
                { title: locale === "zh" ? "本周动作" : "This Week's Actions", value: workTargetChecklistRows.length, detail: locale === "zh" ? "当前建议优先动作" : "Recommended priority actions", tone: "violet" },
                { title: locale === "zh" ? "经营信号" : "Live Signal", value: formatUsdAmount(Number(scopedWorkspaceMetrics?.revenueUSD ?? adminOverview?.revenueUSD ?? 0) || 0), detail: locale === "zh" ? "当前可参考的经营金额" : "Current revenue signal available to this workspace", tone: "amber" }
              ]}
            >
            <section className="admin-ops-section-grid">
              <div className="admin-ops-surface">
                <div className="admin-ops-surface-head">
                  <div>
                    <h4>{locale === "zh" ? "当前目标泳道" : "Current Target Lanes"}</h4>
                    <p>{locale === "zh" ? "根据当前可访问模块，明确每条业务线应该盯什么信号。" : "Use accessible modules to define which signals each lane should watch."}</p>
                  </div>
                </div>
                <div className="table-wrap admin-ops-table-scroll">
                  <table className="controlhub-table">
                    <thead>
                      <tr>
                        <th>{locale === "zh" ? "模块" : "Lane"}</th>
                        <th>{locale === "zh" ? "本周目标" : "This Week's Target"}</th>
                        <th>{locale === "zh" ? "当前信号" : "Current Signal"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {workTargetLaneRows.map((item) => (
                        <tr key={`work-target-lane-${item.key}`}>
                          <td>{item.label}</td>
                          <td>{item.target}</td>
                          <td>{item.signal}</td>
                        </tr>
                      ))}
                      {!workTargetLaneRows.length ? <tr><td colSpan="3">{texts.none}</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-ops-surface">
                <h5>{locale === "zh" ? "执行清单" : "Execution Checklist"}</h5>
                <div style={{ display: "grid", gap: 12 }}>
                  {workTargetChecklistRows.map((item) => (
                    <div key={`work-target-check-${item.key}`} style={{ border: "1px solid #e2e8f0", borderRadius: 14, padding: "12px 14px", background: "#fff" }}>
                      <strong>{item.title}</strong>
                      <p style={{ margin: "6px 0 0", color: "#475569" }}>{item.detail}</p>
                    </div>
                  ))}
                  {!workTargetChecklistRows.length ? <p>{texts.none}</p> : null}
                </div>
              </div>
            </section>
            </AdminOpsShell>
            )
          ) : null}

          {adminTab === "workProcessing" ? workProcessingPanel : null}

          {adminTab === "settings" ? (
            <AdminSettingsHubPage
              locale={locale}
              systemConfigDraft={systemConfigDraft}
              savingSystemConfig={savingSystemConfig}
              systemConfigForm={systemConfigForm}
            />
          ) : null}

          {adminTab === "siteMaintenance" ? (
            <AdminSiteMaintenanceHubPage
              locale={locale}
              metrics={[
                {
                  title: locale === "zh" ? "岗位模板" : "Positions",
                  value: positionRows.length,
                  detail: locale === "zh" ? "当前维护的岗位数量" : "Current maintained position rows",
                  tone: "blue"
                },
                {
                  title: locale === "zh" ? "健康探针" : "Healthy Probes",
                  value: Number(siteMaintenanceSummary.healthy || 0),
                  detail: locale === "zh" ? "运行正常的检查项" : "Runtime checks currently healthy",
                  tone: "emerald"
                },
                {
                  title: locale === "zh" ? "异常探针" : "Unhealthy Probes",
                  value: Number(siteMaintenanceSummary.unhealthy || 0),
                  detail: locale === "zh" ? "需要排查的服务状态" : "Services that need follow-up",
                  tone: "amber"
                }
              ]}
              toolbar={(
                <div className="row-actions">
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={() => {
                      void fetchSiteMaintenanceProbes();
                    }}
                    disabled={siteMaintenanceProbeLoading}
                  >
                    {siteMaintenanceProbeLoading ? (locale === "zh" ? "刷新中..." : "Refreshing...") : (locale === "zh" ? "刷新探针" : "Refresh Probes")}
                  </button>
                </div>
              )}
              leftPanel={(
                <article className="controlhub-card controlhub-position-maintenance-panel" style={{ margin: 0 }}>
                <h4>{locale === "zh" ? "网站全局维护" : "Global Site Maintenance"}</h4>
                <p className="controlhub-merchandising-caption">
                  {locale === "zh"
                    ? "左侧保留岗位与权限维护；右侧面板提供 MySQL、Redis 等运行状态与配置视图。"
                    : "The left side keeps position maintenance, while the right panel shows MySQL/Redis runtime probes and config."}
                </p>
                <div className="table-wrap">
                  <table className="controlhub-table">
                    <thead>
                      <tr>
                        <th>{texts.id}</th>
                        <th>{texts.positionName}</th>
                        <th>{texts.positionKey}</th>
                        <th>{texts.department}</th>
                        <th>{locale === "zh" ? "权限等级" : "Permission Level"}</th>
                        <th>{texts.permissions}</th>
                        <th>{texts.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedPositionRows.items.map((item) => (
                        <tr key={`position-maintenance-${item.id}`}>
                          <td>{item.id}</td>
                          <td>
                            {editingPositionId === item.id ? (
                              <input
                                type="text"
                                value={positionEditDraft.name}
                                onChange={(event) =>
                                  setPositionEditDraft((prev) => ({ ...prev, name: event.target.value }))
                                }
                              />
                            ) : (
                              item.name
                            )}
                          </td>
                          <td>
                            {editingPositionId === item.id ? (
                              <input
                                type="text"
                                value={positionEditDraft.key}
                                onChange={(event) =>
                                  setPositionEditDraft((prev) => ({ ...prev, key: event.target.value }))
                                }
                              />
                            ) : (
                              item.key
                            )}
                          </td>
                          <td>
                            {editingPositionId === item.id ? (
                              <input
                                type="text"
                                value={positionEditDraft.department}
                                onChange={(event) =>
                                  setPositionEditDraft((prev) => ({ ...prev, department: event.target.value }))
                                }
                              />
                            ) : (
                              item.department
                            )}
                          </td>
                          <td>
                            {editingPositionId === item.id ? (
                              <input
                                type="number"
                                value={positionEditDraft.permissionLevel}
                                onChange={(event) =>
                                  setPositionEditDraft((prev) => ({ ...prev, permissionLevel: Number(event.target.value || 0) || 0 }))
                                }
                              />
                            ) : (
                              item.permissionLevel
                            )}
                          </td>
                          <td className="position-permission-cell">
                            {(() => {
                              const rowTarget = `edit-${item.id}`;
                              const permissionSource = editingPositionId === item.id
                                ? getPositionPermissionWorkingSelection(rowTarget, positionEditDraft.permissions)
                                : item.permissions;
                              const preview = getPermissionPreviewMeta(permissionSource);
                              return (
                                <div className="position-permission-inline position-permission-editor">
                                  <span className="position-permission-preview" title={preview.fullText || texts.none}>
                                    <span className="position-permission-preview-main">{preview.previewText}</span>
                                    {preview.hiddenCount ? <span className="position-permission-preview-badge">{preview.badgeText}</span> : null}
                                  </span>
                                  {editingPositionId === item.id ? (
                                    <button
                                      type="button"
                                      className="controlhub-button ghost position-permission-trigger"
                                      onClick={() => openPositionPermissionModal(rowTarget)}
                                    >
                                      {texts.editPermissions}
                                    </button>
                                  ) : null}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="position-maintenance-cell-actions">
                            <div className="row-actions user-management-actions">
                              {editingPositionId === item.id ? (
                                <>
                                  <button
                                    type="button"
                                    className="user-management-icon-button"
                                    aria-label={texts.save}
                                    title={texts.save}
                                    onClick={() => savePosition(item.id)}
                                  >
                                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                      <path d="M7.8 13.6 4.2 10l1.4-1.4 2.2 2.2 6.6-6.6L15.8 5l-8 8.6Z" fill="currentColor" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="user-management-icon-button"
                                    aria-label={texts.cancel}
                                    title={texts.cancel}
                                    onClick={() => setEditingPositionId(0)}
                                  >
                                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                      <path d="m5.3 4 4.7 4.7L14.7 4 16 5.3 11.3 10 16 14.7 14.7 16 10 11.3 5.3 16 4 14.7 8.7 10 4 5.3 5.3 4Z" fill="currentColor" />
                                    </svg>
                                  </button>
                                </>
                              ) : (
                                <button
                                  type="button"
                                  className="user-management-icon-button"
                                  aria-label={texts.edit}
                                  title={texts.edit}
                                  onClick={() => startEditPosition(item)}
                                >
                                  <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                    <path d="M13.9 2.6a2 2 0 0 1 2.8 2.8l-8.6 8.6-3.8 1 1-3.8 8.6-8.6Zm-7.4 9.2-.4 1.4 1.4-.4 7.8-7.8-1-1-7.8 7.8Z" fill="currentColor" />
                                  </svg>
                                </button>
                              )}
                              <button
                                type="button"
                                className="user-management-icon-button danger"
                                aria-label={texts.remove}
                                title={texts.remove}
                                onClick={() => deletePosition(item.id)}
                              >
                                <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                  <path d="M7 2h6l1 2h3v2H3V4h3l1-2Zm-1 6h2v7H6V8Zm6 0h2v7h-2V8ZM5 6h10l-.7 11.1A1 1 0 0 1 13.3 18H6.7a1 1 0 0 1-1-.9L5 6Z" fill="currentColor" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td>+</td>
                        <td>
                          <input
                            type="text"
                            value={positionDraft.name}
                            onChange={(event) =>
                              setPositionDraft((prev) => ({ ...prev, name: event.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={positionDraft.key}
                            onChange={(event) =>
                              setPositionDraft((prev) => ({ ...prev, key: event.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={positionDraft.department}
                            onChange={(event) =>
                              setPositionDraft((prev) => ({ ...prev, department: event.target.value }))
                            }
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            value={positionDraft.permissionLevel}
                            onChange={(event) =>
                              setPositionDraft((prev) => ({ ...prev, permissionLevel: Number(event.target.value || 0) || 0 }))
                            }
                          />
                        </td>
                        <td className="position-permission-cell">
                          <div className="position-permission-inline position-permission-editor">
                            {(() => {
                              const preview = getPermissionPreviewMeta(
                                getPositionPermissionWorkingSelection("create", positionDraft.permissions)
                              );
                              return (
                                <span className="position-permission-preview" title={preview.fullText || texts.none}>
                                  <span className="position-permission-preview-main">{preview.previewText}</span>
                                  {preview.hiddenCount ? <span className="position-permission-preview-badge">{preview.badgeText}</span> : null}
                                </span>
                              );
                            })()}
                            <button
                              type="button"
                              className="controlhub-button ghost position-permission-trigger"
                              onClick={() => openPositionPermissionModal("create")}
                            >
                              {texts.editPermissions}
                            </button>
                          </div>
                        </td>
                        <td>
                          <button type="button" className="controlhub-button" onClick={createPosition}>
                            {texts.addPosition}
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {renderPagination("positions", paginatedPositionRows)}
                <p style={{ marginTop: 14 }}>
                  {texts.permissionSectionTitle}: {accessCapabilityLabels.join(" · ") || texts.none}
                </p>
                </article>
              )}
              rightPanel={(
                <aside className="controlhub-card" style={{ margin: 0 }}>
                <div className="controlhub-settings-head" style={{ marginBottom: 8 }}>
                  <h4>{locale === "zh" ? "运行维护面板" : "Runtime Maintenance Panel"}</h4>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={() => {
                      void fetchSiteMaintenanceProbes();
                    }}
                    disabled={siteMaintenanceProbeLoading}
                  >
                    {siteMaintenanceProbeLoading ? (locale === "zh" ? "刷新中..." : "Refreshing...") : (locale === "zh" ? "刷新探针" : "Refresh")}
                  </button>
                </div>

                <div className="controlhub-settings-tabs compact" role="tablist" aria-label="Site maintenance tabs">
                  <button
                    type="button"
                    className={`controlhub-settings-tab ${siteMaintenancePanelTab === "probes" ? "active" : ""}`}
                    role="tab"
                    aria-selected={siteMaintenancePanelTab === "probes"}
                    onClick={() => setSiteMaintenancePanelTab("probes")}
                  >
                    {locale === "zh" ? "数据探针预览" : "Probe Preview"}
                  </button>
                  <button
                    type="button"
                    className={`controlhub-settings-tab ${siteMaintenancePanelTab === "mysql" ? "active" : ""}`}
                    role="tab"
                    aria-selected={siteMaintenancePanelTab === "mysql"}
                    onClick={() => setSiteMaintenancePanelTab("mysql")}
                  >
                    MySQL
                  </button>
                  <button
                    type="button"
                    className={`controlhub-settings-tab ${siteMaintenancePanelTab === "redis" ? "active" : ""}`}
                    role="tab"
                    aria-selected={siteMaintenancePanelTab === "redis"}
                    onClick={() => setSiteMaintenancePanelTab("redis")}
                  >
                    Redis
                  </button>
                </div>

                {siteMaintenancePanelTab === "probes" ? (
                  <div style={{ marginTop: 12 }}>
                    <div className="controlhub-form-grid three-col">
                      <label>
                        <span>{locale === "zh" ? "总体状态" : "Overall"}</span>
                        <strong style={{ color: siteMaintenanceIsHealthy ? "#0f766e" : "#b91c1c" }}>
                          {siteMaintenanceStatus ? (siteMaintenanceIsHealthy ? (locale === "zh" ? "健康" : "Healthy") : (locale === "zh" ? "降级" : "Degraded")) : "-"}
                        </strong>
                      </label>
                      <label>
                        <span>{locale === "zh" ? "健康探针" : "Healthy Probes"}</span>
                        <strong>{Number(siteMaintenanceSummary.healthy || 0)}</strong>
                      </label>
                      <label>
                        <span>{locale === "zh" ? "异常探针" : "Unhealthy Probes"}</span>
                        <strong>{Number(siteMaintenanceSummary.unhealthy || 0)}</strong>
                      </label>
                    </div>

                    <div className="table-wrap" style={{ marginTop: 12 }}>
                      <table className="controlhub-table">
                        <thead>
                          <tr>
                            <th>{locale === "zh" ? "探针" : "Probe"}</th>
                            <th>{locale === "zh" ? "状态" : "Status"}</th>
                            <th>{locale === "zh" ? "详情" : "Detail"}</th>
                            <th>{locale === "zh" ? "检查时间" : "Checked At"}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {siteMaintenanceProbes.map((item) => (
                            <tr key={`probe-${item.key}`}>
                              <td>{item.label || item.key}</td>
                              <td style={{ color: item.available ? "#0f766e" : "#b91c1c", fontWeight: 600 }}>
                                <span
                                  style={{
                                    ...getProbeTone(Boolean(item.available)),
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 6,
                                    borderRadius: 999,
                                    padding: "2px 10px",
                                    fontSize: 12,
                                    fontWeight: 700
                                  }}
                                >
                                  <span
                                    style={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      background: item.available ? "#0f766e" : "#b91c1c"
                                    }}
                                  />
                                  {item.available ? (locale === "zh" ? "正常" : "OK") : (locale === "zh" ? "异常" : "DOWN")}
                                </span>
                              </td>
                              <td>{item.detail || "-"}</td>
                              <td>{formatProbeTime(item.checkedAt)}</td>
                            </tr>
                          ))}
                          {!siteMaintenanceProbes.length ? (
                            <tr>
                              <td colSpan="4">{siteMaintenanceProbeLoading ? (locale === "zh" ? "正在加载探针数据..." : "Loading probes...") : texts.none}</td>
                            </tr>
                          ) : null}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : null}

                {siteMaintenancePanelTab === "mysql" ? (
                  <div className="controlhub-settings-fields" style={{ marginTop: 12 }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      {renderProbeHistoryStrip(mysqlProbeHistory)}
                    </div>
                    <label>
                      <span>{locale === "zh" ? "连接状态" : "Connection"}</span>
                      <input
                        value={(siteMaintenanceProbes.find((item) => item.key === "mysql")?.available
                          ? (locale === "zh" ? "已连接" : "Connected")
                          : (locale === "zh" ? "未连接" : "Disconnected"))}
                        readOnly
                      />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "主机" : "Host"}</span>
                      <input value={String(siteMaintenanceConfig?.mysql?.host || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "端口" : "Port"}</span>
                      <input value={String(siteMaintenanceConfig?.mysql?.port || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "数据库" : "Database"}</span>
                      <input value={String(siteMaintenanceConfig?.mysql?.database || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "账号" : "User"}</span>
                      <input value={String(siteMaintenanceConfig?.mysql?.user || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "连接池" : "Pool Size"}</span>
                      <input value={String(siteMaintenanceConfig?.mysql?.poolSize || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "超时(ms)" : "Timeout (ms)"}</span>
                      <input value={String(siteMaintenanceConfig?.mysql?.connectTimeoutMs || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "最近检查" : "Last Check"}</span>
                      <input value={formatProbeTime(siteMaintenanceProbes.find((item) => item.key === "mysql")?.checkedAt)} readOnly />
                    </label>
                  </div>
                ) : null}

                {siteMaintenancePanelTab === "redis" ? (
                  <div className="controlhub-settings-fields" style={{ marginTop: 12 }}>
                    <div style={{ gridColumn: "1 / -1" }}>
                      {renderProbeHistoryStrip(redisProbeHistory)}
                    </div>
                    <label>
                      <span>{locale === "zh" ? "连接状态" : "Connection"}</span>
                      <input
                        value={(siteMaintenanceProbes.find((item) => item.key === "redis")?.available
                          ? (locale === "zh" ? "可用" : "Available")
                          : (locale === "zh" ? "不可用" : "Unavailable"))}
                        readOnly
                      />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "是否启用" : "Enabled"}</span>
                      <input value={siteMaintenanceConfig?.redis?.enabled ? "true" : "false"} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "连接串" : "Connection String"}</span>
                      <input value={String(siteMaintenanceConfig?.redis?.connectionString || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "数据库编号" : "Database Index"}</span>
                      <input value={String(siteMaintenanceConfig?.redis?.database ?? "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "默认 TTL(秒)" : "Default TTL (sec)"}</span>
                      <input value={String(siteMaintenanceConfig?.redis?.defaultTtlSeconds ?? "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "状态说明" : "Detail"}</span>
                      <input value={String(siteMaintenanceProbes.find((item) => item.key === "redis")?.detail || "")} readOnly />
                    </label>
                    <label>
                      <span>{locale === "zh" ? "最近检查" : "Last Check"}</span>
                      <input value={formatProbeTime(siteMaintenanceProbes.find((item) => item.key === "redis")?.checkedAt)} readOnly />
                    </label>
                  </div>
                ) : null}
                </aside>
              )}
            />
          ) : null}

          {adminTab === "positionMaintenance" ? (
            <AdminTeamPermissionsPage
              locale={locale}
              userCount={memberFilteredRows.length}
              activeCount={activeManagedUserCount}
              toolbar={(
                <div className="admin-ops-toolbar-stack">
                  <div className="row-actions">
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      onClick={() => {
                        void fetchAdminData();
                        void fetchPositionWorkspaceData();
                      }}
                    >
                      {texts.refresh}
                    </button>
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      disabled={!selectedManageableMemberRows.length}
                      onClick={() => void applyBulkUserStatus("active")}
                    >
                      {locale === "zh" ? "批量启用" : "Bulk Enable"}
                    </button>
                    <button
                      type="button"
                      className="controlhub-button ghost"
                      disabled={!selectedManageableMemberRows.length}
                      onClick={() => void applyBulkUserStatus("disactive")}
                    >
                      {locale === "zh" ? "批量停用" : "Bulk Disable"}
                    </button>
                  </div>
                  <div className="admin-ops-filter-grid">
                    <label className="admin-ops-filter">
                      <span>{locale === "zh" ? "角色筛选" : "Role Filter"}</span>
                      <select value={memberRoleFilter} onChange={(event) => setMemberRoleFilter(event.target.value)}>
                        <option value="">{locale === "zh" ? "全部角色" : "All Roles"}</option>
                        {memberRoleOptions.map((roleKey) => (
                          <option key={`member-role-${roleKey}`} value={roleKey}>
                            {roleLabels[roleKey] || roleKey}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="admin-ops-filter">
                      <span>{locale === "zh" ? "状态筛选" : "Status Filter"}</span>
                      <select value={memberStatusFilter} onChange={(event) => setMemberStatusFilter(event.target.value)}>
                        <option value="">{locale === "zh" ? "全部状态" : "All Statuses"}</option>
                        <option value="active">{texts.statusActive}</option>
                        <option value="disactive">{texts.statusDisactive}</option>
                      </select>
                    </label>
                    <label className="admin-ops-filter">
                      <span>{locale === "zh" ? "搜索上下文" : "Search Context"}</span>
                      <input value={adminQuery} readOnly placeholder={locale === "zh" ? "顶部搜索联动" : "Linked to top search"} />
                    </label>
                    <div className="admin-ops-selection-summary">
                      <span className="admin-ops-pill is-primary">
                        {locale === "zh" ? `已选 ${memberSelectedIds.length}` : `Selected ${memberSelectedIds.length}`}
                      </span>
                      <span className="admin-ops-pill is-success">
                        {locale === "zh" ? `可批量操作 ${selectedManageableMemberRows.length}` : `Actionable ${selectedManageableMemberRows.length}`}
                      </span>
                    </div>
                  </div>
                </div>
              )}
              table={(
                <section className="controlhub-card controlhub-wide-card user-management-panel admin-ops-user-table">
              <div className="admin-ops-surface-head">
                <div>
                  <h4>{locale === "zh" ? "成员列表" : "Member Directory"}</h4>
                  <p>{locale === "zh" ? "通过顶部全局搜索配合角色/状态筛选快速锁定成员，并执行批量状态操作。" : "Use the top search plus role/status filters to isolate members and run bulk status actions."}</p>
                </div>
                <div className="admin-ops-inline-metrics">
                  <span className="admin-ops-pill">{locale === "zh" ? `结果 ${memberFilteredRows.length}` : `Results ${memberFilteredRows.length}`}</span>
                  <span className="admin-ops-pill">{locale === "zh" ? `当前页 ${paginatedMemberRows.items.length}` : `Page ${paginatedMemberRows.items.length}`}</span>
                </div>
              </div>
              <div className="table-wrap admin-ops-table-scroll">
                <table className="controlhub-table user-management-table">
                  <colgroup>
                    <col className="user-management-col user-management-col-serial" />
                    <col className="user-management-col user-management-col-serial" />
                    <col className="user-management-col user-management-col-userid" />
                    <col className="user-management-col user-management-col-username" />
                    <col className="user-management-col user-management-col-nickname" />
                    <col className="user-management-col user-management-col-email" />
                    <col className="user-management-col user-management-col-phone" />
                    <col className="user-management-col user-management-col-role" />
                    <col className="user-management-col user-management-col-status" />
                    <col className="user-management-col user-management-col-position" />
                    <col className="user-management-col user-management-col-registered" />
                    <col className="user-management-col user-management-col-last-active" />
                    <col className="user-management-col user-management-col-actions" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="user-management-cell-serial">
                        <input
                          type="checkbox"
                          checked={allCurrentMembersSelected}
                          onChange={(event) => toggleAllCurrentMembers(event.target.checked)}
                          disabled={!currentMemberIds.length}
                        />
                      </th>
                      <th className="user-management-cell-serial">{texts.serialNumber}</th>
                      <th className="user-management-cell-userid">{texts.id}</th>
                      <th className="user-management-cell-username">{texts.username}</th>
                      <th className="user-management-cell-nickname">{texts.nickname}</th>
                      <th className="user-management-cell-email">{texts.email}</th>
                      <th className="user-management-cell-phone">{texts.phone}</th>
                      <th className="user-management-cell-role">{texts.userRole}</th>
                      <th className="user-management-cell-status">{texts.accountStatus}</th>
                      <th className="user-management-cell-position">{texts.position}</th>
                      <th className="user-management-cell-registered">{texts.registeredAt}</th>
                      <th className="user-management-cell-last-active">{texts.lastActiveAt}</th>
                      <th className="user-management-cell-actions">{texts.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedMemberRows.items.map((item, index) => (
                      <tr key={`position-${item.id}`}>
                        <td className="user-management-cell-serial">
                          <input
                            type="checkbox"
                            checked={selectedMemberIdSet.has(Number(item.id || 0))}
                            onChange={(event) => toggleMemberSelection(item.id, event.target.checked)}
                          />
                        </td>
                        <td className="user-management-cell-serial">{paginatedMemberRows.startIndex + index + 1}</td>
                        <td className="user-management-cell-userid" title={String(item.id || "-")}>{item.id}</td>
                        <td className="user-management-cell-username" title={item.username || "-"}>{item.username || "-"}</td>
                        <td className="user-management-cell-nickname" title={item.name || "-"}>{item.name || "-"}</td>
                        <td className="user-management-cell-email" title={item.email || "-"}>{item.email || "-"}</td>
                        <td className="user-management-cell-phone" title={item.phone || "-"}>{item.phone || "-"}</td>
                        <td className="user-management-cell-role" title={item.roleLabel || roleLabels[item.role] || item.role || "-"}>{item.roleLabel || roleLabels[item.role] || item.role}</td>
                        <td className="user-management-cell-status">
                          {(() => {
                            const canManageUser = canManageUserAccount(item);
                            const currentStatus = normalizeUserStatus(
                              (userDraftById[item.id] && userDraftById[item.id].status) || item.status || "active"
                            );
                            const nextStatus = currentStatus === "active" ? "disactive" : "active";
                            const currentLabel = currentStatus === "active" ? texts.statusActive : texts.statusDisactive;

                            return (
                              <button
                                type="button"
                                className="controlhub-status-switch"
                                data-status={currentStatus}
                                role="switch"
                                aria-checked={currentStatus === "active"}
                                aria-label={`${texts.accountStatus}: ${currentLabel}`}
                                title={canManageUser ? currentLabel : texts.userManagementRestrictedHint}
                                disabled={userSavingId === item.id || !canManageUser}
                                onClick={() => saveUserManagement(item, { status: nextStatus })}
                              >
                                <span className="controlhub-status-switch-track">
                                  <span className="controlhub-status-switch-thumb" />
                                </span>
                                <span className="controlhub-status-switch-label">{currentLabel}</span>
                              </button>
                            );
                          })()}
                        </td>
                        <td className="user-management-cell-position" title={item.jobTitle || "-"}>{item.jobTitle || "-"}</td>
                        <td className="user-management-cell-registered" title={formatUserTime(item.registeredAt)}>{formatUserTime(item.registeredAt)}</td>
                        <td className="user-management-cell-last-active" title={formatUserTime(item.lastActiveAt)}>{formatUserTime(item.lastActiveAt)}</td>
                        <td className="user-management-cell-actions">
                          <div className="row-actions user-management-actions">
                            {(() => {
                              const canEditUser = canOpenUserEditModal(item);
                              const canResetPassword = canResetUserPassword(item);
                              const canDeleteUserRow = canDeleteAdminUser(item);
                              return (
                                <>
                                  <button
                                    type="button"
                                    className="user-management-icon-button"
                                    aria-label={canEditUser ? texts.edit : texts.userManagementRestrictedHint}
                                    title={canEditUser ? texts.edit : texts.userManagementRestrictedHint}
                                    disabled={!canEditUser}
                                    onClick={() => openEditUserModal(item)}
                                  >
                                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                      <path d="M13.9 2.6a2 2 0 0 1 2.8 2.8l-8.6 8.6-3.8 1 1-3.8 8.6-8.6Zm-7.4 9.2-.4 1.4 1.4-.4 7.8-7.8-1-1-7.8 7.8Z" fill="currentColor" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="user-management-icon-button"
                                    aria-label={canResetPassword ? texts.changePasswordAction : texts.userManagementRestrictedHint}
                                    title={canResetPassword ? texts.changePasswordAction : texts.userManagementRestrictedHint}
                                    disabled={!canResetPassword}
                                    onClick={() => openUserPasswordModal(item)}
                                  >
                                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                      <path d="M12.5 2a5.5 5.5 0 0 0-5.3 6.8L2 14v4h4l1.2-1.2H9V15h1.8l1.1-1.1A5.5 5.5 0 1 0 12.5 2Zm0 2a3.5 3.5 0 1 1 0 7 3.5 3.5 0 0 1 0-7Z" fill="currentColor" />
                                    </svg>
                                  </button>
                                  <button
                                    type="button"
                                    className="user-management-icon-button danger"
                                    aria-label={canDeleteUserRow ? texts.remove : texts.userManagementRestrictedHint}
                                    title={canDeleteUserRow ? texts.remove : texts.userManagementRestrictedHint}
                                    disabled={!canDeleteUserRow}
                                    onClick={() => openDeleteUserModal(item)}
                                  >
                                    <svg viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                                      <path d="M7 2h6l1 2h3v2H3V4h3l1-2Zm-1 6h2v7H6V8Zm6 0h2v7h-2V8ZM5 6h10l-.7 11.1A1 1 0 0 1 13.3 18H6.7a1 1 0 0 1-1-.9L5 6Z" fill="currentColor" />
                                    </svg>
                                  </button>
                                </>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!paginatedMemberRows.items.length ? (
                      <tr>
                        <td colSpan={13}>{texts.none}</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
              {renderPagination("userManagement", paginatedMemberRows)}
                </section>
              )}
            />
          ) : null}


          {adminTab === "hrEmployee" ? (
            scopedPositionKey === "hr-admin-specialist" && scopedWorkspace ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "人力资源视图" : "HR Workspace"}
              title={locale === "zh" ? "员工档案" : "Employee Profiles"}
              description={locale === "zh" ? "仅展示已定岗且具备管理员及以上权限的员工，方便快速查看人力覆盖。" : "Only show positioned employees with admin-or-above roles for quick staffing coverage review."}
              metrics={[
                { title: locale === "zh" ? "员工记录" : "Profiles", value: hrEmployeeRows.length, detail: locale === "zh" ? "当前可见员工数" : "Visible employee rows", tone: "blue" },
                { title: locale === "zh" ? "活跃账号" : "Active", value: hrEmployeeRows.filter((item) => normalizeUserStatus(item.status) === "active").length, detail: locale === "zh" ? "当前启用状态" : "Currently active accounts", tone: "emerald" },
                { title: locale === "zh" ? "岗位覆盖" : "Coverage", value: hrPositionCoverage.length, detail: locale === "zh" ? "已配置岗位模板" : "Configured position templates", tone: "amber" }
              ]}
            >
              <section className="admin-ops-surface">
              <div className="admin-ops-surface-head">
                <div>
                  <h4>{locale === "zh" ? "员工档案表" : "Employee Table"}</h4>
                  <p>{locale === "zh" ? "仅展示具备岗位名称且角色为管理员及以上的员工。" : "Only users with a position title and admin-or-above role are listed."}</p>
                </div>
              </div>
              <div className="table-wrap admin-ops-table-scroll">
                <table className="controlhub-table">
                  <thead>
                    <tr>
                      <th>{texts.id}</th>
                      <th>{texts.nickname}</th>
                      <th>{texts.userRole}</th>
                      <th>{texts.position}</th>
                      <th>{texts.accountStatus}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrEmployeeRows.map((item) => (
                      <tr key={`hr-staff-${item.id}`}>
                        <td>{item.id}</td>
                        <td>{item.name || item.username || item.id}</td>
                        <td>{item.roleLabel || roleLabels[item.role] || item.role || "-"}</td>
                        <td>{item.jobTitle || "-"}</td>
                        <td>{normalizeUserStatus(item.status) === "active" ? texts.statusActive : texts.statusDisactive}</td>
                      </tr>
                    ))}
                    {!hrEmployeeRows.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
                  </tbody>
                </table>
              </div>
              </section>
            </AdminOpsShell>
            ) : null
          ) : null}

          {adminTab === "hrRecruitment" ? (
            scopedPositionKey === "hr-admin-specialist" && scopedWorkspace ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "招聘协同" : "Recruitment Workspace"}
              title={locale === "zh" ? "招聘与入岗任务" : "Recruitment and Onboarding"}
              description={locale === "zh" ? "围绕招聘状态和入岗阶段推进每日人力协同。" : "Coordinate daily recruiting and onboarding around stage progress."}
              metrics={[
                { title: locale === "zh" ? "招聘记录" : "Candidates", value: hrRecruitmentRows.length, detail: locale === "zh" ? "当前招聘与入岗条目" : "Recruitment and onboarding rows", tone: "blue" },
                { title: locale === "zh" ? "待定岗" : "Needs Position", value: hrRecruitmentRows.filter((item) => (item.onboardingStage || "needs_position") === "needs_position").length, detail: locale === "zh" ? "仍需分配岗位" : "Still waiting for position assignment", tone: "amber" },
                { title: locale === "zh" ? "已入岗" : "Onboarded", value: hrRecruitmentRows.filter((item) => item.onboardingStage === "onboarded").length, detail: locale === "zh" ? "已完成入岗流程" : "Completed onboarding", tone: "emerald" }
              ]}
            >
              <section className="admin-ops-surface">
              <div className="table-wrap admin-ops-table-scroll">
                <table className="controlhub-table">
                  <thead>
                    <tr>
                      <th>{texts.nickname}</th>
                      <th>{texts.position}</th>
                      <th>{locale === "zh" ? "账号状态" : "Account Status"}</th>
                      <th>{locale === "zh" ? "入岗阶段" : "Onboarding Stage"}</th>
                      <th>{texts.actions}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrRecruitmentRows.map((item) => (
                      <tr key={`hr-recruit-${item.id}`}>
                        <td>{item.name || item.id}</td>
                        <td>{item.jobTitle || "-"}</td>
                        <td>{normalizeUserStatus(item.status) === "active" ? texts.statusActive : texts.statusDisactive}</td>
                        <td>
                          <select
                            value={item.onboardingStage || "needs_position"}
                            onChange={(event) => updateHrRecruitmentStage(item.id, event.target.value)}
                          >
                            {HR_RECRUITMENT_STAGE_OPTIONS.map((stage) => (
                              <option key={`hr-stage-${stage}`} value={stage}>
                                {locale === "zh"
                                  ? {
                                      needs_position: "待定岗",
                                      screening: "筛选中",
                                      interview: "面试中",
                                      offer: "发放 Offer",
                                      onboarded: "已入岗"
                                    }[stage] || stage
                                  : {
                                      needs_position: "Needs Position",
                                      screening: "Screening",
                                      interview: "Interview",
                                      offer: "Offer",
                                      onboarded: "Onboarded"
                                    }[stage] || stage}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="controlhub-button ghost"
                            onClick={() => updateHrRecruitmentStage(item.id, item.onboardingStage || "needs_position")}
                          >
                            {texts.save}
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!hrRecruitmentRows.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
                  </tbody>
                </table>
              </div>
              </section>
            </AdminOpsShell>
            ) : null
          ) : null}

          {adminTab === "hrAttendance" ? (
            scopedPositionKey === "hr-admin-specialist" && scopedWorkspace ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "考勤视图" : "Attendance Workspace"}
              title={locale === "zh" ? "考勤与活跃状态" : "Attendance and Activity"}
              description={locale === "zh" ? "统一查看员工最近活跃与考勤导入入口。" : "Review recent employee activity and attendance imports in a single shell."}
              metrics={[
                { title: locale === "zh" ? "员工数" : "Employees", value: hrEmployeeRows.length, detail: locale === "zh" ? "纳入考勤列表" : "Employees in attendance scope", tone: "blue" },
                { title: locale === "zh" ? "活跃账号" : "Active", value: hrEmployeeRows.filter((item) => normalizeUserStatus(item.status) === "active").length, detail: locale === "zh" ? "当前启用账号" : "Accounts currently active", tone: "emerald" },
                { title: locale === "zh" ? "导入入口" : "Import Entry", value: locale === "zh" ? "已就绪" : "Ready", detail: locale === "zh" ? "支持 CSV/TSV 导入" : "CSV/TSV import available", tone: "amber" }
              ]}
            >
              <section className="admin-ops-surface">
              <div className="row-actions" style={{ marginBottom: 12 }}>
                <label className="controlhub-button ghost" style={{ cursor: hrAttendanceImporting ? "wait" : "pointer" }}>
                  {hrAttendanceImporting
                    ? (locale === "zh" ? "导入中..." : "Importing...")
                    : (locale === "zh" ? "导入考勤文件 (CSV/TSV)" : "Import Attendance File (CSV/TSV)")}
                  <input
                    type="file"
                    accept=".csv,.tsv,text/csv,text/plain"
                    onChange={importHrAttendance}
                    disabled={hrAttendanceImporting}
                    style={{ display: "none" }}
                  />
                </label>
              </div>
              <div className="table-wrap admin-ops-table-scroll">
                <table className="controlhub-table">
                  <thead>
                    <tr>
                      <th>{texts.nickname}</th>
                      <th>{texts.position}</th>
                      <th>{texts.accountStatus}</th>
                      <th>{texts.lastActiveAt}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrEmployeeRows.map((item) => (
                      <tr key={`hr-attendance-${item.id}`}>
                        <td>{item.name || item.username || item.id}</td>
                        <td>{item.jobTitle || "-"}</td>
                        <td>{normalizeUserStatus(item.status) === "active" ? texts.statusActive : texts.statusDisactive}</td>
                        <td>{formatUserTime(item.lastActiveAt)}</td>
                      </tr>
                    ))}
                    {!hrEmployeeRows.length ? <tr><td colSpan="4">{texts.none}</td></tr> : null}
                  </tbody>
                </table>
              </div>
              </section>
            </AdminOpsShell>
            ) : null
          ) : null}

          {adminTab === "hrOffice" ? (
            scopedPositionKey === "hr-admin-specialist" && scopedWorkspace ? (
            <AdminOpsShell
              eyebrow={locale === "zh" ? "行政协同中心" : "Office Administration Hub"}
              title={locale === "zh" ? "行政与岗位覆盖" : "Office Administration and Coverage"}
              description={locale === "zh" ? "把公告、制度文件和岗位覆盖合并到统一行政协同页面。" : "Bring announcements, policy files, and position coverage into one office admin page."}
              metrics={[
                { title: locale === "zh" ? "公告数" : "Announcements", value: hrAnnouncements.length, detail: locale === "zh" ? "当前公告条目" : "Current announcement rows", tone: "blue" },
                { title: locale === "zh" ? "制度文件" : "Policy Files", value: hrDocumentUploads.length, detail: locale === "zh" ? "已上传制度文档" : "Uploaded policy files", tone: "violet" },
                { title: locale === "zh" ? "岗位覆盖" : "Coverage Rows", value: hrPositionCoverage.length, detail: locale === "zh" ? "当前岗位覆盖明细" : "Position coverage rows", tone: "emerald" }
              ]}
            >
              <section className="admin-ops-section-grid">
              <div className="admin-ops-surface">
                <h5>{locale === "zh" ? "发布行政公告" : "Publish Office Announcement"}</h5>
                <textarea
                  rows={3}
                  value={hrAnnouncementDraft}
                  onChange={(event) => setHrAnnouncementDraft(event.target.value)}
                  placeholder={locale === "zh" ? "输入公告内容" : "Enter announcement content"}
                />
                <div className="row-actions" style={{ marginTop: 8 }}>
                  <button type="button" className="controlhub-button" onClick={publishHrAnnouncement}>
                    {locale === "zh" ? "发布公告" : "Publish"}
                  </button>
                </div>
                <div className="table-wrap admin-ops-table-scroll" style={{ marginTop: 10 }}>
                  <table className="controlhub-table">
                    <thead>
                      <tr>
                        <th>{locale === "zh" ? "公告" : "Announcement"}</th>
                        <th>{locale === "zh" ? "发布人" : "Author"}</th>
                        <th>{locale === "zh" ? "发布时间" : "Created At"}</th>
                        <th>{texts.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hrAnnouncements.map((item) => (
                        <tr key={`hr-announcement-${item.id}`}>
                          <td>{item.content || "-"}</td>
                          <td>{item.author || "-"}</td>
                          <td>{formatUserTime(item.createdAt)}</td>
                          <td>
                            <button
                              type="button"
                              className="controlhub-button danger"
                              onClick={() => deleteHrAnnouncement(item.id)}
                            >
                              {texts.remove}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!hrAnnouncements.length ? <tr><td colSpan="4">{texts.none}</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-ops-surface">
                <h5>{locale === "zh" ? "制度文档上传" : "Policy Document Upload"}</h5>
                <div className="row-actions" style={{ marginBottom: 8 }}>
                  <label className="controlhub-button ghost" style={{ cursor: hrDocumentUploading ? "wait" : "pointer" }}>
                    {hrDocumentUploading
                      ? (locale === "zh" ? "上传中..." : "Uploading...")
                      : (locale === "zh" ? "上传文档" : "Upload Document")}
                    <input
                      type="file"
                      onChange={uploadHrDocument}
                      disabled={hrDocumentUploading}
                      style={{ display: "none" }}
                    />
                  </label>
                </div>
                <div className="table-wrap admin-ops-table-scroll">
                  <table className="controlhub-table">
                    <thead>
                      <tr>
                        <th>{texts.filename || (locale === "zh" ? "文件名" : "File Name")}</th>
                        <th>{locale === "zh" ? "类型" : "Type"}</th>
                        <th>{locale === "zh" ? "大小" : "Size"}</th>
                        <th>{locale === "zh" ? "上传时间" : "Uploaded At"}</th>
                        <th>{texts.actions}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {hrDocumentUploads.map((item) => (
                        <tr key={`hr-doc-${item.id}`}>
                          <td>{item.name || "-"}</td>
                          <td>{item.type || "-"}</td>
                          <td>{`${Math.max(1, Math.round(Number(item.size || 0) / 1024))} KB`}</td>
                          <td>{formatUserTime(item.uploadedAt)}</td>
                          <td>
                            <button
                              type="button"
                              className="controlhub-button danger"
                              onClick={() => deleteHrDocument(item.id)}
                            >
                              {texts.remove}
                            </button>
                          </td>
                        </tr>
                      ))}
                      {!hrDocumentUploads.length ? <tr><td colSpan="5">{texts.none}</td></tr> : null}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="admin-ops-surface" style={{ gridColumn: "1 / -1" }}>
              <div className="table-wrap admin-ops-table-scroll">
                <table className="controlhub-table">
                  <thead>
                    <tr>
                      <th>{texts.positionName}</th>
                      <th>{texts.department}</th>
                      <th>{locale === "zh" ? "已分配人数" : "Assigned"}</th>
                      <th>{locale === "zh" ? "权限数" : "Permissions"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {hrPositionCoverage.map((item) => (
                      <tr key={`hr-office-${item.id}`}>
                        <td>{item.name}</td>
                        <td>{item.department}</td>
                        <td>{item.assignedCount}</td>
                        <td>{item.permissionCount}</td>
                      </tr>
                    ))}
                    {!hrPositionCoverage.length ? <tr><td colSpan="4">{texts.none}</td></tr> : null}
                  </tbody>
                </table>
              </div>
              </div>
              </section>
            </AdminOpsShell>
            ) : null
          ) : null}
          {adminTab === "products" ? (
            <AdminProductsPage
              locale={locale}
              texts={texts}
              adminProducts={adminProducts}
              analyticsByProductId={productAnalyticsById}
              analyticsDailyByProductId={productAnalyticsDailyById}
              analyticsLoading={productAnalyticsLoading}
              analyticsRange={productAnalyticsRange}
              onChangeAnalyticsRange={updateProductAnalyticsRange}
              onRefreshAnalytics={() => fetchProductsAnalyticsOverview(adminProducts)}
              onExportAnalyticsDaily={exportProductsAnalyticsDailyCsv}
              analyticsExporting={productAnalyticsExporting}
              onStartEdit={startEditProduct}
              onDeleteProduct={deleteProduct}
              onOpenCreate={allowedTabs.includes("productCreate") ? openCreateProductEditor : undefined}
              paginatedProductRows={paginatedProductRows}
              renderPagination={renderPagination}
              productsTable={(
                <ProductsTable
                  texts={texts}
                  locale={locale}
                  filteredAdminProducts={paginatedProductRows.items}
                  analyticsByProductId={productAnalyticsById}
                  analyticsDailyByProductId={productAnalyticsDailyById}
                  analyticsLoading={productAnalyticsLoading}
                  analyticsRange={productAnalyticsRange}
                  onChangeAnalyticsRange={updateProductAnalyticsRange}
                  onRefreshAnalytics={() => fetchProductsAnalyticsOverview(adminProducts)}
                  onExportAnalyticsDaily={exportProductsAnalyticsDailyCsv}
                  analyticsExporting={productAnalyticsExporting}
                  onStartEdit={startEditProduct}
                  onDeleteProduct={deleteProduct}
                  onToggleRecommended={toggleProductRecommended}
                  onOpenCreate={allowedTabs.includes("productCreate") ? openCreateProductEditor : undefined}
                />
              )}
            />
          ) : null}

          {adminTab === "productCreate" ? (
            <AdminProductCreatePage
              locale={locale}
              texts={texts}
              editingProductId={editingProductId}
              onBackToProducts={() => switchMenu("products")}
              productForm={(
                <CreateProductForm
                  texts={texts}
                  newProduct={newProduct}
                  setNewProduct={setNewProduct}
                  onSubmit={submitProductEditor}
                  productCategories={PRODUCT_CATEGORIES}
                  locale={locale}
                  onBack={closeProductEditor}
                  onUploadAssets={uploadAdminProductAssets}
                  onDeleteAssets={deleteAdminProductAssets}
                  editorTitle={editingProductId ? (locale === "zh" ? "编辑商品详情" : "Edit Product Detail") : (locale === "zh" ? "新增商品详情" : texts.createProduct)}
                  editorEyebrow={editingProductId ? (locale === "zh" ? "运营专员商品详情编辑" : "Operations Product Detail Edit") : undefined}
                  primaryActionLabel={editingProductId ? (locale === "zh" ? "更新商品" : texts.save) : undefined}
                />
              )}
            />
          ) : null}

          {adminTab === "merchandising" ? (
            <div className="controlhub-content-grid controlhub-merchandising-workspace">
              {merchandisingView !== "collection" ? (
                <article className="controlhub-card controlhub-full-width controlhub-merchandising-hero">
                  <div className="controlhub-merchandising-hero-main">
                    <div className="controlhub-merchandising-hero-copy">
                      <p className="controlhub-merchandising-eyebrow">
                        {locale === "zh" ? "Merchandising Workspace" : "Merchandising Workspace"}
                      </p>
                      <h4>{locale === "zh" ? "选品决策总览" : "Merchandising Command View"}</h4>
                      <p>
                        {locale === "zh"
                          ? "按市场适配、利润带、供应准备度和资料完整度梳理选品节奏，先看风险，再推进可落地商品。"
                          : "Organize export selection around market fit, margin, sourcing readiness, and content completeness so teams can clear risk before pushing launch-ready products."}
                      </p>
                      <div className="controlhub-merchandising-hero-highlights">
                        <div className="controlhub-merchandising-highlight-card">
                          <span>{locale === "zh" ? "当前视图" : "Current View"}</span>
                          <strong>{(merchandisingViewTabs.find((item) => item.key === merchandisingView)?.label) || (locale === "zh" ? "总览" : "Overview")}</strong>
                        </div>
                        <div className="controlhub-merchandising-highlight-card">
                          <span>{locale === "zh" ? "激活筛选" : "Active Filters"}</span>
                          <strong>{activeMerchandisingFilterCount}</strong>
                        </div>
                        <div className="controlhub-merchandising-highlight-card">
                          <span>{locale === "zh" ? "重点动作" : "Priority Actions"}</span>
                          <strong>{merchandisingExecutiveActions.length}</strong>
                        </div>
                      </div>
                    </div>
                    <div className="row-actions controlhub-merchandising-hero-actions">
                      {allowedTabs.includes("productCreate") ? (
                        <button type="button" className="controlhub-button" onClick={openCreateProductEditor}>
                          {texts.createProduct}
                        </button>
                      ) : null}
                      <button type="button" className="controlhub-button ghost" onClick={fetchPositionWorkspaceData}>
                        {texts.refresh}
                      </button>
                    </div>
                  </div>
                  <div className="controlhub-merchandising-metric-grid">
                    {merchandisingHeroMetrics.map((item) => (
                      <div key={`merch-hero-${item.key}`} className="controlhub-merchandising-metric-tile">
                        <span>{item.label}</span>
                        <strong>{item.value}</strong>
                        <small>{item.note}</small>
                      </div>
                    ))}
                  </div>
                  {merchandisingExecutiveActions.length ? (
                    <div className="controlhub-merchandising-action-ribbon">
                      {merchandisingExecutiveActions.map((item) => (
                        <div key={`merch-ribbon-${item.key}`} className="controlhub-merchandising-action-chip">
                          <span className={`controlhub-merchandising-badge is-${item.tone}`}>{item.count}</span>
                          <div>
                            <strong>{item.title}</strong>
                            <small>{locale === "zh" ? "当前建议优先推进的事项" : "Recommended for immediate follow-up"}</small>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </article>
              ) : null}

              {merchandisingView !== "collection" ? (
                <section className="controlhub-full-width controlhub-merchandising-tab-strip" aria-label={locale === "zh" ? "选品中心视图切换" : "Merchandising view switch"}>
                  {merchandisingViewTabs.map((item) => (
                    <button
                      key={`merch-view-${item.key}`}
                      type="button"
                      className={`controlhub-merchandising-tab${merchandisingView === item.key ? " is-active" : ""}`}
                      onClick={() => setMerchandisingView(item.key)}
                    >
                      <span>{item.label}</span>
                      <strong>{item.count}</strong>
                    </button>
                  ))}
                </section>
              ) : null}

              {merchandisingView === "overview" ? (
                <>
                  <section className="controlhub-full-width controlhub-merchandising-overview-layout">
                    <article className="controlhub-card controlhub-merchandising-overview-spotlight">
                      <div className="controlhub-merchandising-overview-spotlight-head">
                        <div>
                          <p className="controlhub-merchandising-eyebrow">
                            {locale === "zh" ? "Priority Readout" : "Priority Readout"}
                          </p>
                          <h4>{locale === "zh" ? "今日优先推进商品" : "Today’s Lead SKU"}</h4>
                        </div>
                        <span className="controlhub-merchandising-section-count">
                          {merchandisingExecutiveQueue.length}
                        </span>
                      </div>

                      {merchandisingLeadItem ? (
                        <div className="controlhub-merchandising-overview-spotlight-body">
                          <div className="controlhub-merchandising-overview-lead-main">
                            <div>
                              <strong>{merchandisingLeadItem.name || "-"}</strong>
                              <p>
                                {locale === "zh"
                                  ? "这是当前最值得先推进的候选项，优先检查报价、样品与合规链路。"
                                  : "This is the strongest current candidate. Prioritize pricing, sampling, and compliance handoff first."}
                              </p>
                            </div>
                            <div className="controlhub-merchandising-tag-list">
                              <span className={`controlhub-merchandising-badge is-${merchandisingLeadItem?.merchandisingStatus === "ready" ? "positive" : "warning"}`}>
                                {merchandisingLeadItem?.merchandisingStatus === "ready" ? (locale === "zh" ? "可推进" : "Ready") : (locale === "zh" ? "待完善" : "Needs Update")}
                              </span>
                              <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(merchandisingLeadItem.sourcingPriority)}`}>
                                {merchandisingStatusLabels[merchandisingLeadItem.sourcingPriority] || merchandisingLeadItem.sourcingPriority}
                              </span>
                            </div>
                          </div>

                          <div className="controlhub-merchandising-overview-lead-stats">
                            <div>
                              <span>{locale === "zh" ? "售价" : "Price"}</span>
                              <strong>${merchandisingLeadItem.priceUSD || 0}</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "毛利带" : "Margin"}</span>
                              <strong>{merchandisingLeadItem.grossMarginRate || 0}%</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "准备度" : "Readiness"}</span>
                              <strong>{merchandisingLeadItem.readinessScore || 0}/6</strong>
                            </div>
                            <div>
                              <span>MOQ</span>
                              <strong>{merchandisingLeadItem.moq || 0}</strong>
                            </div>
                          </div>

                          <div className="controlhub-merchandising-overview-lead-markets">
                            {(Array.isArray(merchandisingLeadItem.targetMarkets) ? merchandisingLeadItem.targetMarkets : []).map((market) => (
                              <span key={`lead-market-${merchandisingLeadItem.id}-${market}`} className="controlhub-merchandising-badge is-neutral">{market}</span>
                            ))}
                            {!Array.isArray(merchandisingLeadItem.targetMarkets) || !merchandisingLeadItem.targetMarkets.length ? (
                              <span className="controlhub-merchandising-badge is-neutral">{texts.none}</span>
                            ) : null}
                          </div>
                        </div>
                      ) : (
                        <p className="controlhub-merchandising-caption">{texts.none}</p>
                      )}
                    </article>

                    <div className="controlhub-merchandising-overview-side">
                      <article className="controlhub-card controlhub-merchandising-summary-card is-spotlighted">
                        <div className="controlhub-card-head">
                          <h4>{locale === "zh" ? "经营视角" : "Executive Snapshot"}</h4>
                          <span className="controlhub-merchandising-caption">{locale === "zh" ? "优先看利润、成熟度与拖慢节奏的问题" : "Lead with margin, readiness, and blockers"}</span>
                        </div>
                        <div className="controlhub-merchandising-bar-list">
                          {merchandisingReadinessBreakdown.map((item) => (
                            <div key={`merch-breakdown-${item.key}`} className="controlhub-merchandising-bar-row">
                              <div className="controlhub-merchandising-bar-meta">
                                <span>{merchandisingBreakdownLabels[item.key] || item.key}</span>
                                <strong>{item.count}</strong>
                              </div>
                              <div className="controlhub-merchandising-bar-track">
                                <span style={{ width: `${Math.max(8, (Number(item.count || 0) / merchandisingSummaryMaxima.readiness) * 100)}%` }} />
                              </div>
                            </div>
                          ))}
                          {!merchandisingReadinessBreakdown.length ? <p>{texts.none}</p> : null}
                        </div>
                      </article>

                      <div className="controlhub-merchandising-overview-subgrid">
                        <article className="controlhub-card controlhub-merchandising-summary-card">
                          <div className="controlhub-card-head">
                            <h4>{locale === "zh" ? "利润带分布" : "Margin Mix"}</h4>
                            <span className="controlhub-merchandising-caption">{locale === "zh" ? "看当前候选结构是否健康" : "Check whether the shortlist structure is healthy"}</span>
                          </div>
                          <div className="controlhub-merchandising-pill-list">
                            {merchandisingPriceBandSummary.map((item) => (
                              <div key={`merch-band-${item.band}`} className="controlhub-merchandising-pill-card">
                                <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(item.band)}`}>
                                  {merchandisingPriceBandLabels[item.band] || item.band}
                                </span>
                                <strong>{item.count}</strong>
                              </div>
                            ))}
                            {!merchandisingPriceBandSummary.length ? <p>{texts.none}</p> : null}
                          </div>
                        </article>

                        <article className="controlhub-card controlhub-merchandising-summary-card">
                          <div className="controlhub-card-head">
                            <h4>{locale === "zh" ? "本周推进动作" : "Actions This Week"}</h4>
                            <span className="controlhub-merchandising-caption">{locale === "zh" ? "把动作压缩到最关键三件事" : "Compress action into the three most important moves"}</span>
                          </div>
                          <div className="controlhub-merchandising-ranked-list">
                            {merchandisingExecutiveActions.map((item) => (
                              <div key={`exec-action-${item.key}`} className="controlhub-merchandising-ranked-row compact">
                                <div>
                                  <strong>{item.title}</strong>
                                  <span>{locale === "zh" ? "涉及商品数" : "Impacted SKUs"}</span>
                                </div>
                                <span className={`controlhub-merchandising-badge is-${item.tone}`}>{item.count}</span>
                              </div>
                            ))}
                            {!merchandisingExecutiveActions.length ? <p>{texts.none}</p> : null}
                          </div>
                        </article>
                      </div>
                    </div>
                  </section>

                  <article className="controlhub-card controlhub-full-width controlhub-merchandising-section-card">
                    <div className="controlhub-card-head">
                      <div>
                        <h4>{locale === "zh" ? "重点推进名单" : "Priority Shortlist"}</h4>
                        <p className="controlhub-merchandising-caption">
                          {locale === "zh" ? "只保留最值得老板继续追问的商品。" : "Only keep the products worth immediate executive attention."}
                        </p>
                      </div>
                      <span className="controlhub-merchandising-section-count">{merchandisingExecutiveQueue.length}</span>
                    </div>
                    <div className="controlhub-merchandising-product-grid">
                      {merchandisingExecutiveQueue.map((item) => (
                        <article key={`exec-merchandising-${item.id}`} className="controlhub-merchandising-product-card is-priority">
                          <div className="controlhub-merchandising-item-head">
                            <div>
                              <strong>{item.name || "-"}</strong>
                              <span>{item.sku || "-"} · {item.category || "-"}</span>
                            </div>
                            <span className={`controlhub-merchandising-badge is-${item?.merchandisingStatus === "ready" ? "positive" : "warning"}`}>
                              {item?.merchandisingStatus === "ready" ? (locale === "zh" ? "可推进" : "Ready") : (locale === "zh" ? "待完善" : "Needs Update")}
                            </span>
                          </div>
                          <div className="controlhub-merchandising-board-stats compact">
                            <div>
                              <span>{locale === "zh" ? "价格" : "Price"}</span>
                              <strong>${item.priceUSD || 0}</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "毛利带" : "Margin"}</span>
                              <strong>{item.grossMarginRate || 0}%</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "准备度" : "Readiness"}</span>
                              <strong>{item.readinessScore || 0}/6</strong>
                            </div>
                          </div>
                          <div className="controlhub-merchandising-tag-list">
                            {(Array.isArray(item.targetMarkets) ? item.targetMarkets : []).map((market) => (
                              <span key={`exec-market-${item.id}-${market}`} className="controlhub-merchandising-badge is-neutral">{market}</span>
                            ))}
                            <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(item.sourcingPriority)}`}>
                              {merchandisingStatusLabels[item.sourcingPriority] || item.sourcingPriority}
                            </span>
                          </div>
                        </article>
                      ))}
                      {!merchandisingExecutiveQueue.length ? <p>{texts.none}</p> : null}
                    </div>
                  </article>
                </>
              ) : null}

              {merchandisingView === "collection" ? (
                <>
                  <section className="controlhub-full-width controlhub-merchandising-view-banner is-collection">
                    <div className="controlhub-merchandising-view-banner-copy">
                      <p className="controlhub-merchandising-eyebrow">{locale === "zh" ? "Collected Source Pool" : "Collected Source Pool"}</p>
                      <h4>{locale === "zh" ? "候选采集池" : "Collected Candidate Pool"}</h4>
                      <p>
                        {locale === "zh"
                          ? "先把第三方采集线索按状态分层，再决定哪些线索真正值得转进正式选品工作台。"
                          : "Triage third-party leads by import state first, then move only strong candidates into the formal merchandising workspace."}
                      </p>
                    </div>
                    <div className="controlhub-merchandising-view-banner-grid">
                      <article className="controlhub-merchandising-view-banner-card">
                        <span>{locale === "zh" ? "采集池总量" : "Pool Size"}</span>
                        <strong>{collectedProducts.length}</strong>
                        <small>{locale === "zh" ? "第三方采集候选线索" : "Third-party candidate leads"}</small>
                      </article>
                      <article className="controlhub-merchandising-view-banner-card">
                        <span>{locale === "zh" ? "待转入" : "Pending Import"}</span>
                        <strong>{collectedProductsPendingCount}</strong>
                        <small>{locale === "zh" ? "尚未进入正式工作台" : "Not yet moved into the formal workspace"}</small>
                      </article>
                      <article className="controlhub-merchandising-view-banner-card">
                        <span>{locale === "zh" ? "已加入选品" : "Imported"}</span>
                        <strong>{collectedProductsImportedCount}</strong>
                        <small>{locale === "zh" ? "已进入当前选品节奏" : "Already in current merchandising flow"}</small>
                      </article>
                    </div>
                  </section>

                  <article className="controlhub-card controlhub-full-width controlhub-merchandising-section-card is-collection-board">
                    <div className="controlhub-card-head">
                      <div>
                        <h4>{locale === "zh" ? "采集池列表" : "Collected Pool"}</h4>
                        <p className="controlhub-merchandising-caption">
                          {locale === "zh"
                            ? "这里先只读展示采集商品，并提供“加入选品”动作。"
                            : "This is a read-only pool of collected products with a direct add-to-merchandising action."}
                        </p>
                      </div>
                      <div className="row-actions controlhub-merchandising-hero-actions">
                        <button type="button" className="controlhub-button ghost" onClick={fetchCollectedProducts}>
                          {texts.refresh}
                        </button>
                      </div>
                    </div>

                    {collectedProductsLoading ? <p>{locale === "zh" ? "采集池加载中..." : "Loading collected pool..."}</p> : null}

                    {!collectedProductsLoading ? (
                      <>
                        <div className="table-wrapper">
                          <table className="controlhub-table">
                            <thead>
                              <tr>
                                <th>{locale === "zh" ? "图片" : "Image"}</th>
                                <th>{locale === "zh" ? "候选商品" : "Candidate"}</th>
                                <th>{locale === "zh" ? "价格 / 采集时间" : "Price / Collected Time"}</th>
                                <th>{locale === "zh" ? "来源" : "Source"}</th>
                                <th>{locale === "zh" ? "字段元数据" : "Field Metadata"}</th>
                                <th>{locale === "zh" ? "状态" : "Status"}</th>
                                <th>{texts.actions}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {paginatedCollectedProductRows.items.map((item) => {
                                const isImported = Boolean(item.isFormalSelection) || Number(item.draftPid || 0) > 0;
                                const itemRecordKey = String(item.recordKey || item.id || "");
                                const isActing = collectedProductActionId === itemRecordKey;
                                const sourceLink = normalizeExternalLink(item.itemUrl);
                                const detailObjectKey = String(item.detailObjectKey || "").trim();
                                const detailSha256 = String(item.detailSha256 || "").trim();
                                const detailCompression = String(item.detailCompression || "").trim();
                                return (
                                  <tr key={itemRecordKey}>
                                    <td>
                                      {item.imageUrl ? (
                                        <img
                                          src={item.imageUrl}
                                          alt={item.title || "Collected product"}
                                          style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 12 }}
                                        />
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td>
                                      <div>
                                        {sourceLink ? (
                                          <strong>
                                            <a href={sourceLink} target="_blank" rel="noreferrer">
                                              {item.title || sourceLink}
                                            </a>
                                          </strong>
                                        ) : (
                                          <strong>{item.title || "-"}</strong>
                                        )}
                                        <div>{item.keywords || item.seller || "-"}</div>
                                        <div>
                                          {locale === "zh" ? "主键" : "Key"}
                                          {`: PID ${formatCollectedMetaValue(item.productId)} / CID ${formatCollectedMetaValue(item.customerId)} / ${formatCollectedPlatformLabel(item.commercePlatform)}`}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <strong>{formatCollectedPrice(item)}</strong>
                                        <div>{formatUserTime(item.createdAt || item.updatedAt || item.collectionTime)}</div>
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <strong>{formatCollectedPlatformLabel(item.commercePlatform)}</strong>
                                        <div>{item.seller || texts.none}</div>
                                        <div>
                                          {locale === "zh" ? "原类目 / 国际站类目" : "Category / ICBU Category"}
                                          {`: ${formatCollectedMetaValue(item.categoryId)} / ${formatCollectedMetaValue(item.icbuCategoryId)}`}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <div>
                                          {locale === "zh" ? "草稿商品ID" : "Draft PID"}
                                          {`: ${formatCollectedMetaValue(item.draftPid)}`}
                                        </div>
                                        <div>
                                          {locale === "zh" ? "详情对象键" : "Detail Object Key"}
                                          {": "}
                                          <span title={detailObjectKey || ""} style={{ wordBreak: "break-all" }}>
                                            {formatCollectedMetaValue(detailObjectKey)}
                                          </span>
                                        </div>
                                        <div>
                                          {locale === "zh" ? "详情SHA256" : "Detail SHA256"}
                                          {": "}
                                          <span title={detailSha256 || ""} style={{ wordBreak: "break-all" }}>
                                            {formatCollectedMetaValue(detailSha256)}
                                          </span>
                                        </div>
                                        <div>
                                          {locale === "zh" ? "压缩方式" : "Compression"}
                                          {`: ${formatCollectedMetaValue(detailCompression)}`}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div>
                                        <span className={`controlhub-merchandising-badge is-${isImported ? "positive" : "warning"}`}>
                                          {isImported
                                            ? (locale === "zh" ? "已导入" : "Imported")
                                            : (locale === "zh" ? "待转入" : "Pending")}
                                        </span>
                                        <div style={{ marginTop: 8 }}>
                                          {locale === "zh" ? "Redis缓存" : "Redis Cache"}
                                          {`: ${item.isCached ? (locale === "zh" ? "已缓存" : "Cached") : (locale === "zh" ? "未缓存" : "Not Cached")}`}
                                        </div>
                                      </div>
                                    </td>
                                    <td>
                                      <div className="row-actions">
                                        {!isImported ? (
                                          <button
                                            type="button"
                                            className="controlhub-button"
                                            disabled={isActing}
                                            onClick={() => importCollectedProduct(itemRecordKey)}
                                          >
                                            {isActing ? texts.loading : (locale === "zh" ? "加入选品" : "Import")}
                                          </button>
                                        ) : null}
                                        {isImported ? (
                                          <button
                                            type="button"
                                            className="controlhub-button ghost"
                                            disabled={isActing}
                                            onClick={() => removeCollectedProduct(itemRecordKey)}
                                          >
                                            {isActing ? texts.loading : (locale === "zh" ? "移出选品" : "Remove")}
                                          </button>
                                        ) : null}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                        {renderPagination("collectedProducts", paginatedCollectedProductRows)}
                      </>
                    ) : null}
                  </article>
                </>
              ) : null}

              {merchandisingView === "risk" ? (
                <>
                  <section className="controlhub-full-width controlhub-merchandising-risk-layout">
                    <article className="controlhub-card controlhub-merchandising-section-card is-filter-panel">
                      <div className="controlhub-card-head">
                        <div>
                          <h4>{locale === "zh" ? "风险筛选" : "Risk Filters"}</h4>
                          <p className="controlhub-merchandising-caption">
                            {locale === "zh" ? "按市场、价格带、MOQ 与样品/合规状态筛出真正阻塞推进的项目。" : "Filter blockers by market, price band, MOQ, and sample/compliance readiness."}
                          </p>
                        </div>
                      </div>
                      <div className="controlhub-merchandising-filter-toolbar">
                        <div className="controlhub-merchandising-filter-summary">
                          <strong>
                            {locale === "zh"
                              ? `当前生效 ${activeMerchandisingFilterCount} 个筛选条件`
                              : `${activeMerchandisingFilterCount} active filter conditions`}
                          </strong>
                          <span>
                            {locale === "zh"
                              ? "建议先缩小到真实阻塞项，再推进重点商品。"
                              : "Narrow to real blockers first, then drive the shortlist forward."}
                          </span>
                        </div>
                        <div className="row-actions controlhub-merchandising-filter-actions">
                          <button
                            type="button"
                            className="controlhub-button ghost"
                            onClick={clearMerchandisingFilters}
                            disabled={!activeMerchandisingFilterCount}
                          >
                            {locale === "zh" ? "清空筛选" : "Clear Filters"}
                          </button>
                        </div>
                      </div>
                      {activeMerchandisingFilterCount ? (
                        <div className="controlhub-merchandising-filter-pill-row">
                          {activeMerchandisingFilterEntries.map(([key, value]) => {
                            let normalizedValue = value;
                            if (key === "priceBand") {
                              normalizedValue = merchandisingPriceBandLabels[value] || value;
                            } else if (key === "sampleStatus" || key === "complianceStatus") {
                              normalizedValue = merchandisingStatusLabels[value] || value;
                            }

                            return (
                              <span key={`merch-filter-pill-${key}`} className="controlhub-merchandising-badge is-accent">
                                {`${merchandisingFilterPillLabels[key] || key}: ${normalizedValue}`}
                              </span>
                            );
                          })}
                        </div>
                      ) : null}
                      <div className="controlhub-merchandising-filter-row">
                        <label>
                          <span>{locale === "zh" ? "目标市场" : "Target Market"}</span>
                          <select
                            value={merchandisingFilters.targetMarket}
                            onChange={(event) => setMerchandisingFilters((prev) => ({ ...prev, targetMarket: event.target.value }))}
                          >
                            <option value="">{texts.none}</option>
                            {merchandisingFilterOptions.targetMarkets.map((market) => (
                              <option key={`market-filter-${market}`} value={market}>{market}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>{locale === "zh" ? "价格带" : "Price Band"}</span>
                          <select
                            value={merchandisingFilters.priceBand}
                            onChange={(event) => setMerchandisingFilters((prev) => ({ ...prev, priceBand: event.target.value }))}
                          >
                            <option value="">{texts.none}</option>
                            {merchandisingFilterOptions.priceBands.map((band) => (
                              <option key={`band-filter-${band}`} value={band}>{merchandisingPriceBandLabels[band] || band}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>{locale === "zh" ? "MOQ" : "MOQ"}</span>
                          <select
                            value={merchandisingFilters.moqRange}
                            onChange={(event) => setMerchandisingFilters((prev) => ({ ...prev, moqRange: event.target.value }))}
                          >
                            <option value="">{texts.none}</option>
                            <option value="lte300">&lt;= 300</option>
                            <option value="301to500">301 - 500</option>
                            <option value="gt500">&gt; 500</option>
                          </select>
                        </label>
                        <label>
                          <span>{locale === "zh" ? "样品状态" : "Sample Status"}</span>
                          <select
                            value={merchandisingFilters.sampleStatus}
                            onChange={(event) => setMerchandisingFilters((prev) => ({ ...prev, sampleStatus: event.target.value }))}
                          >
                            <option value="">{texts.none}</option>
                            {merchandisingFilterOptions.sampleStatuses.map((statusKey) => (
                              <option key={`sample-filter-${statusKey}`} value={statusKey}>{merchandisingStatusLabels[statusKey] || statusKey}</option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span>{locale === "zh" ? "合规状态" : "Compliance Status"}</span>
                          <select
                            value={merchandisingFilters.complianceStatus}
                            onChange={(event) => setMerchandisingFilters((prev) => ({ ...prev, complianceStatus: event.target.value }))}
                          >
                            <option value="">{texts.none}</option>
                            {merchandisingFilterOptions.complianceStatuses.map((statusKey) => (
                              <option key={`compliance-filter-${statusKey}`} value={statusKey}>{merchandisingStatusLabels[statusKey] || statusKey}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    </article>

                    <article className="controlhub-card controlhub-merchandising-section-card controlhub-merchandising-risk-summary-card">
                      <div className="controlhub-card-head">
                        <div>
                          <h4>{locale === "zh" ? "风险驾驶舱" : "Risk Cockpit"}</h4>
                          <p className="controlhub-merchandising-caption">
                            {locale === "zh" ? "先看最卡进度的商品，再决定跟进顺序。" : "Look at the items slowing progress first, then set follow-up order."}
                          </p>
                        </div>
                      </div>
                      <div className="controlhub-merchandising-view-banner-grid is-tight">
                        <article className="controlhub-merchandising-view-banner-card">
                          <span>{locale === "zh" ? "当前风险项" : "Open Risks"}</span>
                          <strong>{filteredMerchandisingAlertRows.length}</strong>
                          <small>{locale === "zh" ? "仍阻塞推进的商品" : "Items still blocking execution"}</small>
                        </article>
                        <article className="controlhub-merchandising-view-banner-card">
                          <span>{locale === "zh" ? "激活筛选" : "Active Filters"}</span>
                          <strong>{activeMerchandisingFilterCount}</strong>
                          <small>{locale === "zh" ? "正在缩小风险范围" : "Currently narrowing the blocker set"}</small>
                        </article>
                      </div>
                      {merchandisingRiskLeadItem ? (
                        <div className="controlhub-merchandising-inline-spotlight">
                          <div>
                            <strong>{merchandisingRiskLeadItem.name || "-"}</strong>
                            <span>{locale === "zh" ? "当前优先排查的阻塞项" : "Highest-priority blocker to inspect now"}</span>
                          </div>
                          <span className={`controlhub-merchandising-badge is-${Number(merchandisingRiskLeadItem.readinessScore || 0) >= 5 ? "positive" : "danger"}`}>
                            {locale === "zh" ? `准备度 ${merchandisingRiskLeadItem.readinessScore || 0}/6` : `Readiness ${merchandisingRiskLeadItem.readinessScore || 0}/6`}
                          </span>
                        </div>
                      ) : null}
                    </article>
                  </section>

                  <article className="controlhub-card controlhub-full-width controlhub-merchandising-section-card">
                    <div className="controlhub-card-head">
                      <div>
                        <h4>{locale === "zh" ? "待处理风险" : "Open Risks"}</h4>
                        <p className="controlhub-merchandising-caption">
                          {locale === "zh" ? "这里只保留真正拖慢推进速度的阻塞项。" : "Only the blockers that materially slow execution stay here."}
                        </p>
                      </div>
                      <span className="controlhub-merchandising-section-count">{filteredMerchandisingAlertRows.length}</span>
                    </div>
                    <div className="controlhub-merchandising-alert-grid">
                      {filteredMerchandisingAlertRows.map((item) => {
                        const readinessPercent = Math.round((Number(item.readinessScore || 0) / 6) * 100);
                        return (
                          <article key={`merch-alert-${item.id}`} className="controlhub-merchandising-alert-item">
                            <div className="controlhub-merchandising-item-head">
                              <div>
                                <strong>{item.name || "-"}</strong>
                                <span>{item.sku || "-"} · {item.category || "-"}</span>
                              </div>
                              <span className={`controlhub-merchandising-badge is-${Number(item.readinessScore || 0) >= 5 ? "positive" : "danger"}`}>
                                {locale === "zh" ? `准备度 ${item.readinessScore || 0}/6` : `Readiness ${item.readinessScore || 0}/6`}
                              </span>
                            </div>
                            <div className="controlhub-merchandising-tag-list">
                              {Array.isArray(item.selectionAlerts) && item.selectionAlerts.length
                                ? item.selectionAlerts.map((alertKey) => (
                                  <span key={`alert-${item.id}-${alertKey}`} className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(alertKey)}`}>
                                    {merchandisingAlertLabels[alertKey] || alertKey}
                                  </span>
                                ))
                                : <span className="controlhub-merchandising-badge is-neutral">{texts.none}</span>}
                            </div>
                            <div className="controlhub-merchandising-progress-block">
                              <div className="controlhub-merchandising-progress-track">
                                <span style={{ width: `${Math.max(8, readinessPercent)}%` }} />
                              </div>
                              <small>
                                {locale === "zh"
                                  ? `语言覆盖 ${item.translationCoverage || 0}/4，素材评分 ${item.readinessScore || 0}/6`
                                  : `Translation ${item.translationCoverage || 0}/4, asset score ${item.readinessScore || 0}/6`}
                              </small>
                            </div>
                          </article>
                        );
                      })}
                      {!filteredMerchandisingAlertRows.length ? <p>{texts.none}</p> : null}
                    </div>
                  </article>
                </>
              ) : null}

              {merchandisingView === "decision" ? (
                <>
                  <section className="controlhub-full-width controlhub-merchandising-decision-layout">
                    <div className="controlhub-merchandising-summary-grid is-compact">
                      <article className="controlhub-card controlhub-merchandising-summary-card">
                        <div className="controlhub-card-head">
                          <h4>{locale === "zh" ? "类目覆盖" : "Category Coverage"}</h4>
                          <span className="controlhub-merchandising-caption">{locale === "zh" ? "先看成熟类目，再看扩张类目" : "Start with mature categories, then expansion bets"}</span>
                        </div>
                        <div className="controlhub-merchandising-ranked-list">
                          {merchandisingCategorySummary.slice(0, 5).map((item) => (
                            <div key={`merch-category-${item.category}`} className="controlhub-merchandising-ranked-row">
                              <div>
                                <strong>{item.category}</strong>
                                <span>{locale === "zh" ? `可推进 ${item.readyCount} / 总计 ${item.count}` : `${item.readyCount} ready / ${item.count} total`}</span>
                              </div>
                              <div className="controlhub-merchandising-bar-track is-soft">
                                <span style={{ width: `${Math.max(10, (Number(item.count || 0) / merchandisingSummaryMaxima.category) * 100)}%` }} />
                              </div>
                            </div>
                          ))}
                          {!merchandisingCategorySummary.length ? <p>{texts.none}</p> : null}
                        </div>
                      </article>

                      <article className="controlhub-card controlhub-merchandising-summary-card">
                        <div className="controlhub-card-head">
                          <h4>{locale === "zh" ? "目标市场分布" : "Target Market Mix"}</h4>
                          <span className="controlhub-merchandising-caption">{locale === "zh" ? "帮助判断扩张方向" : "Helps judge expansion direction"}</span>
                        </div>
                        <div className="controlhub-merchandising-ranked-list">
                          {merchandisingMarketSummary.slice(0, 5).map((item) => (
                            <div key={`merch-market-${item.market}`} className="controlhub-merchandising-ranked-row compact">
                              <div>
                                <strong>{item.market}</strong>
                                <span>{locale === "zh" ? `${item.count} 个商品涉及` : `${item.count} products tagged`}</span>
                              </div>
                              <div className="controlhub-merchandising-bar-track is-soft">
                                <span style={{ width: `${Math.max(10, (Number(item.count || 0) / merchandisingSummaryMaxima.market) * 100)}%` }} />
                              </div>
                            </div>
                          ))}
                          {!merchandisingMarketSummary.length ? <p>{texts.none}</p> : null}
                        </div>
                      </article>
                    </div>

                    <article className="controlhub-card controlhub-merchandising-decision-spotlight">
                      <div className="controlhub-card-head">
                        <div>
                          <h4>{locale === "zh" ? "决策焦点" : "Decision Spotlight"}</h4>
                          <span className="controlhub-merchandising-caption">{locale === "zh" ? "用一张卡先看当前最值得判断的商品。" : "Use one card to anchor the next decision first."}</span>
                        </div>
                      </div>
                      {merchandisingDecisionLeadItem ? (
                        <>
                          <div className="controlhub-merchandising-inline-spotlight">
                            <div>
                              <strong>{merchandisingDecisionLeadItem.name || "-"}</strong>
                              <span>{merchandisingChannelLabels[merchandisingDecisionLeadItem.channelFocus] || merchandisingDecisionLeadItem.channelFocus || texts.none}</span>
                            </div>
                            <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(merchandisingDecisionLeadItem.sourcingPriority)}`}>
                              {merchandisingStatusLabels[merchandisingDecisionLeadItem.sourcingPriority] || merchandisingDecisionLeadItem.sourcingPriority}
                            </span>
                          </div>
                          <div className="controlhub-merchandising-overview-lead-stats is-compact-grid">
                            <div>
                              <span>MOQ</span>
                              <strong>{merchandisingDecisionLeadItem.moq || 0}</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "交期" : "Lead Time"}</span>
                              <strong>{merchandisingDecisionLeadItem.supplierLeadDays || 0}d</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "毛利带" : "Margin"}</span>
                              <strong>{merchandisingDecisionLeadItem.grossMarginRate || 0}%</strong>
                            </div>
                          </div>
                        </>
                      ) : <p className="controlhub-merchandising-caption">{texts.none}</p>}
                    </article>
                  </section>

                  <article className="controlhub-card controlhub-full-width controlhub-merchandising-section-card">
                    <div className="controlhub-card-head">
                      <div>
                        <h4>{locale === "zh" ? "利润与市场决策板" : "Margin and Market Board"}</h4>
                        <p className="controlhub-merchandising-caption">
                          {locale === "zh" ? "集中看毛利、交期、MOQ 和渠道方向，不把细碎过程信息堆在老板面前。" : "Keep margin, lead time, MOQ, and channel direction visible without operational clutter."}
                        </p>
                      </div>
                      <span className="controlhub-merchandising-section-count">{filteredMerchandisingSourcingBoard.length}</span>
                    </div>
                    <div className="controlhub-merchandising-board-grid">
                      {filteredMerchandisingSourcingBoard.map((item) => (
                        <article key={`merch-sourcing-${item.id}`} className="controlhub-merchandising-board-item is-decision">
                          <div className="controlhub-merchandising-item-head">
                            <div>
                              <strong>{item.name || "-"}</strong>
                              <span>{item.sku || "-"}</span>
                            </div>
                            <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(item.sourcingPriority)}`}>
                              {merchandisingStatusLabels[item.sourcingPriority] || item.sourcingPriority}
                            </span>
                          </div>
                          <p className="controlhub-merchandising-board-copy">{merchandisingChannelLabels[item.channelFocus] || item.channelFocus}</p>
                          <div className="controlhub-merchandising-board-stats">
                            <div>
                              <span>MOQ</span>
                              <strong>{item.moq || 0}</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "交期" : "Lead Time"}</span>
                              <strong>{item.supplierLeadDays || 0}d</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "毛利带" : "Margin"}</span>
                              <strong>{item.grossMarginRate || 0}%</strong>
                            </div>
                          </div>
                          <div className="controlhub-merchandising-tag-list">
                            {(Array.isArray(item.targetMarkets) ? item.targetMarkets : []).map((market) => (
                              <span key={`market-tag-${item.id}-${market}`} className="controlhub-merchandising-badge is-neutral">{market}</span>
                            ))}
                          </div>
                        </article>
                      ))}
                      {!filteredMerchandisingSourcingBoard.length ? <p>{texts.none}</p> : null}
                    </div>
                  </article>
                </>
              ) : null}

              {merchandisingView === "focus" ? (
                <>
                  <section className="controlhub-full-width controlhub-merchandising-view-banner is-focus">
                    <div className="controlhub-merchandising-view-banner-copy">
                      <p className="controlhub-merchandising-eyebrow">{locale === "zh" ? "Focus Shortlist" : "Focus Shortlist"}</p>
                      <h4>{locale === "zh" ? "重点名单" : "Focus List"}</h4>
                      <p>
                        {locale === "zh"
                          ? "把候选商品压缩成适合快速过会的一组重点卡片，优先看最有可能推进的商品。"
                          : "Compress the candidate set into a fast-review shortlist, led by the products most likely to move forward."}
                      </p>
                    </div>
                    <div className="controlhub-merchandising-view-banner-grid">
                      <article className="controlhub-merchandising-view-banner-card">
                        <span>{locale === "zh" ? "重点商品" : "Focus SKUs"}</span>
                        <strong>{merchandisingExecutiveQueue.length}</strong>
                        <small>{locale === "zh" ? "压缩后的会议名单" : "Compressed shortlist for review"}</small>
                      </article>
                      <article className="controlhub-merchandising-view-banner-card">
                        <span>{locale === "zh" ? "优先动作" : "Priority Actions"}</span>
                        <strong>{merchandisingExecutiveActions.length}</strong>
                        <small>{locale === "zh" ? "建议优先推进的事项" : "Recommended next actions"}</small>
                      </article>
                    </div>
                  </section>

                  <article className="controlhub-card controlhub-full-width controlhub-merchandising-section-card">
                    <div className="controlhub-card-head">
                      <div>
                        <h4>{locale === "zh" ? "重点名单" : "Focus List"}</h4>
                        <p className="controlhub-merchandising-caption">
                          {locale === "zh" ? "保留压缩后的候选名单，适合快速过会。" : "A compressed shortlist designed for faster decision reviews."}
                        </p>
                      </div>
                      <span className="controlhub-merchandising-section-count">{merchandisingExecutiveQueue.length}</span>
                    </div>
                    <div className="controlhub-merchandising-product-grid">
                      {merchandisingExecutiveQueue.map((item) => (
                        <article key={`merchandising-${item.id}`} className="controlhub-merchandising-product-card is-focus">
                          <div className="controlhub-merchandising-item-head">
                            <div>
                              <strong>{item.name || "-"}</strong>
                              <span>{item.sku || "-"} · {item.model || "-"}</span>
                            </div>
                            <span className={`controlhub-merchandising-badge is-${item?.merchandisingStatus === "ready" ? "positive" : "warning"}`}>
                              {item?.merchandisingStatus === "ready" ? (locale === "zh" ? "可推进" : "Ready") : (locale === "zh" ? "待完善" : "Needs Update")}
                            </span>
                          </div>
                          <div className="controlhub-merchandising-subline">
                            <span>{item.category || "-"}</span>
                            <span>${item.priceUSD || 0}</span>
                            <span>{merchandisingPriceBandLabels[item.priceBand] || item.priceBand}</span>
                          </div>
                          <div className="controlhub-merchandising-board-stats compact">
                            <div>
                              <span>{locale === "zh" ? "毛利带" : "Margin"}</span>
                              <strong>{item.grossMarginRate || 0}%</strong>
                            </div>
                            <div>
                              <span>{locale === "zh" ? "准备度" : "Readiness"}</span>
                              <strong>{item.readinessScore || 0}/6</strong>
                            </div>
                            <div>
                              <span>MOQ</span>
                              <strong>{item.moq || 0}</strong>
                            </div>
                          </div>
                          <div className="controlhub-merchandising-tag-list">
                            <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(item.sampleStatus)}`}>
                              {merchandisingStatusLabels[item.sampleStatus] || item.sampleStatus}
                            </span>
                            <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(item.complianceStatus)}`}>
                              {merchandisingStatusLabels[item.complianceStatus] || item.complianceStatus}
                            </span>
                            <span className={`controlhub-merchandising-badge is-${getMerchandisingBadgeTone(item.packagingStatus)}`}>
                              {merchandisingStatusLabels[item.packagingStatus] || item.packagingStatus}
                            </span>
                          </div>
                        </article>
                      ))}
                      {!merchandisingExecutiveQueue.length ? <p>{texts.none}</p> : null}
                    </div>
                  </article>
                </>
              ) : null}
            </div>
          ) : null}

          {adminTab === "procurement" ? (
            <AdminProcurementPage
              locale={locale}
              texts={texts}
              scopedWorkspaceMetrics={scopedWorkspaceMetrics}
              procurementQueueRows={procurementQueueRows}
            />
          ) : null}

          {adminTab === "solutions" ? (
            <AdminQualityPage
              locale={locale}
              texts={texts}
              scopedWorkspaceMetrics={scopedWorkspaceMetrics}
              qualityQueueRows={qualityQueueRows}
            />
          ) : null}

          {adminTab === "logistics" ? (
            <AdminLogisticsPage
              locale={locale}
              texts={texts}
              scopedWorkspaceMetrics={scopedWorkspaceMetrics}
              logisticsQueueRows={logisticsQueueRows}
            />
          ) : null}

          {adminTab === "designNav" ? (
            <AdminStorefrontNavigationPage
              locale={locale}
              texts={texts}
              canEditStorefront={canEditStorefront}
              navMenuDraft={navMenuDraft}
              expandedNavRows={expandedNavRows}
              editingNavPath={editingNavPath}
              setEditingNavPath={setEditingNavPath}
              savingNavMenu={savingNavMenu}
              saveNavigationMenu={saveNavigationMenu}
              addNavMenuItem={addNavMenuItem}
              expandAllNavRows={expandAllNavRows}
              collapseAllNavRows={collapseAllNavRows}
              renderNavMenuRows={renderNavMenuRows}
            />
          ) : null}

          {adminTab === "storeBase" ? (
            <AdminStorefrontBasePage
              locale={locale}
              texts={texts}
              storeSettingsDraft={storeSettingsDraft}
              setStoreSettingsDraft={setStoreSettingsDraft}
              canEditStorefront={canEditStorefront}
              savingStoreSettings={savingStoreSettings}
              saveStoreBaseSettings={saveStoreBaseSettings}
              applyingStorefrontTheme={applyingStorefrontTheme}
              applyStorefrontThemeTemplate={applyStorefrontThemeTemplate}
              resetStorefrontThemeTemplate={resetStorefrontThemeTemplate}
            />
          ) : null}
          {adminTab === "templateStudio" ? (
            <AdminTemplateStudioPage
              locale={locale}
              texts={texts}
              storeSettingsDraft={storeSettingsDraft}
              setStoreSettingsDraft={setStoreSettingsDraft}
              canEditStorefront={canEditStorefront}
              savingStoreSettings={savingStoreSettings}
              saveTemplateStudioSettings={saveTemplateStudioSettings}
              saveAsCustomThemeTemplate={saveAsCustomThemeTemplate}
              applyCustomThemeTemplate={applyCustomThemeTemplate}
              clearActiveCustomThemeTemplate={clearActiveCustomThemeTemplate}
            />
          ) : null}
          {adminTab === "articleManagement" ? (
            <AdminArticleManagementPage
              locale={locale}
              texts={texts}
              blogArticleDrafts={blogArticleDrafts}
              maxBlogArticleItems={MAX_BLOG_ARTICLE_ITEMS}
              canManageTrustContent={canManageTrustContent}
              savingStoreSettings={savingStoreSettings}
              addBlogArticleDraft={addBlogArticleDraft}
              saveArticleManagementSettings={saveArticleManagementSettings}
              openBlogArticleEditor={openBlogArticleEditor}
              removeBlogArticleDraft={removeBlogArticleDraft}
            />
          ) : null}

          {adminTab === "designCarousel" ? (
            <AdminCarouselPage
              locale={locale}
              texts={texts}
              canEditStorefront={canEditStorefront}
              savingBanners={savingBanners}
              renderBannerListView={renderBannerListView}
              saveCarouselBanners={saveCarouselBanners}
              addBannerDraft={addBannerDraft}
            />
          ) : null}

          {adminTab === "companyImages" ? (
            <AdminCompanyImagesPage
              locale={locale}
              texts={texts}
              companyImageSectionsDraft={companyImageSectionsDraft}
              maxCompanyImageItems={MAX_COMPANY_IMAGE_ITEMS}
              canEditStorefront={canEditStorefront}
              reportStorefrontWriteDenied={reportStorefrontWriteDenied}
              savingCompanyImages={savingCompanyImages}
              saveCompanyImages={saveCompanyImages}
              showCustomCompanySectionInput={showCustomCompanySectionInput}
              setShowCustomCompanySectionInput={setShowCustomCompanySectionInput}
              customCompanySectionName={customCompanySectionName}
              setCustomCompanySectionName={setCustomCompanySectionName}
              addCustomCompanyImageSection={addCustomCompanyImageSection}
              getCompanyImageSectionLocaleLabels={getCompanyImageSectionLocaleLabels}
              updateCompanyImageSectionEnabled={updateCompanyImageSectionEnabled}
              companyImageLinkDrafts={companyImageLinkDrafts}
              updateCompanyImageLinkDraft={updateCompanyImageLinkDraft}
              handleAddCompanyImageLink={handleAddCompanyImageLink}
              uploadingCompanyImageSectionKey={uploadingCompanyImageSectionKey}
              handleClearCompanyImageSection={handleClearCompanyImageSection}
              normalizeCompanyImageValue={normalizeCompanyImageValue}
              toCompanyImagePreviewUrl={toCompanyImagePreviewUrl}
              companyImageDragging={companyImageDragging}
              handleCompanyImageDrop={handleCompanyImageDrop}
              openCompanyImagePreview={openCompanyImagePreview}
              handleCompanyImageDragStart={handleCompanyImageDragStart}
              handleCompanyImageDragEnd={handleCompanyImageDragEnd}
              moveCompanyImageUrl={moveCompanyImageUrl}
              handleRemoveCompanyImage={handleRemoveCompanyImage}
              copyCompanyImageLink={copyCompanyImageLink}
              uploadCompanyImages={uploadCompanyImages}
              companyIntroductionDraft={companyIntroductionDraft}
              setCompanyIntroductionDraft={setCompanyIntroductionDraft}
            />
          ) : null}

          {adminTab === "faqKnowledge" ? (
            <AdminFaqKnowledgePage
              locale={locale}
              texts={texts}
              faqKnowledgeDrafts={faqKnowledgeDrafts}
              maxFaqKnowledgeItems={MAX_FAQ_KNOWLEDGE_ITEMS}
              adminRole={adminRole}
              savingFaqKnowledge={savingFaqKnowledge}
              addFaqDraft={addFaqDraft}
              saveFaqKnowledge={saveFaqKnowledge}
              updateFaqDraftField={updateFaqDraftField}
              removeFaqDraft={removeFaqDraft}
              reviewFilterProductId={reviewFilterProductId}
              setReviewFilterProductId={setReviewFilterProductId}
              fetchAdminProductReviews={fetchAdminProductReviews}
              loadingProductReviews={loadingProductReviews}
              reviewDraft={reviewDraft}
              setReviewDraft={setReviewDraft}
              canManageTrustContent={canManageTrustContent}
              savingProductReview={savingProductReview}
              saveProductReviewDraft={saveProductReviewDraft}
              productReviewRows={productReviewRows}
              deleteProductReview={deleteProductReview}
            />
          ) : null}

          {adminTab === "orders" || adminTab === "ordersPaidQueue" || adminTab === "ordersInTransit" || adminTab === "ordersCompleted" || adminTab === "ordersAfterSales" ? (
            <AdminOrdersPage
              locale={locale}
              adminOrders={adminOrders}
              view={activeOrderView}
              orderRows={activeOrderRows}
              texts={texts}
              paginatedOrderRows={activePaginatedOrderRows}
              renderPagination={renderPagination}
              statusLabels={statusLabels}
              orderStatusOptions={ORDER_STATUS_OPTIONS}
              updateOrderStatus={updateOrderStatus}
              onRefresh={() => {
                void fetchAdminData();
              }}
            />
          ) : null}

          {adminTab === "customers" || adminTab === "customersSegments" || adminTab === "customersTags" ? (
            <AdminCustomersPage
              locale={locale}
              texts={texts}
              view={activeCustomerView}
              paginatedCustomerRows={activePaginatedCustomerRows}
              renderPagination={renderPagination}
              adminUsers={activeCustomerRows}
            />
          ) : null}

          {adminTab === "marketing" || adminTab === "marketingPoster" || adminTab === "marketingEmail" || adminTab === "discounts" || adminTab === "finance" || adminTab === "financeIncome" || adminTab === "financeExpenses" || adminTab === "financeReceivables" || adminTab === "financePayables" || adminTab === "financeInvoices" || adminTab === "financeTax" || adminTab === "financeBudget" || adminTab === "financeDaily" || adminTab === "financeMonthly" || adminTab === "financeYearly" || adminTab === "financeReconciliation" || adminTab === "financeApprovals" || adminTab === "financeReports" || adminTab === "analytics" || adminTab === "analyticsSales" || adminTab === "analyticsProducts" || adminTab === "analyticsCustomers" ? (
            <AdminInsightsPage
              adminTab={adminTab}
              locale={locale}
              adminOverview={adminOverview}
              adminOrders={adminOrders}
              adminUsers={adminUsers}
              operationsBanners={operationsBanners}
              scopedWorkspaceMetrics={scopedWorkspaceMetrics}
              adminQuery={adminQuery}
              financeWorkspaceData={financeWorkspaceData}
              onSaveFinanceWorkspace={saveFinanceWorkspace}
            />
          ) : null}

          {adminError ? <p className="state error">{adminError}</p> : null}
      </AdminDashboardShell>

      {passwordModalUser ? (
        <div className="controlhub-user-password-overlay" role="dialog" aria-modal="true">
          <div className="controlhub-user-password-modal">
            <div className="controlhub-user-password-head">
              <h3>{texts.userPasswordModalTitle}</h3>
              <button type="button" className="controlhub-user-password-close" onClick={closeUserPasswordModal}>
                ✕
              </button>
            </div>
            <p className="controlhub-user-password-target">
              {texts.targetUser}: {passwordModalUser.username || passwordModalUser.name || passwordModalUser.id}
            </p>
            <p className="controlhub-user-password-target">{texts.adminResetPasswordHint}</p>
            <form className="controlhub-user-password-form" onSubmit={submitUserPasswordChange}>
              <label>
                <span>{texts.newPassword}</span>
                <input
                  type="password"
                  value={userPasswordForm.newPassword}
                  onChange={(event) =>
                    setUserPasswordForm((prev) => ({ ...prev, newPassword: event.target.value }))
                  }
                  required
                />
              </label>
              <label>
                <span>{texts.confirmPassword}</span>
                <input
                  type="password"
                  value={userPasswordForm.confirmPassword}
                  onChange={(event) =>
                    setUserPasswordForm((prev) => ({ ...prev, confirmPassword: event.target.value }))
                  }
                  required
                />
              </label>
              {userPasswordNotice ? <p className="state">{userPasswordNotice}</p> : null}
              <div className="row-actions">
                <button type="submit" className="controlhub-button" disabled={userPasswordSaving}>
                  {userPasswordSaving ? `${texts.save}...` : texts.savePassword}
                </button>
                <button
                  type="button"
                  className="controlhub-button ghost"
                  onClick={closeUserPasswordModal}
                  disabled={userPasswordSaving}
                >
                  {texts.cancel}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {editUserModalUser ? (
        <div className="controlhub-user-password-overlay" role="dialog" aria-modal="true">
          <div className="controlhub-user-password-modal">
            {(() => {
              const canEditAccount = canManageUserAccount(editUserModalUser);
              const canAssignPosition = canAssignUserPosition(editUserModalUser);
              const canEditRole = canEditAccount && accessCapabilitySet.has("admin:users:role:write");
              return (
                <>
            <div className="controlhub-user-password-head">
              <h3>{texts.editUserTitle || texts.profileCardTitle}</h3>
              <button type="button" className="controlhub-user-password-close" onClick={closeEditUserModal}>
                ✕
              </button>
            </div>
            <p className="controlhub-user-password-target">
              {texts.targetUser}: {editUserModalUser.username || editUserModalUser.name || editUserModalUser.id}
            </p>
            <form className="controlhub-user-password-form" onSubmit={submitEditUser}>
              <label>
                <span>{texts.nickname}</span>
                <input
                  value={editUserForm.name}
                  onChange={(event) => setEditUserForm((prev) => ({ ...prev, name: event.target.value }))}
                  disabled={!canEditAccount}
                />
              </label>
              <label>
                <span>{texts.email}</span>
                <input
                  type="email"
                  value={editUserForm.email}
                  onChange={(event) => setEditUserForm((prev) => ({ ...prev, email: event.target.value }))}
                  disabled={!canEditAccount}
                />
              </label>
              <label>
                <span>{texts.phone}</span>
                <input
                  value={editUserForm.phone}
                  onChange={(event) => setEditUserForm((prev) => ({ ...prev, phone: event.target.value }))}
                  disabled={!canEditAccount}
                />
              </label>
              <label>
                <span>{texts.userRole}</span>
                <select
                  value={editUserForm.role}
                  onChange={(event) => setEditUserForm((prev) => ({ ...prev, role: event.target.value }))}
                  disabled={!canEditRole}
                >
                  {assignableRoleOptions.map((option) => (
                    <option key={`edit-user-role-${option.value}`} value={option.value}>
                      {roleLabels[option.value] || option.label || option.value}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>{texts.accountStatus}</span>
                <select
                  value={editUserForm.status}
                  onChange={(event) => setEditUserForm((prev) => ({ ...prev, status: event.target.value }))}
                  disabled={!canEditAccount}
                >
                  <option value="active">{texts.statusActive}</option>
                  <option value="disactive">{texts.statusDisactive}</option>
                </select>
              </label>
              <label>
                <span>{texts.position}</span>
                <select
                  value={editUserForm.jobTitle}
                  onChange={(event) => setEditUserForm((prev) => ({ ...prev, jobTitle: event.target.value }))}
                  disabled={!canAssignPosition}
                >
                  <option value="">-</option>
                  {positionSelectOptions.map((positionName) => (
                    <option key={`edit-user-position-${positionName}`} value={positionName}>
                      {positionName}
                    </option>
                  ))}
                </select>
              </label>
              {editUserNotice ? <p className="state">{editUserNotice}</p> : null}
              <div className="row-actions">
                <button type="submit" className="controlhub-button" disabled={editUserSaving}>
                  {editUserSaving ? `${texts.save}...` : texts.save}
                </button>
                <button type="button" className="controlhub-button ghost" onClick={closeEditUserModal} disabled={editUserSaving}>
                  {texts.cancel}
                </button>
              </div>
            </form>
                </>
              );
            })()}
          </div>
        </div>
      ) : null}

      {deleteUserModalUser ? (
        <div className="controlhub-user-password-overlay" role="dialog" aria-modal="true">
          <div className="controlhub-user-password-modal">
            <div className="controlhub-user-password-head">
              <h3>{texts.deleteUserTitle || texts.remove}</h3>
              <button type="button" className="controlhub-user-password-close" onClick={closeDeleteUserModal}>
                ✕
              </button>
            </div>
            <p className="controlhub-user-password-target">
              {texts.targetUser}: {deleteUserModalUser.username || deleteUserModalUser.name || deleteUserModalUser.id}
            </p>
            <p>{texts.deleteUserConfirmHint || "Type DELETE to confirm this action."}</p>
            <form className="controlhub-user-password-form" onSubmit={submitDeleteUser}>
              <label>
                <span>DELETE</span>
                <input
                  value={deleteUserConfirmText}
                  onChange={(event) => setDeleteUserConfirmText(event.target.value)}
                  autoComplete="off"
                />
              </label>
              {deleteUserNotice ? <p className="state error">{deleteUserNotice}</p> : null}
              <div className="row-actions">
                <button type="submit" className="controlhub-button danger" disabled={deleteUserSaving}>
                  {deleteUserSaving ? `${texts.remove}...` : texts.remove}
                </button>
                <button type="button" className="controlhub-button ghost" onClick={closeDeleteUserModal} disabled={deleteUserSaving}>
                  {texts.cancel}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {positionPermissionModal ? (
        <div className="controlhub-user-password-overlay" role="dialog" aria-modal="true">
          <div className="controlhub-user-password-modal controlhub-position-permission-modal">
            <div className="controlhub-user-password-head">
              <h3>{texts.permissionSelectTitle}</h3>
              <button type="button" className="controlhub-user-password-close" onClick={closePositionPermissionModal}>
                ✕
              </button>
            </div>
            <p className="controlhub-user-password-target">
              <span>{texts.permissionSelectedSummary}: </span>
              {(positionPermissionSelection || []).length ? (
                <span className="position-permission-selected-list">
                  {normalizePermissionKeys(positionPermissionSelection).map((permissionKey) => (
                    <button
                      type="button"
                      key={`selected-permission-${permissionKey}`}
                      className="position-permission-selected-chip"
                      onClick={() => focusPermissionSelection(permissionKey)}
                    >
                      {formatCapabilityLabel(permissionKey, locale) || permissionKey}
                    </button>
                  ))}
                </span>
              ) : (
                texts.none
              )}
            </p>

            <div className="position-permission-search-wrap">
              <input
                type="text"
                className="position-permission-search-input"
                placeholder={texts.permissionQuickSearchPlaceholder || texts.search}
                value={positionPermissionSearch}
                onChange={(event) => setPositionPermissionSearch(event.target.value)}
              />
              {String(positionPermissionSearch || "").trim() ? (
                <div className="position-permission-search-results">
                  {quickPermissionResults.length ? (
                    quickPermissionResults.map((item) => (
                      <button
                        type="button"
                        key={`position-permission-search-${item.key}`}
                        className="position-permission-search-item"
                        onClick={() => quickSelectPermission(item.key)}
                      >
                        <span className="position-permission-search-key">{item.name || item.key}</span>
                      </button>
                    ))
                  ) : (
                    <div className="position-permission-search-empty">{texts.none}</div>
                  )}
                </div>
              ) : null}
            </div>

            <div className="position-permission-inline-dropdown-list">
              {groupedPermissionRows.map((groupItem) => {
                const target = String(positionPermissionModal?.target || "");
                const expandedGroup = expandedPermissionGroupByTarget[target] || groupedPermissionRows[0]?.group || "";
                const isOpen = expandedGroup === groupItem.group;
                return (
                  <div className="position-permission-group" key={`position-permission-group-modal-${groupItem.group}`}>
                    <button
                      type="button"
                      className={`position-permission-group-head ${isOpen ? "open" : ""}`}
                      onClick={() => togglePermissionGroup(target, groupItem.group)}
                    >
                      <span>{groupItem.group}</span>
                      <span>{isOpen ? "-" : "+"}</span>
                    </button>
                    {isOpen ? (
                      <div className="position-permission-selector position-permission-group-list">
                        {groupItem.items.map((permission) => {
                          return (
                            <label
                              key={`position-modal-permission-${permission.id}`}
                              className="position-permission-option position-permission-option-rich"
                              ref={(node) => {
                                if (node) {
                                  permissionItemRefs.current[permission.key] = node;
                                }
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={(positionPermissionSelection || []).includes(permission.key)}
                                onChange={(event) => togglePermissionSelection(permission.key, event.target.checked)}
                              />
                              <span className="position-permission-option-copy">
                                <span className="position-permission-option-name">{permission.name}</span>
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              {!groupedPermissionRows.length ? <span className="position-permission-empty">{texts.none}</span> : null}
            </div>

            <div className="row-actions">
              <button type="button" className="controlhub-button" onClick={applyPositionPermissionSelection}>
                {texts.save}
              </button>
              <button type="button" className="controlhub-button ghost" onClick={closePositionPermissionModal}>
                {texts.cancel}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showSettings ? (
        <div className="controlhub-settings-overlay">
          <div className="controlhub-settings-modal">
            <aside className="controlhub-settings-left">
              <div className="controlhub-settings-store">
                <strong>{texts.title}</strong>
                <p>8cmcnk-bf.mycontrolhub.com</p>
              </div>

              <input className="controlhub-settings-search" placeholder={texts.search} />

              <nav className="controlhub-settings-nav">
                <button
                  type="button"
                  className={settingsTab === "general" ? "active" : ""}
                  onClick={() => setSettingsTab("general")}
                >
                  {texts.general}
                </button>
                <button
                  type="button"
                  className={settingsTab === "billing" ? "active" : ""}
                  onClick={() => setSettingsTab("billing")}
                >
                  {texts.billing}
                </button>
                <button
                  type="button"
                  className={settingsTab === "users" ? "active" : ""}
                  onClick={() => setSettingsTab("users")}
                >
                  {texts.usersPermissions}
                </button>
                <button
                  type="button"
                  className={settingsTab === "policies" ? "active" : ""}
                  onClick={() => setSettingsTab("policies")}
                >
                  {texts.policies}
                </button>
              </nav>
            </aside>

            <div className="controlhub-settings-main">
              <div className="controlhub-settings-head">
                <h3>{texts.storeSettings}</h3>
                <button type="button" onClick={() => setShowSettings(false)}>
                  ✕
                </button>
              </div>

              <article className="controlhub-settings-card">
                <h4>{texts.businessInfo}</h4>
                <p>My Store - entity</p>
                <p>美国</p>
              </article>

              <article className="controlhub-settings-card">
                <h4>{texts.contactInfo}</h4>
                <p>yuxiping101@gmail.com</p>
                <p>无电话号码</p>
              </article>

              <article className="controlhub-settings-card">
                <h4>{texts.defaults}</h4>
                <div className="controlhub-settings-fields">
                  <label>
                    <span>商店货币</span>
                    <input value="美元 (USD $)" readOnly />
                  </label>
                  <label>
                    <span>备份区域</span>
                    <input value="美国" readOnly />
                  </label>
                  <label>
                    <span>单位制</span>
                    <input value="英制" readOnly />
                  </label>
                  <label>
                    <span>默认重量单位</span>
                    <input value="磅 (lb)" readOnly />
                  </label>
                </div>

                <div className="controlhub-mode-switch">
                  <span>{texts.businessMode}</span>
                  <div className="controlhub-mode-switch-buttons">
                    <button
                      type="button"
                      className={`controlhub-button ${storeMode === "b2b" ? "active" : "ghost"}`}
                      disabled={updatingStoreMode || adminRole !== "admin"}
                      onClick={() => updateStoreMode("b2b")}
                    >
                      {texts.b2b}
                    </button>
                    <button
                      type="button"
                      className={`controlhub-button ${storeMode === "b2c" ? "active" : "ghost"}`}
                      disabled={updatingStoreMode || adminRole !== "admin"}
                      onClick={() => updateStoreMode("b2c")}
                    >
                      {texts.b2c}
                    </button>
                  </div>
                  <small>{texts.modeSaved}: {storeMode.toUpperCase()}</small>
                </div>
              </article>

            </div>
          </div>
        </div>
      ) : null}

      {editingBlogArticleIndex >= 0 && blogArticleDrafts[editingBlogArticleIndex] ? (
        <div className="controlhub-user-password-overlay" role="dialog" aria-modal="true">
          <div className="controlhub-user-password-modal controlhub-article-edit-modal">
            <div className="controlhub-user-password-head">
              <div className="controlhub-article-edit-headline">
                <h3>{locale === "zh" ? "编辑文章" : "Edit Article"}</h3>
                <p>{locale === "zh" ? `正在编辑第 ${editingBlogArticleIndex + 1} 条` : `Editing item ${editingBlogArticleIndex + 1}`}</p>
              </div>
              {hasEditingBlogArticleUnsavedChanges() ? (
                <span className="controlhub-article-unsaved-badge">
                  {locale === "zh" ? "未保存修改" : "Unsaved changes"}
                </span>
              ) : null}
              <button type="button" className="controlhub-user-password-close" onClick={closeBlogArticleEditor}>
                ✕
              </button>
            </div>

            <div className="controlhub-article-edit-layout">
              <aside className="controlhub-article-preview-card" aria-label={locale === "zh" ? "文章预览" : "Article preview"}>
                <h4>{locale === "zh" ? "实时预览" : "Live Preview"}</h4>
                {blogArticleDrafts[editingBlogArticleIndex]?.cover ? (
                  !blogCoverPreviewFailed ? (
                    <img
                      src={blogArticleDrafts[editingBlogArticleIndex]?.cover}
                      alt={blogArticleDrafts[editingBlogArticleIndex]?.title || "cover-preview"}
                      className="controlhub-article-preview-cover"
                      onError={() => setBlogCoverPreviewFailed(true)}
                    />
                  ) : (
                    <p className="controlhub-article-preview-empty">{locale === "zh" ? "封面加载失败" : "Cover failed to load"}</p>
                  )
                ) : (
                  <p className="controlhub-article-preview-empty">{locale === "zh" ? "暂无封面" : "No cover image"}</p>
                )}
                <strong>{blogArticleDrafts[editingBlogArticleIndex]?.title || (locale === "zh" ? "未命名文章" : "Untitled article")}</strong>
                <div className="controlhub-article-preview-meta">
                  <span>{blogArticleDrafts[editingBlogArticleIndex]?.category || "-"}</span>
                  <span>{blogArticleDrafts[editingBlogArticleIndex]?.date || (locale === "zh" ? "未设置日期" : "Date not set")}</span>
                </div>
                <p>{blogArticleDrafts[editingBlogArticleIndex]?.summary || (locale === "zh" ? "暂无摘要" : "No summary")}</p>
              </aside>

              <div className="controlhub-article-editor-pane">
                <div className="row-actions controlhub-banner-edit-actions controlhub-article-edit-actions controlhub-article-edit-toolbar">
                  <span className="controlhub-article-toolbar-index">
                    {locale === "zh"
                      ? `第 ${editingBlogArticleIndex + 1} / ${blogArticleDrafts.length} 条`
                      : `Item ${editingBlogArticleIndex + 1} / ${blogArticleDrafts.length}`}
                  </span>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={openPreviousBlogArticleEditor}
                    disabled={editingBlogArticleIndex <= 0}
                  >
                    {locale === "zh" ? "上一条" : "Previous"}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={openNextBlogArticleEditor}
                    disabled={editingBlogArticleIndex >= blogArticleDrafts.length - 1}
                  >
                    {locale === "zh" ? "下一条" : "Next"}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button"
                    onClick={() => {
                      void saveArticleManagementSettings({ closeAfterSave: false });
                    }}
                    disabled={savingStoreSettings || !canManageTrustContent}
                  >
                    {savingStoreSettings ? (locale === "zh" ? "保存中..." : "Saving...") : (locale === "zh" ? "保存" : "Save")}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button"
                    onClick={() => {
                      closeBlogArticleEditor();
                    }}
                  >
                    {locale === "zh" ? "完成" : "Done"}
                  </button>
                  <button
                    type="button"
                    className="controlhub-button ghost"
                    onClick={closeBlogArticleEditor}
                  >
                    {locale === "zh" ? "关闭" : "Close"}
                  </button>
                </div>

                <div className="controlhub-settings-fields controlhub-article-edit-fields">
                <section className="controlhub-article-group controlhub-article-group-basic" aria-label={locale === "zh" ? "基础信息" : "Basic Information"}>
                  <div className="controlhub-article-group-head">
                    <h5>{locale === "zh" ? "基础信息" : "Basic Information"}</h5>
                    <p>{locale === "zh" ? "先设置标题、分类和发布时间。" : "Start with title, category, and publish date."}</p>
                  </div>
                  <div className="controlhub-article-group-grid">
                    <label className="controlhub-article-field controlhub-article-field-id">
                      <span>ID</span>
                      <input value={blogArticleDrafts[editingBlogArticleIndex]?.id || ""} readOnly />
                    </label>

                    <label className="controlhub-article-field controlhub-article-field-title">
                      <span>{locale === "zh" ? "标题" : "Title"}</span>
                      <input
                        value={blogArticleDrafts[editingBlogArticleIndex]?.title || ""}
                        onChange={(event) => updateBlogArticleDraft(editingBlogArticleIndex, "title", event.target.value)}
                        disabled={!canManageTrustContent}
                      />
                    </label>

                    <div className="controlhub-form-grid two-col controlhub-article-meta-grid">
                      <label>
                        <span>{locale === "zh" ? "分类" : "Category"}</span>
                        <select
                          value={blogArticleDrafts[editingBlogArticleIndex]?.category || "Sourcing"}
                          onChange={(event) => updateBlogArticleDraft(editingBlogArticleIndex, "category", event.target.value)}
                          disabled={!canManageTrustContent}
                        >
                          {BLOG_CATEGORY_OPTIONS.map((category) => (
                            <option key={`blog-edit-category-${category}`} value={category}>{category}</option>
                          ))}
                        </select>
                      </label>

                      <label>
                        <span>{locale === "zh" ? "发布日期" : "Publish Date"}</span>
                        <input
                          type="date"
                          value={blogArticleDrafts[editingBlogArticleIndex]?.date || ""}
                          onChange={(event) => updateBlogArticleDraft(editingBlogArticleIndex, "date", event.target.value)}
                          disabled={!canManageTrustContent}
                        />
                      </label>
                    </div>
                  </div>
                </section>

                <section className="controlhub-article-group controlhub-article-group-content" aria-label={locale === "zh" ? "内容编辑" : "Content Editor"}>
                  <div className="controlhub-article-group-head">
                    <h5>{locale === "zh" ? "内容编辑" : "Content Editor"}</h5>
                    <p>{locale === "zh" ? "摘要用于列表展示，正文用于详情阅读。" : "Summary appears in list pages; body is used on detail pages."}</p>
                  </div>
                  <div className="controlhub-article-group-grid">
                    <label className="controlhub-article-field controlhub-article-field-summary">
                      <span>{locale === "zh" ? "文章内容简要" : "Summary"}</span>
                      <textarea
                        rows={3}
                        value={blogArticleDrafts[editingBlogArticleIndex]?.summary || ""}
                        onChange={(event) => updateBlogArticleDraft(editingBlogArticleIndex, "summary", event.target.value)}
                        disabled={!canManageTrustContent}
                      />
                    </label>

                    <label className="controlhub-article-field controlhub-article-field-body">
                      <span>{locale === "zh" ? "正文" : "Body"}</span>
                      <textarea
                        rows={8}
                        value={blogArticleDrafts[editingBlogArticleIndex]?.body || ""}
                        onChange={(event) => updateBlogArticleDraft(editingBlogArticleIndex, "body", event.target.value)}
                        disabled={!canManageTrustContent}
                      />
                    </label>
                  </div>
                </section>

                <section className="controlhub-article-group controlhub-article-group-publish" aria-label={locale === "zh" ? "发布与链接" : "Publish & Links"}>
                  <div className="controlhub-article-group-head">
                    <h5>{locale === "zh" ? "发布与链接" : "Publish & Links"}</h5>
                    <p>{locale === "zh" ? "补充封面与跳转地址，增强内容转化。" : "Add cover and destination links to improve conversion."}</p>
                  </div>
                  <div className="controlhub-article-group-grid">
                    <label className="controlhub-article-field controlhub-article-field-cover">
                      <span>{locale === "zh" ? "封面图链接" : "Cover URL"}</span>
                      <input
                        value={blogArticleDrafts[editingBlogArticleIndex]?.cover || ""}
                        onChange={(event) => {
                          setBlogCoverPreviewFailed(false);
                          updateBlogArticleDraft(editingBlogArticleIndex, "cover", event.target.value);
                        }}
                        placeholder="https://..."
                        disabled={!canManageTrustContent}
                      />
                      {blogArticleDrafts[editingBlogArticleIndex]?.cover ? (
                        !blogCoverPreviewFailed ? (
                          <img
                            src={blogArticleDrafts[editingBlogArticleIndex]?.cover}
                            alt="cover-inline-preview"
                            className="controlhub-article-cover-inline-preview"
                            onError={() => setBlogCoverPreviewFailed(true)}
                          />
                        ) : (
                          <small className="controlhub-article-cover-error">{locale === "zh" ? "封面链接无法加载，请检查地址或图片权限。" : "Cover URL cannot be loaded. Please check the link or image access."}</small>
                        )
                      ) : null}
                    </label>

                    <label className="controlhub-article-field controlhub-article-field-readmore">
                      <span>Read More URL</span>
                      <input
                        value={blogArticleDrafts[editingBlogArticleIndex]?.readMoreUrl || ""}
                        onChange={(event) => updateBlogArticleDraft(editingBlogArticleIndex, "readMoreUrl", event.target.value)}
                        placeholder="https://..."
                        disabled={!canManageTrustContent}
                      />
                    </label>
                  </div>
                </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {editingBannerIndex !== null && getActiveBannerEditorDraft() ? (
        <div className="controlhub-user-password-overlay" role="dialog" aria-modal="true">
          <div className="controlhub-user-password-modal controlhub-banner-edit-modal">
            {savingBanners ? (
              <div className="controlhub-banner-saving-overlay is-modal" role="status" aria-live="polite">
                <div className="controlhub-banner-saving-card">
                  <span className="controlhub-banner-saving-spinner" aria-hidden="true" />
                  <span>{locale === "zh" ? "正在保存横幅配置..." : "Saving banner configuration..."}</span>
                </div>
              </div>
            ) : null}
            <div className="controlhub-user-password-head">
              <h3>{editingBannerIndex === -1 ? (locale === "zh" ? "新增横幅" : "Add Banner") : (locale === "zh" ? "编辑横幅" : "Edit Banner")}</h3>
              <button type="button" className="controlhub-user-password-close" onClick={cancelBannerEdit}>
                ✕
              </button>
            </div>

            <div className="controlhub-settings-fields controlhub-banner-edit-fields">
              <label className="controlhub-banner-image-field">
                <span>{texts.bannerImageUrl}</span>
                <input
                  value={getActiveBannerEditorDraft()?.imageUrl || ""}
                  onChange={(event) => updateBannerDraft(editingBannerIndex, "imageUrl", event.target.value)}
                  disabled={!canEditStorefront}
                  placeholder="https://..."
                />
                <label className="controlhub-button ghost controlhub-banner-upload-trigger">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => {
                      void uploadBannerImage(editingBannerIndex, event);
                    }}
                    disabled={!canEditStorefront || uploadingBannerIndex !== null}
                    hidden
                  />
                  {uploadingBannerIndex === editingBannerIndex
                    ? (locale === "zh" ? "上传中..." : "Uploading...")
                    : texts.uploadLocalImages}
                </label>
              </label>
              <label>
                <span>{texts.bannerTitle}</span>
                <input
                  value={getActiveBannerEditorDraft()?.title || ""}
                  onChange={(event) => updateBannerDraft(editingBannerIndex, "title", event.target.value)}
                  disabled={!canEditStorefront}
                />
              </label>
              <label>
                <span>{texts.bannerAnnotation}</span>
                <input
                  value={getActiveBannerEditorDraft()?.annotation || ""}
                  onChange={(event) => updateBannerDraft(editingBannerIndex, "annotation", event.target.value)}
                  disabled={!canEditStorefront}
                />
              </label>
              <label>
                <span>{texts.bannerButtonText}</span>
                <input
                  value={getActiveBannerEditorDraft()?.buttonText || ""}
                  onChange={(event) => updateBannerDraft(editingBannerIndex, "buttonText", event.target.value)}
                  disabled={!canEditStorefront}
                />
              </label>
              <label>
                <span>{texts.bannerButtonLink}</span>
                <input
                  value={getActiveBannerEditorDraft()?.buttonLink || ""}
                  onChange={(event) => updateBannerDraft(editingBannerIndex, "buttonLink", event.target.value)}
                  disabled={!canEditStorefront}
                />
              </label>
              <label>
                <span>{locale === "zh" ? "文字/按钮位置（拖动预览框）" : "Text/Button Position (drag on preview)"}</span>
                <div
                  className="controlhub-banner-position-preview"
                  onPointerDown={(event) => {
                    if (!canEditStorefront) {
                      return;
                    }
                    event.preventDefault();
                    event.currentTarget.setPointerCapture?.(event.pointerId);
                    setBannerPositionDragging(true);
                    handleBannerPositionPointer(editingBannerIndex, event);
                  }}
                  onPointerMove={(event) => {
                    if (!canEditStorefront || !bannerPositionDragging) {
                      return;
                    }
                    handleBannerPositionPointer(editingBannerIndex, event);
                  }}
                  onPointerUp={(event) => {
                    event.currentTarget.releasePointerCapture?.(event.pointerId);
                    setBannerPositionDragging(false);
                  }}
                  onPointerCancel={(event) => {
                    event.currentTarget.releasePointerCapture?.(event.pointerId);
                    setBannerPositionDragging(false);
                  }}
                >
                  {getActiveBannerEditorDraft()?.imageUrl ? (
                    <img
                      src={getActiveBannerEditorDraft()?.imageUrl}
                      alt={getActiveBannerEditorDraft()?.title || "banner-preview"}
                      className="controlhub-banner-position-preview-image"
                    />
                  ) : null}
                  <div
                    className="controlhub-banner-position-marker"
                    style={{
                      left: `${Math.max(4, Math.min(78, Math.round(Number(getActiveBannerEditorDraft()?.contentX) || 12)))}%`,
                      top: `${Math.max(4, Math.min(78, Math.round(Number(getActiveBannerEditorDraft()?.contentY) || 52)))}%`
                    }}
                  >
                    <span>{locale === "zh" ? "文字+按钮" : "Text + CTA"}</span>
                  </div>
                </div>
                <small className="controlhub-banner-position-value">
                  X: {Math.max(4, Math.min(78, Math.round(Number(getActiveBannerEditorDraft()?.contentX) || 12)))}% ·
                  Y: {Math.max(4, Math.min(78, Math.round(Number(getActiveBannerEditorDraft()?.contentY) || 52)))}%
                </small>
              </label>
            </div>

            <div className="row-actions controlhub-banner-edit-actions">
              <button
                type="button"
                className="controlhub-button"
                onClick={() => {
                  void saveBannerEdit();
                }}
                disabled={!canEditStorefront || savingBanners}
              >
                {texts.save}
              </button>
              <button
                type="button"
                className="controlhub-button ghost"
                onClick={cancelBannerEdit}
                disabled={!canEditStorefront}
              >
                {locale === "zh" ? "取消" : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {companyImagePreviewModal?.src ? (
        <div
          className="controlhub-company-image-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={locale === "zh" ? "公司图片预览" : "Company image preview"}
          onClick={closeCompanyImagePreview}
        >
          <div
            className="controlhub-company-image-lightbox-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="controlhub-company-image-lightbox-close"
              onClick={closeCompanyImagePreview}
              aria-label={locale === "zh" ? "关闭预览" : "Close preview"}
            >
              ×
            </button>
            <img
              src={companyImagePreviewModal.src}
              alt={companyImagePreviewModal.alt}
              className="controlhub-company-image-lightbox-image"
            />
          </div>
        </div>
      ) : null}
    </>
  );
}
